<template>
    <div class="oio-app">

        <NavBar v-if="!task" :wurflData="myWURFLData"/>

        <!-- In front of everything, because rasterising is the one thing here
          -- that cannot be worked around and the card version sat below the
          -- fold on a laptop. -->
        <PdfProgress @cancel="cancelPdf" />
        <VideoProgress :saving="offersVideo && workInFlight" />

        <!-- Cropping a region needs the image big and the pointer unobstructed,
          -- which the option column cannot give it on a phone. -->
        <CropModal :open="cropperOpen"
                   :file="cropSource"
                   :crop="uiStore.crop"
                   :flip-h="uiStore.flipH"
                   :flip-v="uiStore.flipV"
                   @close="cropperOpen = false"
                   @apply="applyCrop"
                   @flip-h="toggleFlip('flipH')"
                   @flip-v="toggleFlip('flipV')"
                   @reset="resetTransforms" />

        <!-- Modal @close="toggleModal" :modalActive="modalActive" -->
        <ModalRetrieveImageFromUrl />

        <!-- Top leaderboard, every form factor.
          -- A responsive horizontal unit lets Google pick from the leaderboard
          -- family (728x90, 970x90, 1264x100) by available width, so there is
          -- no need to declare each size. Reserved at the tallest of them,
          -- 100px, so nothing shifts when the unit loads. -->
        <div v-if="adSlotIdTop || showAdPlaceholders" class="ad-block mx-auto flex max-w-[1264px] flex-col items-center gap-1.5 px-4 md:px-8 pt-4">
            <div class="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-faint">{{ t('ads.label') }}</div>
            <!-- David's shape, and it is the right one: a fluid width with a
              -- PINNED height. The height is the whole problem solved --
              -- data-full-width-responsive let AdSense choose it, and on a
              -- 412px phone it chose ~420px and buried the drop zone. Ninety
              -- pixels on every screen means the reserved space is always
              -- exactly right, so there is no layout shift anywhere, and it
              -- needs no JavaScript deciding anything.
              --
              -- No data-ad-format on purpose: with a measurable box AdSense
              -- fits a banner to it (970x90 or 728x90 on a desktop, 320x50 on
              -- a phone) instead of picking a shape of its own.
              --
              -- full-width-responsive is stated rather than left to its
              -- default, because its default is exactly what went wrong. -->
            <ins v-if="adSlotIdTop" class="adsbygoogle mx-auto"
                 style="display:block;min-width:320px;max-width:1260px;width:100%;height:90px"
                 data-ad-client="ca-pub-5261099672579630"
                 :data-ad-slot="adSlotIdTop"
                 data-full-width-responsive="false"></ins>
            <div v-else-if="showAdPlaceholders" class="flex h-[60px] md:h-[100px] w-full items-center justify-center rounded-lg border border-dashed border-rule-strong bg-sunk text-[13px] text-ink-faint">
                Leaderboard &mdash; 728&times;90 / 970&times;90 / 1264&times;100 &mdash; unit id not set yet
            </div>
        </div>

        <!-- Content plus optional side rails.
          --
          -- A 300px rail needs a 1652px viewport to sit beside the 1280px
          -- content without squeezing it, and two rails need 1976px. So they
          -- appear only on screens wide enough to spare the room -- at 1366,
          -- 1440 or 1512 the content keeps the full width and no rail shows.
          -- Narrowing the columns to make rails fit sooner would trade the
          -- primary experience for inventory, which is the wrong way round. -->
        <div class="mx-auto flex w-full max-w-[1976px] justify-center gap-6 px-0 min-[1652px]:px-6">

            <!-- LEFT RAIL. Off by default: on a left-to-right page this sits
              -- between the reader and the drop zone, which is the one thing
              -- the page exists for. Flip adRailLeft to enable. -->
            <aside v-if="adRailLeft && (adSlotIdRailLeft || showAdPlaceholders)"
                   class="hidden min-[1976px]:block w-[300px] shrink-0 pt-8">
                <div class="ad-block sticky top-6 flex flex-col items-center gap-1.5">
                    <div class="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-faint">{{ t('ads.label') }}</div>
                    <ins v-if="adSlotIdRailLeft && railFits" class="adsbygoogle block w-[300px]"
                         style="display:block;width:300px;height:600px"
                         data-ad-client="ca-pub-5261099672579630"
                         :data-ad-slot="adSlotIdRailLeft"
                         data-ad-format="vertical"
                         data-full-width-responsive="false"></ins>
                    <div v-else-if="showAdPlaceholders" class="flex h-[600px] w-[300px] items-center justify-center rounded-lg border border-dashed border-rule-strong bg-sunk text-center text-[13px] text-ink-faint">
                        300 &times; 600<br>{{ t('ads.leftRail') }}
                    </div>
                </div>
            </aside>

            <div class="min-w-0 flex-1">

        <!-- from:  smashingmagazine.com/2022/03/drag-drop-file-uploader-vuejs-3/ -->
        <!-- FLEX WHEN EMPTY, GRID ONCE A PICTURE IS STAGED.
          --
          -- The grid is not decoration: it is the only way to get the settings
          -- beside the picture on a desktop while keeping them BELOW it on a
          -- phone. Stacked columns take their order from the DOM, so simply
          -- moving the panel into the left column would have put the settings
          -- in front of the picture on every phone -- the settings for
          -- something you cannot see yet.
          --
          -- So the DOM order stays drop zone, stage, settings, which is right
          -- when the columns stack, and on md+ the grid puts the settings in
          -- column one under the drop zone while the stage spans both rows. -->
        <div class="mx-auto max-w-screen-xl flex flex-wrap gap-y-8 px-4 md:px-8 py-8 md:py-10"
             :class="stageWide
                 ? 'md:grid md:grid-cols-3 md:gap-x-10 md:items-start md:grid-rows-[auto_1fr]'
                 : ''">

<!-- FIRST COLUMN  -->

            <!-- THE COLUMNS ARE NOT ALWAYS HALF AND HALF.
              -- Empty, this is the landing state and the drop zone deserves the
              -- room: it is the only thing anybody is meant to do.
              --
              -- Once a picture is staged the balance is wrong, and measurably
              -- so. At 1280 the drop zone was 460x326 to hold a 176px thumbnail
              -- of the picture the stage was already showing at 460 -- with
              -- about 400px of empty column under it, because a task page's
              -- steps are in its own prose rather than in the application.
              -- Meanwhile the one thing being worked on had half a screen.
              --
              -- So the estate goes to the picture. See stageWide(). -->
            <div class="min-w-0 w-full flex flex-col gap-5"
                 :class="stageWide
                     ? 'md:col-start-1 md:row-start-1'
                     : 'md:w-1/2 md:pr-5'">

                <!-- The primary action carries the brand gradient. The dashed
                  -- border belongs to the inner wrapper, not to the label, so
                  -- that the selected-file preview sits INSIDE the drop target
                  -- rather than below it in the gradient border. The preview
                  -- stays outside the <label> though -- its remove button would
                  -- otherwise re-open the file picker on every click. -->
                <!-- HIDDEN WHILE A CLIP IS IN THE TRIMMER.
                  --
                  -- Everything inside is gated on uiStore.files being empty,
                  -- and it IS empty then -- a clip is not intake, only the GIF
                  -- made from it is. So the whole "Tap to choose images" panel
                  -- sat above the filmstrip, inviting the visitor to choose the
                  -- file they had just chosen, in the wrong noun.
                  --
                  -- v-show on the component rather than five more conditions
                  -- inside it: the question is about the whole panel. -->
                <!-- THE COLOUR NAMES THE ERRAND.
                  -- Converting a file, taking a document apart, stripping
                  -- location data and making a picture smaller are four
                  -- different jobs, and the drop zone is the biggest thing on
                  -- the page. data-family comes from the same list the "All
                  -- tools" menu groups by (lib/task.js, taskFamily()); the
                  -- gradient itself is in palette.css, keyed off it, because a
                  -- colour written into a component is a colour that never
                  -- went through the palette. No family -- the home page --
                  -- keeps the brand violet, which is every errand at once. -->
                <!-- THE SHARE THAT NEVER ARRIVED.
                  -- server.py answers /_share-target only when the service
                  -- worker is not in control -- first launch after installing,
                  -- or a worker still activating -- and only the worker can
                  -- read a multipart body before the navigation completes. So
                  -- the file is already gone by the time this page loads. It
                  -- used to open an empty drop zone and say nothing, which is
                  -- indistinguishable from a broken app.
                  --
                  -- ABOVE the drop zone, not below it, and that is measured.
                  -- Below, on a 390x740 phone, it rendered at y=669..748 --
                  -- starting 669px down a 740px screen, running off the
                  -- bottom, and in the reported case sitting exactly where
                  -- the sharing app's own toast was drawn. A report about
                  -- the thing the visitor just did belongs before the
                  -- fallback action, not after it.
                  -- Still outside #single-stage, which is v-show'n on
                  -- something being staged and would hide a message about
                  -- nothing being staged -- the trap #video-error records. -->
                <div v-if="shareMissed" id="share-missed" role="alert"
                     class="flex items-start gap-3 rounded-2xl border border-error bg-raised p-4 text-[15px] text-error">
                    <svg class="w-6 h-6 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
                    </svg>
                    <span>{{ t('errors.shareMissed') }}<template v-if="shareMissedDetail"><br><span class="text-ink-mute text-[13px] break-all">{{ shareMissedDetail }}</span></template></span>
                </div>

                <DropZone v-show="!uiStore.videoTrimming" id="drop-zone" :data-family="taskFamily()" class="drop-area rounded-[20px] p-[3px] cursor-pointer transition duration-300 hover:shadow-lg"
                    @files-dropped="onFilesDropped" #default="{ dropZoneActive }">
                    <!-- The tall box is for the EMPTY state, where it is a
                      -- target you are meant to hit with a file and the words
                      -- inside it are the page's invitation. Once a picture is
                      -- staged and its thumbnail has stood down, all that is
                      -- left in here is one line offering to swap the file --
                      -- and 320px of gradient around one line is the estate
                      -- this change exists to give back. -->
                    <div class="flex flex-col items-center justify-center gap-2 rounded-[17px] border-2 border-dashed border-white/60 bg-white/10 px-6"
                         :class="previewIsRedundant
                             ? 'min-h-0 py-5'
                             : 'min-h-[260px] md:min-h-[320px] py-8'">

                        <!-- A handful wraps, as it always did. More than that
                          -- stacks: twenty previews in a wrapping row fill the
                          -- drop zone and push the button that starts the work
                          -- off the bottom of the screen, which is the one
                          -- thing that must stay visible.
                          --
                          -- The stack drops the per-file remove button along
                          -- with the rest, because a card that is nine tenths
                          -- covered has nowhere to put one. "Choose different
                          -- images" below is the way out, and it was always the
                          -- likelier one for a set this size. -->
                        <!-- ...or a clip waiting in the trimmer, which stands where the stage
                  -- would. Nothing is in uiStore.files then: the clip is not
                  -- intake, only the GIF made from it is. -->
                <div v-show="uiStore.files.length || uiStore.videoTrimming" class="w-full">
                            <div v-if="uiStore.files.length <= STACK_AFTER"
                                 class="flex w-full flex-wrap justify-center gap-2">
                                <!-- Not while the stage is showing the same one file:
                                  -- see previewIsRedundant(). The remove button
                                  -- goes with it, which is why the drop zone's own
                                  -- "Choose a different image" stays below. -->
                                <FilePreview v-show="!previewIsRedundant"
                                             v-for="file in uiStore.files" :key="file.id" :file="file" tag="div"
                                             :compact="uiStore.files.length > 1" :large="editsInBrowser"
                                             @remove="removeFile" />
                            </div>
                            <!-- A SET IS SHOWN AS A SET, not as a stack.
                              -- 
                              -- This was five 64px thumbnails overlapping each
                              -- other by half, with "+15" on the end -- which
                              -- shows a quarter of four pictures and none of
                              -- the rest, in a drop zone that is 400px wide
                              -- and mostly empty. Reported as exactly that:
                              -- "the way images are previewed in the drop zone
                              -- can be improved".
                              --
                              -- A wrapping grid instead. Every picture gets
                              -- its own square and the count only appears when
                              -- there genuinely are more than fit -- and the
                              -- ceiling is on ROWS rather than on files, so a
                              -- wide column shows more of them than a narrow
                              -- one instead of both showing five.
                              --
                              -- auto-fill rather than a column count: the
                              -- drop zone is in a column whose width depends
                              -- on whether anything is staged, so a fixed
                              -- number of columns is wrong at one of the two. -->
                            <div v-else class="flex w-full flex-col items-center gap-2.5">
                                <div class="grid w-full gap-1.5 px-1"
                                     style="grid-template-columns: repeat(auto-fill, minmax(58px, 1fr))">
                                    <div v-for="file in stackedPreviews" :key="file.id"
                                         class="relative aspect-square overflow-hidden rounded-lg border border-white/50 bg-black/20 shadow-sm">
                                        <img :src="file.url" alt="" loading="lazy"
                                             class="h-full w-full object-cover" />
                                    </div>
                                    <div v-if="uiStore.files.length > stackedPreviews.length"
                                         class="flex aspect-square items-center justify-center rounded-lg border border-white/50 bg-black/50 text-[15px] font-black text-white shadow-sm">
                                        +{{ uiStore.files.length - stackedPreviews.length }}
                                    </div>
                                </div>
                                <div class="flex flex-col items-center gap-0.5 text-center">
                                    <div class="text-[14px] font-bold text-white/90">
                                        {{ t('dropzone.selectedCount', {n: uiStore.files.length}) }}
                                    </div>
                                    <!-- Only shown here, which is exactly when it
                                      -- matters: this many files is somebody about
                                      -- to assemble a document, and page order is
                                      -- the thing they will check. -->
                                    <div class="text-[12px] text-white/70">{{ t('dropzone.orderNote') }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- CLICKING THIS WHEN SOMETHING IS STAGED CLEARS IT
                          -- rather than opening the file picker.
                          --
                          -- The whole drop zone is a <label for="file-input">,
                          -- which is right while it is empty: clicking it
                          -- anywhere is "choose a file". Once a picture is
                          -- staged the same label said "Choose a different
                          -- image" and still went straight to the OS picker --
                          -- so a picture that arrived by PASTE, by URL or from
                          -- the sample strip could not be replaced the way it
                          -- arrived. Reported exactly that way: pasted an
                          -- image, pressed it, and was offered a file browser
                          -- with no route back.
                          --
                          -- The drop zone has five ways in -- drop, paste,
                          -- browse, a URL and the samples -- and the picker is
                          -- one of them. Going back to the EMPTY zone offers
                          -- all five again, which is what "a different image"
                          -- should mean. -->
                        <label for="file-input" class="dropzonelabel block w-full cursor-pointer text-center"
                               @click="onDropzoneClick">
                            <svg v-show="!uiStore.files.length" class="mx-auto mb-4 h-12 w-12 md:h-14 md:w-14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 16V4"></path><path d="m6 10 6-6 6 6"></path><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"></path>
                            </svg>
                            <!-- The copy says "images", plural, throughout: the
                              -- dropzone accepts several and nothing else on the
                              -- page tells you so until you have already tried. -->
                            <template v-if="dropZoneActive">
                                <span class="block text-2xl md:text-3xl font-black text-white">{{ t('dropzone.dropHere') }}</span>
                                <span class="block text-[17px] text-white/90 mt-1">{{ t('dropzone.dropHint') }}</span>
                            </template>
                            <template v-else>
                                <span v-show="!uiStore.files.length && !myWURFLData.isMobile" class="block text-2xl md:text-3xl font-black text-white leading-tight">{{words.dropzone}}</span>
                                <span v-show="!uiStore.files.length && !myWURFLData.isMobile" class="block text-[17px] text-white/90 mt-2 leading-relaxed">
                                    {{ says('dropzone.browse', { max: maxBatchFiles }) }}<template v-if="myWURFLData.isDesktop && accepted.images">{{ t('dropzone.paste') }} <b>{{ t('dropzone.pasteKeys') }}</b></template>
                                </span>
                                <span v-show="!uiStore.files.length && myWURFLData.isMobile" class="block text-2xl font-black text-white leading-tight">
                                    {{ says('dropzone.mobileHeadline') }}
                                    <span class="block text-[15px] font-normal text-white/90 mt-2 leading-relaxed">{{ says('dropzone.mobileBody', { max: maxBatchFiles }) }}</span>
                                </span>
                                <!-- keeps the drop target clickable once a preview is showing -->
                                <span v-show="uiStore.files.length" class="block text-[15px] text-white/90 underline underline-offset-2">{{ stagedIsPdf ? t('dropzone.chooseDifferentPdf')
                                                                                                                    : uiStore.batchMode ? t('dropzone.chooseDifferent')
                                                                                                                    : t('dropzone.chooseDifferentOne') }}</span>
                            </template>

                            <input type="file" id="file-input" @change="onInputChange" :accept="accepted.attr" :multiple="maxBatchFiles > 1"/>
                        </label>

                        <!-- A camera of its own, on phones and tablets.
                          -- accept="image/*" alone used to be enough: Chrome on
                          -- Android answered it with its own chooser, and Camera
                          -- was one of the entries. Android 13 and up hands
                          -- image-only inputs to the system photo picker
                          -- instead, and that picker has no camera in it, so
                          -- the shortcut quietly disappeared without this app
                          -- changing a line. capture= asks for the camera
                          -- directly and skips the picker altogether.
                          -- Sibling of the label above, not inside it, or the
                          -- tap would open the picker rather than the camera. -->
                        <!-- Not on a page that wants a document: a phone camera
                          -- cannot produce a PDF, so the button was an offer
                          -- that ended in a refusal. -->
                        <!-- ...and gone again on a phone once there is a picture.
                          -- It is the widest thing between the preview and the
                          -- controls, and on a 412px screen it pushed the text,
                          -- the placement, the face, the colours, both sliders
                          -- and the button below the fold: placing a watermark
                          -- meant scrolling up to look and down to change, over
                          -- and over. Removing the picture with the x brings it
                          -- back. Tablets keep it -- they have the room. -->
                        <label v-show="cameraOffered" for="camera-input"
                               class="dropzonelabel mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/60 bg-white/20 px-5 py-2.5 text-[15px] font-bold text-white">
                            <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M4 8h2.5l1.4-2h8.2l1.4 2H20a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/>
                                <circle cx="12" cy="13.5" r="3.4"/>
                            </svg>
                            {{ uiStore.files.length ? t('dropzone.cameraAgain') : t('dropzone.camera') }}
                            <input type="file" id="camera-input" @change="onInputChange" accept="image/*" capture="environment"/>
                        </label>
                    </div>

                </DropZone>

                <!-- An address is the other way in, and it looks like one.
                  --
                  -- It was a text link buried mid-sentence in the fine print --
                  -- between the list of accepted formats and the retention
                  -- line -- which opened a modal you then had to type into.
                  -- Prose describing an affordance is not the same as showing
                  -- one, and it read as a footnote rather than a choice.
                  --
                  -- "or" is what makes it a peer of the drop zone rather than
                  -- an afterthought: the rule either side says these are two
                  -- ways to do the same thing.
                  --
                  -- Only where an image is what the page takes. The fetcher
                  -- returns an image, so on /pdf-to-jpg the offer would end in
                  -- the refusal the drop zone has just described -- which is
                  -- why the old link carried the same condition.
                  --
                  -- Gone once something is staged, like everything else in this
                  -- block: "choose a different image" is what changing your
                  -- mind is for, and on a phone these rows sit between the
                  -- preview and the button that starts the work.
                  --
                  -- It still opens the modal, and that is deliberate:
                  -- prepareFetchImageFromWeb() sets imageFromInternetModal, so
                  -- the panel that used to ask for the address now only reports
                  -- progress and failures. One place to type, one place to
                  -- watch. -->
                <div v-show="!uiStore.files.length && !uiStore.videoTrimming && fetchOffered"
                     class="flex flex-col gap-3">
                    <div class="flex items-center gap-3" aria-hidden="true">
                        <span class="h-px flex-1 bg-rule"></span>
                        <span class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('dropzone.or') }}</span>
                        <span class="h-px flex-1 bg-rule"></span>
                    </div>
                    <form class="flex flex-wrap gap-2" @submit.prevent="fetchFromField">
                        <label class="sr-only" for="url-field">{{ offersVideo ? t('url.titleVideo') : t('url.title') }}</label>
                        <input id="url-field" type="url" inputmode="url" autocomplete="off"
                               v-model="uiStore.fetchedImageUrl"
                               :placeholder="offersVideo ? t('url.placeholderVideo') : t('url.placeholder')"
                               :aria-label="offersVideo ? t('url.titleVideo') : t('url.title')"
                               class="input input-sm min-w-0 grow rounded-lg" />
                        <button type="submit" data-fetch-url
                                :disabled="!(uiStore.fetchedImageUrl || '').trim()"
                                class="btn btn-primary btn-sm rounded-lg shrink-0">{{ t('url.fetch') }}</button>
                    </form>
                </div>

                <!-- One quiet line in place of the four scattered "Tip:" blocks.
                  --
                  -- Gone the moment a file is chosen. It answers "what may I
                  -- put in here?", which is a question nobody has any more once
                  -- they have put something in -- and on a phone those four
                  -- lines sit between the preview and the button that starts
                  -- the work, which is enough to push it under the fold. The
                  -- URL link inside it goes the same way: choosing a different
                  -- file is what "Choose a different image" is for. -->
                <div v-show="!uiStore.files.length && !uiStore.videoTrimming" class="flex items-start gap-2.5 text-sm text-ink-label">
                    <svg class="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="9"></circle><path d="M12 8h.01"></path><path d="M11 12h1v4h1"></path>
                    </svg>
                    <span>
                        <!-- The URL fetcher retrieves IMAGES. On a page that takes a
                          -- document the offer ends in the refusal we just wrote, so the
                          -- clause goes rather than being translated into a dead end.
                          --
                          -- All on ONE line, with a literal space, and no comment in the
                          -- middle of it. Vue's whitespace: 'condense' drops any
                          -- whitespace-only text node that contains a newline, so a
                          -- space before <template> on its own line vanishes and the
                          -- sentence renders as "You can alsofetch an image from a URL".
                          -- An &#32; entity does not survive it either. -->
                        {{ says('dropzone.accepts', { max: maxBatchFiles }) }}{{ says('dropzone.retention') }}
                    </span>
                </div>

                <!-- A CLIP THAT COULD NOT BE READ, OUTSIDE THE STAGE.
                  --
                  -- It used to live inside #single-stage, which is v-show'd on
                  -- something being staged -- and a conversion that FAILS
                  -- stages nothing and has already dismissed the trimmer. So
                  -- the one message that mattered was rendered inside a hidden
                  -- container: "I clicked Make the GIF, the UI disappeared and
                  -- nothing happened" is precisely that, and it was right.
                  --
                  -- Here it sits beside the drop zone, which is both visible
                  -- at that moment and the thing you would use to try again.
                  --
                  -- THE COMMONEST CAUSE IS HEVC. A phone records it by
                  -- default and plays it perfectly; a browser without the
                  -- codec reports 0x0 dimensions and a real duration, because
                  -- the audio track decoded. So the sentence blames the
                  -- browser, not the file. $reset() clears it, so the next
                  -- attempt silences it with no code to do so. -->
                <!-- Beside the drop zone and NOT inside #single-stage, for
                  -- the reason #video-error records: a conversion that fails
                  -- stages nothing, so a message rendered inside the stage is
                  -- hidden exactly when it is the only thing worth seeing. -->
                <!-- DECODING A HEIC IS SECONDS, NOT MILLISECONDS.
                  -- Unlike the SVG path, which is one drawImage, this is
                  -- WebAssembly working through a 12-megapixel photograph --
                  -- and twenty of them is a minute. A page that sits there
                  -- saying nothing for a minute has failed, whatever it is
                  -- doing underneath. -->
                <div v-if="uiStore.heicBusy" id="heic-progress" role="status"
                     class="flex items-center gap-3 text-[15px] mt-4">
                    <span class="loading loading-spinner loading-sm text-brand"></span>
                    <span>
                        {{ t('heic.working') }}<template v-if="uiStore.heicTotal > 1">
                        &nbsp;&middot;&nbsp;{{ t('heic.counted', {done: uiStore.heicDone + 1, total: uiStore.heicTotal}) }}</template>
                    </span>
                </div>

                <!-- OPENING AN ARCHIVE IS A WAIT WITH NOTHING TO SHOW.
                  -- The whole cost is loadAsync() reading the zip before a
                  -- single entry is reachable, so there is no counter to put
                  -- here and a spinner is all there honestly is -- the same
                  -- verdict VideoProgress reaches about the upload. -->
                <div v-if="uiStore.zipBusy" id="zip-progress" role="status"
                     class="flex items-center gap-3 text-[15px] mt-4">
                    <span class="loading loading-spinner loading-sm text-brand"></span>
                    <span>{{ t('zip.working') }}</span>
                </div>

                <div v-if="uiStore.zipError" id="zip-error" role="alert"
                     class="flex items-start gap-2 text-[15px] text-error mt-4">
                    <svg class="w-5 h-5 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v5M12 16h.01" stroke-linecap="round" />
                    </svg>
                    <span>{{ uiStore.zipError }}</span>
                </div>

                <div v-if="uiStore.heicError" id="heic-error" role="alert"
                     class="flex items-start gap-2 text-[15px] text-error mt-4">
                    <svg class="w-5 h-5 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v5M12 16h.01" stroke-linecap="round" />
                    </svg>
                    <span>{{ uiStore.heicError }}</span>
                </div>

                <div v-if="uiStore.svgError" id="svg-error" role="alert"
                     class="flex items-start gap-2 text-[15px] text-error mt-4">
                    <svg class="w-5 h-5 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v5M12 16h.01" stroke-linecap="round" />
                    </svg>
                    <span>{{ uiStore.svgError }}</span>
                </div>

                <div v-if="uiStore.videoError" id="video-error" role="alert"
                     class="flex items-start gap-3 rounded-2xl border border-error bg-raised p-4 text-[15px] text-error">
                    <svg class="w-6 h-6 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
                    </svg>
                    <span>{{ uiStore.videoError }}</span>
                </div>

                <!-- The ready-made pictures and the blank cards, beside the
                  -- choice they are an alternative to.
                  --
                  -- IT USED TO DISAPPEAR THE MOMENT ONE WAS CHOSEN, on the
                  -- reasoning that "offering to replace it belongs to Choose a
                  -- different image rather than to a row of swatches". That
                  -- reasoning was the bug: "Choose a different image" is a
                  -- label for the file input, so it offers the visitor's own
                  -- disk and nothing else. Somebody who picked a ready-made
                  -- picture and wanted a different ready-made picture was
                  -- handed a file browser and had to reload the page.
                  -- Reported exactly that way.
                  --
                  -- So it stands down but does not leave: once something is
                  -- staged it folds to one line, which is what keeps the room
                  -- the folding was for. -->
                <div v-show="offersMeme" class="mt-3">
                    <button v-if="uiStore.files.length && !libraryOpen"
                            type="button" data-library-reopen
                            @click="libraryOpen = true"
                            class="text-[15px] text-brand underline underline-offset-2">
                        {{ t('meme.libraryAgain') }}
                    </button>
                    <LibraryStrip v-show="!uiStore.files.length || libraryOpen"
                                  @picture="useLibraryPicture" />
                </div>

                <!-- One button, and what it says changes with what there is to do.
                  -- Before a result it starts the work; once a single image has come
                  -- back it hands the versions over, because pressing "Optimize your
                  -- image" on an image already optimised did nothing a visitor could
                  -- see. Tasks that edit in the browser keep their own wording and
                  -- their own one-press produce-and-deliver. -->
                <button v-show="!offersInspect && !uiStore.isEmptyWorkableImage && !workInFlight && !stageShown && !documentDone"
                        data-primary @click.prevent="primaryAction()" :title="primaryTitle" :disabled="delivering"
                        class="btn btn-lg btn-primary rounded-lg self-start">{{ primaryLabel }}</button>
                    <WorkingNote v-show="!uiStore.isEmptyWorkableImage && saving && !stageShown && !documentDone"
                                 class="self-start" />

                <!-- The tall unit, in the column that now has room for it.
                  --
                  -- Everything that acts on the picture has gone to the right,
                  -- next to the picture, and this column is the drop zone and
                  -- then nothing. A 300x600 is the shape that fits a column.
                  --
                  -- Still not on a phone. There the columns stack, so this
                  -- would land between the drop zone and the stage -- directly
                  -- in front of the thing the visitor came to look at, and the
                  -- one place a full-height advertisement must never be. -->
                <div v-if="adSlotIdColumn && columnAdFits"
                     class="ad-block hidden md:flex mt-3 flex-col items-center gap-1.5">
                    <div class="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-faint">{{ t('ads.label') }}</div>
                    <ins class="adsbygoogle block w-[300px]"
                         style="display:block;width:300px;height:600px"
                         data-ad-client="ca-pub-5261099672579630"
                         :data-ad-slot="adSlotIdColumn"
                         data-ad-format="vertical"
                         data-full-width-responsive="false"></ins>
                </div>

            </div>
<!-- END OF FIRST COLUMN -->
<!-- SECOND COLUMN -->

            <div class="min-w-0 w-full"
                 :class="stageWide
                     ? 'md:col-start-2 md:col-span-2 md:row-start-1 md:row-span-2'
                     : 'md:w-1/2 md:pl-5'">
                <!-- THE SHEET, IN THE ROOM THE STAGE IS NOT USING.
                  --
                  -- On a batch page there is no single picture, so the second
                  -- column -- two thirds of the grid, and the widest thing on
                  -- the page -- sat empty while the whole tool ran in a narrow
                  -- strip on the left. Reported with a screenshot of exactly
                  -- that: "on the desktop there's a lot of real estate".
                  --
                  -- Which is the right home for it on its own merits, not only
                  -- because the space was free: a collage justifies to the
                  -- width it is given, so the preview shows the sheet at the
                  -- proportions the column has, and the wider the column the
                  -- more the preview is worth looking at.
                  --
                  -- The controls go UNDER it rather than beside it: they are
                  -- read once and the picture is looked at repeatedly, and a
                  -- side-by-side split would take width off the only thing
                  -- here that benefits from it. -->
                <!-- THE CONTROLS SIT BESIDE THE PICTURE, NOT UNDER IT.
                  --
                  -- They were under it, on the reasoning that the settings are
                  -- read once and the picture is looked at repeatedly, so a
                  -- side-by-side split would take width off the only thing
                  -- here that benefits from it.
                  --
                  -- Wrong in use, and reported: "so the user won't have to
                  -- scroll to see the effect". A sheet of eight photographs is
                  -- most of a laptop screen tall, so reaching the controls
                  -- scrolls the preview off the top -- and every one of these
                  -- settings exists ONLY to change what the preview shows.
                  -- A control you operate blind is a control you operate by
                  -- trial and error, which is the download-look-adjust loop
                  -- the preview was built to end.
                  --
                  -- flex-row-reverse rather than ordering utilities: the DOM
                  -- keeps preview-then-controls, which is the right stacking
                  -- on a phone (see the picture, then the settings), and the
                  -- row simply reverses it so the controls land on the left of
                  -- a wide screen. One rule, no order classes to disagree.
                  --
                  -- The controls get a FIXED width so the preview does not
                  -- change size when a button row wraps -- a collage
                  -- justifies to the width it is given, so a reflowing panel
                  -- would re-lay the whole sheet. -->
                <div v-if="offersSheet && sheetReady" id="sheet-panel"
                     class="mb-8 flex flex-col gap-6 lg:flex-row-reverse lg:items-start lg:gap-8">
                    <SheetPreview ref="sheetPreview" class="lg:flex-1 lg:min-w-0" />
                    <div class="flex flex-col gap-5 lg:w-[248px] lg:shrink-0">
                        <button @click.prevent="makeContactSheet()"
                                :disabled="uiStore.sheetBusy" data-sheet-make
                                class="btn btn-primary btn-lg rounded-lg">{{
                                    uiStore.sheetBusy ? t('sheet.making') : t('sheet.download') }}</button>
                        <SheetControl :count="uiStore.batchTotals().done" />
                    </div>
                </div>

                <!-- INITIAL INFORMATION SECOND COLUMN -->

                <div v-show="uiStore.isUploading || uiStore.awaitingFetchImage" class="rounded-2xl border border-rule bg-raised p-6">
                    <h2 class="flex items-center text-lg font-bold"><svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>{{ t('dropzone.uploading') }}</h2>
                </div>


                <!-- Everything a visitor reads BEFORE there is a picture: the three
                  -- steps, the what-it's-for link, the samples. It used to stand
                  -- down on workableImage.ready, which meant "the upload came
                  -- back". Nothing uploads on arrival any more, so that never
                  -- became true and the whole introduction sat beside a staged
                  -- photograph, offering samples to somebody who had already
                  -- chosen one. Having a file is the thing that ends it. -->
                <div v-show="!uiStore.files.length && !uiStore.videoTrimming && !uiStore.isUploading" class="text-left flex flex-col gap-7">

                    <!-- The three steps, and on a task page they are THAT
                      -- task's three. This panel used to describe the generic
                      -- tool on all 110 of them: "Upload your images … or a
                      -- batch of 20" on a page whose subject is turning one PDF
                      -- into pages. The registry has carried the right three
                      -- per task per language all along -- they were only being
                      -- rendered in the static tutorial further down, which is
                      -- for a crawler and is a screen and a half away from the
                      -- reader. -->
                    <div v-show="!myWURFLData.isMobilePhone && !hasOwnSteps">
                        <h2 class="text-2xl font-black tracking-tight mb-4">{{ t('how.title') }}</h2>
                        <ol class="flex flex-col gap-4">
                            <li v-for="(step, i) in howSteps" :key="i" class="flex gap-4 items-start">
                                <span class="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-brand-tint text-[15px] font-black text-brand">{{ i + 1 }}</span>
                                <span>
                                    <span class="block text-[17px] font-bold">{{ step.title }}</span>
                                    <span class="block text-[15px] text-ink-mute leading-relaxed">{{ step.body }}</span>
                                </span>
                            </li>
                        </ol>
                    </div>

                    <!-- Sits with "How it works" because that is where someone
                      -- who does not yet know what this is will be looking. -->
                    <a :href="whatForHref"
                       class="flex items-center justify-between gap-3 rounded-xl border border-rule bg-raised px-4 py-3.5 transition hover:border-brand hover:shadow-sm">
                        <span>
                            <span class="block text-[16px] font-bold text-brand">{{ t('how.whatFor') }}</span>
                            <span class="block text-[14px] text-ink-mute leading-snug">{{ t('how.whatForBody') }}</span>
                        </span>
                        <svg class="h-5 w-5 shrink-0 text-brand" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path>
                        </svg>
                    </a>

                    <div v-show="myWURFLData.isMobilePhone">
                        <a class="text-[15px] font-bold text-brand" href="#" @click.prevent="uiStore.showSmartphoneInstrux = !uiStore.showSmartphoneInstrux">{{ t('how.mobileTitle') }}</a>
                        <ol v-show="uiStore.showSmartphoneInstrux" class="list-disc list-inside mt-3 flex flex-col gap-1.5 text-[15px] text-ink-mute">
                            <li>{{ t('how.mobile1') }}</li>
                            <li>{{ t('how.mobile2') }}</li>
                            <li>{{ t('how.mobile3') }}</li>
                        </ol>
                    </div>

                    <!-- Six JPEG photographs. Exactly the input for "make
                      -- these smaller", and nothing whatever to a page about
                      -- converting a PNG or a WebP, or one that wants a PDF --
                      -- there the offer is a dead end dressed as a demo. Shown
                      -- only where the registry does not name a different
                      -- input. -->
                    <div v-show="!myWURFLData.isMobilePhone && samplesTeachSomething && !offersMeme" class="flex flex-col gap-3">

                        <div class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('dropzone.tryOne') }}</div>
                        <div class="grid grid-cols-3 gap-3">
                            <a href="#" @click.prevent="setImageOnStage(1)" class="block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand">
                                <img :src="`${uiStore.s3urlRoot}image1.jpg?imgeng=/w_240/f_jpg`" loading="lazy" alt="Sample image 1" class="block aspect-[4/3] w-full object-cover"
                                     :srcset="`${uiStore.s3urlRoot}image1.jpg?imgeng=/w_480/f_jpg 2x`" />
                            </a>

                            <a href="#" @click.prevent="setImageOnStage(2)" class="block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand">
                                <img :src="`${uiStore.s3urlRoot}image2.jpg?imgeng=/w_240/f_jpg`" loading="lazy" alt="Sample image 2" class="block aspect-[4/3] w-full object-cover"
                                     :srcset="`${uiStore.s3urlRoot}image2.jpg?imgeng=/w_480/f_jpg 2x`" />
                            </a>

                            <a href="#" @click.prevent="setImageOnStage(3)" class="block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand">
                                <img :src="`${uiStore.s3urlRoot}image3.jpg?imgeng=/w_240/f_jpg`" loading="lazy" alt="Sample image 3" class="block aspect-[4/3] w-full object-cover"
                                     :srcset="`${uiStore.s3urlRoot}image3.jpg?imgeng=/w_480/f_jpg 2x`" />
                            </a>

                            <a href="#" @click.prevent="setImageOnStage(4)" class="block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand">
                                <img :src="`${uiStore.s3urlRoot}image4.jpg?imgeng=/w_240/f_jpg`" loading="lazy" alt="Sample image 4" class="block aspect-[4/3] w-full object-cover"
                                     :srcset="`${uiStore.s3urlRoot}image4.jpg?imgeng=/w_480/f_jpg 2x`" />
                            </a>

                            <a href="#" @click.prevent="setImageOnStage(5)" class="block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand">
                                <img :src="`${uiStore.s3urlRoot}image5.jpg?imgeng=/w_240/f_jpg`" loading="lazy" alt="Sample image 5" class="block aspect-[4/3] w-full object-cover"
                                     :srcset="`${uiStore.s3urlRoot}image5.jpg?imgeng=/w_480/f_jpg 2x`" />
                            </a>

                            <a href="#" @click.prevent="setImageOnStage(6)" class="block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand">
                                <img :src="`${uiStore.s3urlRoot}image6.jpg?imgeng=/w_240/f_jpg`" loading="lazy" alt="Sample image 6" class="block aspect-[4/3] w-full object-cover"
                                     :srcset="`${uiStore.s3urlRoot}image6.jpg?imgeng=/w_480/f_jpg 2x`" />
                            </a>
                        </div>
                    </div>

                </div>

                <!-- h4 v-show="!uiStore.workableImage.ready && !uiStore.isUploading">[VIDEO BELOW TO BE REPLACED WTH VIDEO THAT ILLUSTRATES ONLINE IMAGE OPTIMIZER]</h4 -->
                <!-- iframe v-show="!uiStore.workableImage.ready && !uiStore.isUploading" width="90%" src="https://www.youtube.com/embed/Vt9f7--VOk8" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe -->

                <!-- END OF INITIAL INFORMATION SECOND COLUMN  -->


                <div id="single-stage" v-show="(uiStore.files.length || uiStore.videoTrimming) && !uiStore.batchMode" class="min-w-0 w-full bg-base-100 shadow-x clear-both">
                    <div v-show="uiStore.showFetchedImagePreviewArea" class="bg-gradient-to-r from-[#FF66FF] to-[#4636B3] p-4 rounded-xl flex items-center justify-center">
                        <div class="px-4" id="fetchedUrlPreview">
                        </div>
                    </div>
                    <div class="flex flex-col gap-5 pt-5 md:pt-0">

                        <!-- An animated GIF KEEPS its frames now, in the GIF version
                          -- only and only while nothing has been baked onto it: see
                          -- animationKept in the store. Every other format flattens it,
                          -- and so does the canvas.
                          --
                          -- Still a notice rather than a refusal, for the reason it
                          -- always was: a still out of a moving GIF is a normal thing
                          -- to want, and this page cannot know which visitor it has.
                          -- What changed is that the sentence now has to say WHICH of
                          -- the two happened, because both are possible. -->
                        <!-- THE ANIMATION IS FASTER THAN THE CLIP, said after the
                          -- fact and prominently.
                          --
                          -- It replaces "only the first 10 seconds were used".
                          -- Nothing is discarded any more: the visitor picks the
                          -- span with the handles, and a span wider than the cap
                          -- is SAMPLED across the whole of it, which comes out as
                          -- a speed-up. The trimmer says so before converting;
                          -- this says so afterwards, because by then the clip has
                          -- left the screen and the GIF on it is not the speed the
                          -- visitor filmed.
                          --
                          -- role="status": nothing is wrong, but it should reach
                          -- somebody not looking at this corner of the page. -->
                        <div v-if="uiStore.videoSped" id="video-sped" role="status"
                             class="mt-5 md:mt-0 flex items-start gap-3 rounded-2xl border border-brand bg-brand-tint p-4">
                            <svg class="w-6 h-6 shrink-0 text-brand mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 7v5l3 2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <div class="flex flex-col gap-1">
                                <span class="text-[15px] font-bold leading-snug">{{ t('video.spedUp', { seconds: spedClock, speed: spedLabel }) }}</span>
                                <span class="text-[15px] text-ink-soft leading-snug">{{ t('video.spedUpHow') }}</span>
                            </div>
                        </div>

                        <!-- WHAT THE DRAWING LOST ON THE WAY TO BEING A PNG.
                          -- Both of these are SILENT: the picture comes back
                          -- looking right and something is missing from it.
                          -- Measured on the competitor -- svgtopng.com returns
                          -- a text-bearing SVG as shapes on white, with no
                          -- warning at all -- so saying so IS the feature.
                          -- A notice and not a refusal, for the reason the
                          -- animated-GIF one records. -->
                        <div v-if="uiStore.svgHadText" id="svg-text-notice"
                             class="flex items-start gap-2 text-[15px] text-ink-soft mt-5 md:mt-0">
                            <svg class="w-5 h-5 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                <path d="M5 7V5h14v2M12 5v14M9 19h6" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <span>{{ t('dropzone.svgText') }}</span>
                        </div>

                        <div v-if="uiStore.svgHadExternal" id="svg-external-notice"
                             class="flex items-start gap-2 text-[15px] text-ink-soft mt-5 md:mt-0">
                            <svg class="w-5 h-5 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <span>{{ t('dropzone.svgExternal') }}</span>
                        </div>

                        <div v-if="uiStore.animatedGif" id="animated-gif-notice"
                             class="flex items-start gap-2 text-[15px] text-ink-soft mt-5 md:mt-0">
                            <svg class="w-5 h-5 shrink-0 mt-[1px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 8h.01M11 12h1v4h1" stroke-linecap="round" />
                            </svg>
                            <span>{{ t(uiStore.animationKept ? 'dropzone.animatedGifKept' : 'dropzone.animatedGif') }}</span>
                        </div>

                        <!-- progress while the versions are still arriving -->
                        <div v-show="uiStore.optimizationInProgress" class="flex items-center gap-4">
                            <div class="radial-progress text-brand" :style="`--value:${uiStore.optimizedImagesPercentage};`">{{uiStore.optimizedImagesPercentage}}%</div>
                            <span class="text-[17px] text-ink-soft">{{ t('result.working') }}</span>
                        </div>

                        <div class="flex flex-wrap gap-3">
                            <!-- "Download all N versions" and "See every version" used to sit
                              -- here, next to a headline that already says how much smaller it
                              -- got: three buttons for one intention. The primary button above is
                              -- the download now, and the table below is reached by scrolling. -->
                        </div>

                        <!-- THE STAGE. One surface with two jobs: pointing at the image
                          -- (crop, flip) and looking at what came back. Tabs rather than
                          -- two stacked pictures, which would push the settings off the
                          -- bottom of the screen.
                          --
                          -- The tools sit ON the picture, because pointing at it is what
                          -- this path is for and what a batch cannot do. -->
                        <!-- A phone gets this too.
                          -- It was withheld for room, back when the stage was a
                          -- second look at a picture the drop zone was already
                          -- showing. It is now the only place the file is named,
                          -- the only place an edit in progress is announced, and
                          -- where the button that hands the file over lives -- so
                          -- a phone was getting a flip control with nothing to say
                          -- whether it had worked, which is the device where the
                          -- wait is longest. -->
                        <!-- The trimmer takes the stage's place while a clip is
                          -- waiting to be converted: they are the same question
                          -- ("here is what you gave me") asked of two different
                          -- things, and showing both would offer an empty stage
                          -- beside a filmstrip. -->
                        <VideoTrimmer v-if="uiStore.videoTrimming"
                                      @convert="convertTrimmed" @cancel="cancelClip" />

                        <div v-show="stageShown && !uiStore.videoTrimming" class="rounded-2xl border border-rule bg-raised p-5">
                            <div class="flex items-center justify-between mb-3">
                                <div class="flex items-center gap-2">
                                    <div class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ stagedIsPdf ? t('result.yourDocument') : t('result.yourImage') }}</div>
                                    <div v-show="editing" class="flex items-center gap-1.5 text-[12px] font-bold text-brand" role="status">
                                        <svg class="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                        </svg>{{ t('result.applying') }}
                                    </div>
                                </div>
                                <div v-show="!stagedIsPdf" class="flex gap-1 rounded-[9px] bg-sunk p-[3px]">
                                    <button @click.prevent="pickTab('edit')" class="rounded-[7px] px-3 py-1.5 text-[13px] font-bold"
                                            :class="stageTab === 'edit' ? 'bg-raised shadow-sm' : 'text-ink-mute'">{{ t('result.tabEdit') }}</button>
                                    <button @click.prevent="pickTab('compare')" :disabled="!uiStore.showDownloadables"
                                            class="rounded-[7px] px-3 py-1.5 text-[13px] font-bold disabled:opacity-40"
                                            :class="stageTab === 'compare' ? 'bg-raised shadow-sm' : 'text-ink-mute'">{{ t('result.tabCompare') }}</button>
                                </div>
                            </div>

                            <!-- A document, so a document is what is shown: no frame
                              -- to drag, no flip, no reset. The button underneath is
                              -- the whole interaction, and PdfProgress takes over the
                              -- moment it is pressed. -->
                            <div v-show="stagedIsPdf"
                                 class="flex flex-col items-center justify-center gap-3 rounded-2xl bg-stage px-6 py-16 text-center">
                                <svg class="h-14 w-14 text-ink-faint" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <path d="M14 2v6h6"></path><path d="M8 13h8"></path><path d="M8 17h5"></path>
                                </svg>
                                <div class="text-[15px] font-bold break-all">{{ stagedFile && stagedFile.name }}</div>
                                <div class="max-w-sm text-[14px] text-ink-mute leading-snug">{{ t('pdf.stageNote') }}</div>
                            </div>

                            <!-- :tools is off for a clip. Three of the four
                              -- buttons cannot work on an animation -- a canvas
                              -- holds one frame, so a flip or a crop baked here
                              -- would hand back a still -- and the crop that CAN
                              -- work does so as a cr_ directive with no tool
                              -- needed. A row of controls that mostly cannot act
                              -- is more confusing than no row, which is the same
                              -- verdict a document reached. -->
                            <!-- NO CROP/FLIP/RESET ROW ON A MEME EITHER.
                              -- A clip gets none because three of the four
                              -- cannot act on an animation at all. A meme is
                              -- the other reason: they all work here, and they
                              -- are a row of furniture between the picture and
                              -- the words on the one page where the words are
                              -- the whole errand. On a phone that row is part
                              -- of what puts the picture behind the keyboard.
                              --
                              -- Real capability given up rather than a dead
                              -- control removed: cropping a meme card is a
                              -- thing somebody might want, and /crop-image is
                              -- one menu item away. One line to put back. -->
                            <CropSurface v-show="stageTab === 'edit' && !stagedIsPdf" ref="cropSurface"
                                         :tools="!offersVideo && !offersMeme && !offersInspect"
                                         :turns="stageTurns"
                                         :class="editing ? 'opacity-60 transition-opacity' : 'transition-opacity'"
                                         :file="cropSource" :display="stageDisplay" :crop="uiStore.crop"
                                         :flip-h="uiStore.flipH" :flip-v="uiStore.flipV"
                                         :baked-flip-h="bakedFlip.h" :baked-flip-v="bakedFlip.v"
                                         :baked-decorations="bakedDecorations"
                                         :preset-ratio="presetRatio" :rotate="uiStore.rotate"
                                         :min-height="360"
                                         :zones="uiStore.memeZones"
                                         :tiles="offersTiles
                                             ? {cols: uiStore.tileCols,
                                                rows: uiStore.tileRows,
                                                width: stagedDims.w,
                                                height: stagedDims.h}
                                             : null"
                                         :caption-boxes="captionBoxesShown ? captionBoxes : null"
                                         :caption-empty="captionEmpty"
                                         @caption-box="setCaptionBox"
                                         :editing="captionEditing"
                                         @caption-tap="editCaption"
                                         @caption-text="onCaptionText"
                                         @caption-done="finishCaption"
                                         @apply-crop="applyCrop"
                                         @flip-h="toggleFlip('flipH')"
                                         @flip-v="toggleFlip('flipV')"
                                         @turn="turnBy"
                                         @reset="resetTransforms" />

                            <!-- What is on the stage, named underneath it like a
                              -- caption. It used to sit ABOVE the picture and read
                              -- from workableImage, which the server fills in on its
                              -- way back from an upload. With the upload deferred to
                              -- Download that object is empty while the picture is on
                              -- screen, and the line rendered "NaN undefined × px".
                              -- The staged file is right here and knows its own name
                              -- and size; the pixels are measured from it.
                              --
                              -- Inside the card, so it lines up with the heading and
                              -- the tools rather than starting 16px to their left. -->
                            <div v-show="stagedFile && (stageTab === 'edit' || stagedIsPdf)" class="mt-4 border-t border-rule pt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                                <div class="min-w-0">
                                    <div class="text-[15px] font-bold break-all leading-snug">{{ stagedFile && stagedFile.name }}</div>
                                    <div v-show="!uiStore.isUploading" class="mt-0.5 text-[13px] text-ink-mute tabular-nums">
                                        {{ stagedFile ? formatBytes(stagedFile.size) : '' }}<template v-if="stagedDims.w"> &middot; {{stagedDims.w}} &times; {{stagedDims.h}} px</template>
                                    </div>
                                    <div v-show="uiStore.isUploading" class="mt-0.5 text-[13px] text-ink-mute" role="status">{{ t('result.uploading') }}</div>
                                </div>
                                <button v-show="!offersInspect && !uiStore.isEmptyWorkableImage && !workInFlight"
                                        data-primary @click.prevent="primaryAction()" :title="primaryTitle" :disabled="delivering"
                                        class="btn btn-primary rounded-lg shrink-0">{{ primaryLabel }}</button>
                                    <WorkingNote v-show="!uiStore.isEmptyWorkableImage && saving"
                                                 class="shrink-0" />
                            </div>

                            <div v-show="stageTab === 'compare' && uiStore.showDownloadables">
                            <div class="flex items-baseline justify-between mb-3">
                                <div class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('result.beforeAfter') }}</div>
                                <div class="text-[13px] text-ink-label">{{ t('result.dragCompare') }}</div>
                            </div>
                            <!-- The shape comes from the image's own dimensions, not
                              -- from whichever layer happens to load. Both layers are
                              -- remote, and the "before" one is the FULL original --
                              -- 20.8 MB and 19 megapixels in the report that found
                              -- this. When it did not arrive, the box it was holding
                              -- open collapsed and the comparison became a 40px
                              -- sliver with two size labels under it. -->
                            <div class="relative select-none overflow-hidden rounded-xl bg-stage"
                                 :style="compareShape">
                                <img :src="beforeUrl" alt="Original" class="block w-full" />
                                <div class="absolute inset-0 overflow-hidden" :style="`width: ${uiStore.comparePosition}%`">
                                    <!-- widened by the inverse of the clip, so both
                                      -- layers render at the same scale -->
                                    <img :src="compareOptimizedUrl" alt="Optimized" class="block max-w-none" :style="`width: ${10000 / Math.max(uiStore.comparePosition, 1)}%`" />
                                </div>
                                <div class="pointer-events-none absolute inset-y-0 w-0.5 bg-white shadow" :style="`left: ${uiStore.comparePosition}%`"></div>
                                <input type="range" min="0" max="100" step="0.5" v-model="uiStore.comparePosition"
                                       :aria-label="t('result.compareAria')"
                                       class="absolute inset-0 h-full w-full cursor-ew-resize opacity-0" />
                            </div>
                            <div class="mt-3 flex justify-between text-[13px]">
                                <span class="font-bold text-ink-soft">{{ t('result.optimized') }} &middot; {{formatBytes(primaryVariantSize)}}</span>
                                <span class="text-ink-label">{{ t('result.original') }} &middot; {{formatBytes(beforeSize)}}</span>
                            </div>
                            </div>
                        </div>

                        <!-- Afterwards, not before, and only when there is something
                          -- to report. The percentage has no floor: a small or already
                          -- compressed picture can come back LARGER, and the same markup
                          -- then reads "-50% smaller". It was survivable while this was
                          -- incidental; as the line that greets a finished download it
                          -- would be the page calling a loss a saving.
                          --
                          -- This number used to lead the page, at a size nothing else could
                          -- compete with, announcing a saving on an image the visitor had not
                          -- asked to optimise yet. It is a fact about a file they now have, so
                          -- it is reported once they have it. -->
                        <div id="savings" v-show="handedOver && uiStore.estimatedSavingsPercentage > 0" class="flex flex-col gap-1">
                            <div class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('result.eyebrow') }}</div>
                            <div class="flex items-baseline gap-1.5 leading-[0.86]">
                                <span class="text-[86px] md:text-[120px] font-black tracking-[-0.05em] text-brand">{{uiStore.estimatedSavingsPercentage}}</span>
                                <span class="text-[38px] md:text-[52px] font-black tracking-[-0.03em] text-brand">%</span>
                                <span class="ml-2 text-2xl md:text-3xl font-black">{{ t('result.smaller') }}</span>
                            </div>
                            <div class="text-[17px] text-ink-soft mt-1.5">
                                <i18n-t keypath="result.downTo" tag="span">
                                    <template #original><b>{{formatBytes(uiStore.workableImage.size)}}</b></template>
                                    <template #now><b class="text-brand">{{formatBytes(phoneVariantSize)}}</b></template>
                                </i18n-t>
                            </div>
                        </div>

                        <!-- Developer output, so it follows the developer
                          -- switch rather than appearing for everybody. It also
                          -- only ever made sense on a full-sized screen. -->
                        <div v-show="uiStore.pictureElements && !uiStore.optimizationInProgress && uiStore.workableImage.ready && !myWURFLData.isMobilePhone" class="rounded-2xl bg-code-bg p-5">
                            <div class="flex items-center justify-between mb-3">
                                <div class="text-xs font-bold uppercase tracking-[0.09em] text-code-label">{{ t('picture.paste') }}</div>
                                <a @click.prevent="copyPictureTagsToClipboard()" :title="t('picture.copyTitle')" href="#" class="flex items-center gap-1.5 text-sm font-bold text-code-link hover:text-white" role="button"><svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                                    <rect x="9" y="9" width="12" height="12" rx="2"></rect><path d="M5 15V5a2 2 0 0 1 2-2h10"></path>
                                    </svg>{{ t('picture.copy') }}</a>
                            </div>
                            <!-- What the markup should say, asked where the
                              -- markup is. These were briefly a card of their
                              -- own above, so that a first download could carry
                              -- them; they read as a question about nothing,
                              -- because until the versions are back there is no
                              -- markup to fold them into and no picture to
                              -- describe.
                              --
                              -- The snippet below is a computed, so both reach
                              -- it as they are typed and the Copy above always
                              -- hands over what is on screen. The two markup
                              -- files in the zip are composed AT the download,
                              -- which is before this exists, so they carry no
                              -- folder -- and picture_elem_page.html is a
                              -- working demo BECAUSE of that: the images sit
                              -- beside it in the archive. -->
                            <div id="picture-inputs" class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4">
                                <label class="flex flex-col gap-1.5">
                                    <span class="text-xs font-bold uppercase tracking-[0.09em] text-code-label">{{ t('picture.folder') }}</span>
                                    <input type="text" v-model="uiStore.subfolderUser" class="input input-sm w-full rounded-lg bg-white/10 text-white placeholder-white/40 border border-white/20" :placeholder="t('picture.folderPlaceholder')">
                                </label>
                                <label class="flex flex-col gap-1.5">
                                    <span class="text-xs font-bold uppercase tracking-[0.09em] text-code-label">{{ t('picture.alt') }}</span>
                                    <input type="text" v-model="uiStore.altattr" class="input input-sm w-full rounded-lg bg-white/10 text-white placeholder-white/40 border border-white/20" :placeholder="t('picture.altPlaceholder')">
                                </label>
                            </div>
                            <p class="mt-1 mb-3 text-[13px] text-code-label leading-snug">{{ t('picture.note') }}</p>
                            <div class="text-left text-[13px] overflow-x-auto text-code-ink">
                                <pre id="pictureElementText">{{pictureSnippet}}</pre>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Everything that acts on the picture, under the picture and
                  -- under the button, in the column the picture is in.
                  --
                  -- These were in the left column, beside the drop zone, which
                  -- is where they were when the drop zone was the whole
                  -- application. The stage is the working surface now, and a
                  -- control that changes what is on the stage belongs with it
                  -- rather than in the other half of the page.
                  --
                  -- Deliberately BELOW the button, which reverses what the
                  -- scrim control used to be arranged for -- see the darken
                  -- case in tests/e2e_test.sh, which was written around the
                  -- older order and has been restated, not deleted. -->
                <div v-if="offersOverlay && uiStore.files.length" class="w-full mt-5">
                    <OverlayControl @change="reoptimize" />
                </div>
                <!-- WHAT GOES BEHIND THE DRAWING, and only once one has been
                  -- converted (`svgConverted`, not the task): an SVG page with
                  -- an empty stage has nothing for the colour to be behind.
                  --
                  -- Reported with a Visio schematic whose lettering is black.
                  -- The PNG was transparent, as the source asked, so on a dark
                  -- background the labels were simply not there -- a perfect
                  -- conversion nobody could read. -->
                <div v-if="uiStore.svgConverted && uiStore.files.length"
                     class="w-full mt-5">
                    <SvgBackground :size="stagedDims" @change="reoptimize" />
                </div>
                <div v-if="offersWatermark && uiStore.files.length" class="w-full mt-5">
                    <WatermarkControl :base-url="baseUrl" @change="reoptimize" />
                </div>
                <!-- ...and on a clip, where it replaced a single text field.
                  --
                  -- The words were already living in memeTop/memeBottom, so
                  -- this is the control that belongs to them: two areas, a
                  -- colour and a size each, a shared face and capitals. A
                  -- second control offering a subset of the same fields would
                  -- have been a second answer to "what can the words look
                  -- like".
                  --
                  -- @change is free here: nothing is baked until Save, so the
                  -- reoptimize() it triggers finds the conversion signature
                  -- unchanged and does nothing. -->
                <div v-if="(offersMeme || offersVideo) && uiStore.files.length" class="w-full mt-5">
                    <MemeControl @change="reoptimize" />
                </div>

                <!-- TILES ARE NOT A SECOND PICTURE, so this is not on the
                  -- stage. Where the cuts fall is drawn OVER the photograph by
                  -- CropSurface, because "is 3 x 4 right for this one" is a
                  -- question you answer by looking; the numbers that move the
                  -- lines sit underneath with everything else that is typed
                  -- rather than dragged.
                  --
                  -- stagedDims rather than the file's own width and height:
                  -- UploadableFile carries both and nothing has ever written
                  -- to them, so they read zero everywhere. The measured pair
                  -- is the one the stage caption already prints. -->
                <div v-if="offersTiles && uiStore.files.length" class="w-full mt-5">
                    <TileControl :width="stagedDims.w" :height="stagedDims.h" />
                    <!-- Outside #single-stage on purpose. #video-error was
                      -- reported as "the UI disappeared and nothing happened"
                      -- because its message lived in a box that is hidden at
                      -- exactly the moment it is the only thing worth seeing.
                      -- The stage does stay up here; the rule is still worth
                      -- following rather than rediscovering. -->
                    <p v-if="uiStore.tileError" id="tile-error" role="alert"
                       class="mt-3 text-[15px] text-error leading-snug">{{ uiStore.tileError }}</p>
                </div>


                <!-- Settings, in three groups: size and shape, quality and
                  -- format, privacy. Both paths share this one component, so
                  -- the controls they have in common cannot drift apart. -->
                <!-- Not on the ASCII page. Format, quality, EXIF, crop and
                  -- the pixel width are all ImageEngine directives applied to
                  -- an image that is about to be delivered as an image -- and
                  -- this page delivers text, rendered from a JPEG the CDN
                  -- makes at a fixed 800px. Every control in there would be a
                  -- setting that changes nothing, which is the same fault as
                  -- the flip button a document could never use. -->
            </div>

    <!-- END OF SECOND COLUMN -->

            <!-- THE SETTINGS, BESIDE THE PICTURE RATHER THAN UNDER IT.
              --
              -- Last in the DOM so a phone reads drop zone, picture, settings;
              -- placed in column one on md+ so a desktop reads the settings
              -- next to the thing they change, in the room the drop zone gave
              -- back when its thumbnail stood down.
              --
              -- It is v-show'd on something being staged, which is the same
              -- condition the grid is, so it can never land in a layout that
              -- has no cell for it. -->
            <!-- A REPORT ABOUT THE PICTURE, BESIDE THE PICTURE.
              --
              -- It was below the stage at full width, which put the finding a
              -- scroll away from the photograph it is about and left the
              -- whole left column empty next to it. This is the cell the
              -- options panel would have had, with the same grid placement --
              -- and OptionsPanel stands down on this page, so the two can
              -- never want the cell at once. -->
            <MetadataPanel v-if="offersInspect && uiStore.files.length"
                           class="min-w-0 w-full"
                           :class="stageWide ? 'md:col-start-1 md:row-start-2' : 'mt-5'"
                           :info="metadataInfo" :reading="metadataReading" />

            <OptionsPanel v-show="uiStore.files.length && !offersAscii && !offersInspect"
                          class="min-w-0 w-full"
                          :class="stageWide ? 'md:col-start-1 md:row-start-2' : 'mt-5'"
                          :crop-trigger="cropTrigger" :mode="uiStore.batchMode ? 'batch' : 'single'"
                          :is-mobile-phone="myWURFLData.isMobilePhone"
                          :document-mode="stagedIsPdf"
                          :video-mode="offersVideo"
                          :stage-turns="stageTurns"
                          :sheet-mode="offersSheet"
                          :configured="presetIntact ? words.configured : ''"
                          @change="reoptimize"
                          @crop-region="openCropper" />

            <!-- THE SAME ACTION, AT THE END OF THE CONTROLS.
              --
              -- Reported after watching somebody use the caption controls on a
              -- phone: they work down the panel -- two captions, two colours,
              -- two sizes, a face, the capitals switch -- and by the time they
              -- reach the bottom the button that finishes the job is several
              -- hundred pixels above, off the top of the screen. Nothing at the
              -- end of the controls says what to do next.
              --
              -- STACKED SCREENS ONLY (md:hidden). On a desktop the panel sits
              -- in column one beside the picture and the real button is on
              -- screen the whole time; a second one there would be two Save
              -- buttons side by side, which is worse than the problem.
              --
              -- THE ECHO IS A THIRD CALL SITE, AND IT SHIPPED A BUG BECAUSE OF
              -- IT. /inspect-metadata suppresses the primary button -- there is
              -- nothing to produce and nothing may be uploaded -- and the two
              -- `data-primary` buttons were gated while THIS one was not. It is
              -- `md:hidden`, so it exists only on a phone, and it is not marked
              -- `data-primary`, so every check written with that selector read
              -- zero. Two independent reasons it was invisible, on a desktop,
              -- to a selector. A real person on a real iPhone pressed it and
              -- uploaded her photograph from the page that promises it never
              -- leaves the device.
              --
              -- ANY GATE ON THE PRIMARY ACTION MUST BE APPLIED TO ALL THREE.
              --
              -- NOT data-primary, deliberately. Seven browser suites find the
              -- main action with [data-primary] and
              -- tests/deferred_render_test.sh asserts there is exactly ONE
              -- visible. This is the same action offered a second time, not a
              -- second action, so it carries the same label and the same
              -- handler and none of the marking. -->
            <button v-show="!offersInspect && uiStore.files.length && !uiStore.isEmptyWorkableImage && !workInFlight"
                    @click.prevent="primaryAction()" :title="primaryTitle" :disabled="delivering"
                    class="btn btn-primary btn-lg rounded-lg w-full md:hidden"
                    data-primary-echo>{{ primaryLabel }}</button>
              <WorkingNote v-show="uiStore.files.length && !uiStore.isEmptyWorkableImage && saving"
                           class="md:hidden justify-center" />
        </div>


<!-- BATCH RESULTS -->
    <!-- One row per image, not per variant. The whole point of batch mode is
      -- that twelve photos produce twelve files, so this table is a different
      -- shape from the one below rather than the same one repeated. -->
    <div id="batch-results" class="mx-auto max-w-screen-xl px-4 md:px-8 w-full"
         v-show="uiStore.batchMode && uiStore.batchItems.length">

        <div class="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <h2 class="text-2xl font-black tracking-tight">
                {{ uiStore.batchRunning ? 'Optimizing your images…' : 'Your optimized images' }}
            </h2>
            <span v-show="uiStore.batchRunning" class="text-[15px] tabular-nums text-ink-mute">
                {{ t('batch.progress', { done: uiStore.batchDone, total: uiStore.batchItems.length }) }}
            </span>
        </div>

        <!-- The one number worth putting above a table of twelve rows -->
        <div v-show="!uiStore.batchRunning && uiStore.batchTotals().done"
             class="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-rule bg-raised px-5 py-4">
            <!-- Only when there IS a saving. The minus was hard-coded here
              -- while percent carries its own sign, so a set that came back
              -- bigger printed "--11%" -- two minus signs and a number that
              -- meant the opposite of what the figure is for. A conversion can
              -- legitimately grow the file (PDF pages to PNG do), and the
              -- honest answer is to show the sizes and drop the headline
              -- rather than dress a loss up as one. The single-image path has
              -- always done this: #savings appears only when it is positive. -->
            <span v-if="uiStore.batchTotals().percent > 0"
                  class="text-[38px] md:text-[52px] font-black tracking-[-0.03em] text-brand leading-none">
                &minus;{{uiStore.batchTotals().percent}}%
            </span>
            <span class="text-[17px] text-ink-soft">
                <i18n-t :keypath="uiStore.batchTotals().percent > 0 ? 'batch.totals' : 'batch.totalsFlat'" tag="span">
                    <template #before><b>{{formatBytes(uiStore.batchTotals().before)}}</b></template>
                    <template #after><b>{{formatBytes(uiStore.batchTotals().after)}}</b></template>
                </i18n-t>
                <span class="block text-[15px] text-ink-mute">
                    {{ t('batch.totalsSub', {
                            done: uiStore.batchTotals().done,
                            total: uiStore.batchTotals().total,
                            detail: uiStore.pictureElements
                                ? t('batch.filesAcross', { n: uiStore.enabledFormats.length * 2, m: uiStore.enabledFormats.length })
                                : t('batch.asFormat', { format: uiStore.batchFormat.toUpperCase() }) }) }}
                </span>
            </span>
            <div class="ml-auto flex flex-wrap items-center gap-2">
                <!-- The zip stays the primary action: separate files are what
                  -- most people came for. Putting them back together as one
                  -- document is the secondary one, so it is a quieter button
                  -- beside it rather than a second bright one.
                  --
                  -- Hidden unless the output is a plain JPEG: a PDF embeds JPEG
                  -- byte for byte, but PNG needs a different filter and WebP
                  -- and AVIF have none, so they would have to be re-encoded to
                  -- get in. Better to not offer it than to offer it and
                  -- quietly hand back worse pictures. In <picture> mode there
                  -- are several files per image and no single page order, so it
                  -- is hidden there too. -->
                <!-- The words differ on the sheet page because the RESULT
                  -- does: elsewhere this is one page per photograph, there it
                  -- is one page holding the sheet. "Download as one PDF" is
                  -- true of both and tells you which only after you open it. -->
                <button v-if="canAssemblePdf" @click.prevent="assemblePdf()"
                        :disabled="assemblingPdf" data-batch-pdf
                        class="btn btn-outline rounded-lg">{{ assemblingPdf
                            ? t('batch.assemblingPdf')
                            : (offersSheet ? t('sheet.downloadPdf') : t('batch.downloadPdf')) }}</button>
                <button @click.prevent="downloadBatchSet()"
                        class="btn btn-primary rounded-lg">{{ t('batch.downloadZip') }}</button>
            </div>
        </div>

        <!-- All of them at once, in something a developer can select and paste.
          -- A textarea rather than a <pre>: ten elements is more text than fits
          -- on screen, and this one scrolls and selects like an editor. -->
        <div v-show="batchSnippets" class="mb-5 rounded-2xl bg-code-bg p-5">
            <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div class="text-xs font-bold uppercase tracking-[0.09em] text-code-label">
                    {{ t('picture.pasteBatch') }}
                </div>
                <a @click.prevent="copyBatchSnippets()" href="#" role="button"
                   :title="t('picture.copyAllTitle')"
                   class="flex items-center gap-1.5 text-sm font-bold text-code-link hover:text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                        <rect x="9" y="9" width="12" height="12" rx="2"></rect><path d="M5 15V5a2 2 0 0 1 2-2h10"></path>
                    </svg>{{ t('picture.copyAll') }}</a>
            </div>
            <textarea id="pictureElements" readonly spellcheck="false"
                      :value="batchSnippets"
                      class="w-full h-64 resize-y rounded-lg bg-white/5 p-3 font-mono text-[12px] leading-relaxed text-code-ink border border-white/10"
                      :aria-label="t('picture.batchAria')"></textarea>
            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5 border-t border-white/10 pt-4">
                <label class="flex flex-col gap-1.5">
                    <span class="text-xs font-bold uppercase tracking-[0.09em] text-code-label">{{ t('picture.folder') }}</span>
                    <input type="text" v-model="uiStore.subfolderUser" class="input input-sm w-full rounded-lg bg-white/10 text-white placeholder:text-white/40 border-white/20" :placeholder="t('picture.folderPlaceholder')" />
                </label>
                <p class="text-[13px] text-code-label leading-snug self-end">
                    {{ t('picture.relativeNote') }}
                </p>
            </div>
        </div>

        <div class="overflow-x-auto rounded-2xl border border-rule bg-raised">
        <table class="table w-full">
            <thead>
                <tr class="bg-sunk-soft">
                    <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('batch.colImage') }}</th>
                    <th v-show="uiStore.pictureElements" scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('batch.colAlt') }}</th>
                    <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label text-right">{{ t('batch.colWas') }}</th>
                    <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label text-right">{{ uiStore.pictureElements ? 'Fallback' : 'Now' }}</th>
                    <th scope="col" class="hidden md:table-cell text-xs font-bold uppercase tracking-[0.09em] text-ink-label text-right">{{ t('batch.colSaved') }}</th>
                    <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label text-right">&nbsp;</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="item in uiStore.batchItems" :key="item.id">
                    <td class="text-[15px] font-bold">
                        <div class="flex items-center gap-3">
                            <img v-if="thumbFor(item)" v-peek="thumbFor(item)" :src="thumbFor(item)" alt="" width="40" height="40"
                                 class="h-10 w-10 shrink-0 rounded-lg object-cover bg-stage"
                                 :class="peek.hover || peek.hold ? 'cursor-zoom-in' : ''" />
                            <div v-else class="h-10 w-10 shrink-0 rounded-lg bg-stage"></div>
                            <!-- The OUTPUT's name, which is the same name Save
                              -- writes to disk. It used to be the source's, and
                              -- on a document that reads as a bug: the server
                              -- rasterises pages as JPEG, so choosing PNG left
                              -- every row still saying "f1040_1.jpg" beside a
                              -- size that had changed -- reported as "selecting
                              -- PNG does not refresh". The source name is still
                              -- on the tooltip. Not in <picture> mode, where one
                              -- row stands for six files and no single name is
                              -- the answer. -->
                            <span class="min-w-0 max-w-[14rem] truncate"
                                  :title="item.origName">{{ uiStore.pictureElements
                                      ? item.origName : uiStore.batchOutputName(item) }}</span>
                        </div>
                    </td>
                    <td v-show="uiStore.pictureElements" class="min-w-[12rem]">
                        <input type="text" v-model="item.alt"
                               class="input input-sm w-full rounded-lg border-rule bg-paper text-[14px]"
                               :aria-label="`Alt text for ${item.origName}`"
                               :placeholder="t('picture.altPlaceholder')" />
                    </td>
                    <td class="text-right tabular-nums text-[15px] text-ink-mute">{{formatBytes(item.origSize)}}</td>
                    <td class="text-right tabular-nums text-[15px]">
                        <span v-if="item.status === 'done'" class="font-bold">{{formatBytes(item.outSize)}}</span>
                        <span v-else-if="item.status === 'failed'" class="text-[13px] font-bold uppercase tracking-wide text-error">{{ t('batch.failed') }}</span>
                        <span v-else class="text-[13px] text-ink-label">{{ item.status === 'working' ? 'working…' : 'waiting' }}</span>
                    </td>
                    <td class="hidden md:table-cell text-right tabular-nums text-[15px] text-brand font-bold">
                        <template v-if="item.status === 'done' && item.origSize > item.outSize">
                            &minus;{{Math.round((1 - item.outSize / item.origSize) * 100)}}%
                        </template>
                    </td>
                    <td class="text-right">
                        <a v-show="item.status === 'done' && !uiStore.pictureElements"
                           :href="uiStore.batchDownloadUrl(item)"
                           :download="uiStore.batchOutputName(item)"
                           @click="savedOneRow()"
                           class="btn btn-sm btn-outline rounded-lg">{{ t('batch.save') }}</a>
                        <span v-show="item.status === 'done' && uiStore.pictureElements"
                              class="text-[13px] text-ink-label">{{uiStore.enabledFormats.length * 2}} files</span>
                        <!-- On its own means on its own: the page, rendered, in
                          -- a tab of its own.
                          --
                          -- This used to pull the page back off the CDN and
                          -- stage it in the editor, which was a different thing
                          -- wearing this label. It also left every word around
                          -- the stage describing the job it had just left --
                          -- the button still offering to turn the pages into
                          -- images, the panel still promising one JPEG per page
                          -- -- because the page IS the PDF page. Anyone who
                          -- wants to crop one page can Save it and drop it in.
                          --
                          -- The CDN url rather than the blob: a blob: target is
                          -- refused or silently downloaded by some browsers, and
                          -- cannot be shared or reloaded. -->
                        <a v-show="item.status === 'done'" :href="uiStore.batchViewUrl(item)"
                           target="_blank" rel="noopener" :title="t('batch.openAlone')"
                           class="btn btn-sm btn-ghost rounded-lg text-brand ml-1">{{ t('batch.openAlone') }}</a>
                    </td>
                </tr>
            </tbody>
        </table>
        </div>

        <p v-show="!uiStore.pictureElements" class="mt-3 text-[13px] text-ink-label">
            {{ t('batch.footnote') }}
        </p>
    </div>


<!-- TABLE OF OPTIMIZED VERSIONS -->
    <!-- div id="tableOfDownloadableImages" class="wf"> </div -->
    <!-- ONE VERSION GETS THE TABLE TOO.
      --
      -- It used to need `imageVersions.length > 1`, on the reasoning that a
      -- table exists to choose between versions and one version is no choice:
      -- "the button hands it over and the stage already shows it".
      --
      -- That condition did not just hide a table, it ENCODED AN ASSUMPTION
      -- ABOUT ANOTHER COMPONENT -- that a stage exists to show the picture.
      -- Fetching by URL never made one, so on that path the assumption was
      -- false and the result was nothing at all: the image was fetched,
      -- converted, and reported by a table that was hidden. A condition that
      -- depends on some other part of the page being present is a bug waiting
      -- for a path that does not have it.
      --
      -- And the table is not only a chooser. It carries the width, the height,
      -- the format and the bytes, none of which appear anywhere else for a
      -- single version: #savings is v-show'd on `handedOver && percentage > 0`,
      -- so those numbers were invisible until after a download and never shown
      -- at all when a conversion made the file BIGGER -- which /webp-to-png
      -- does on purpose. On that page a visitor saw no numbers, ever.
      --
      -- The heading pluralises properly in all ten catalogues already
      -- ('{n} version | {n} versions'), so one row reads "1 version". -->
    <!-- A PICTURE AS TEXT, AT FULL WIDTH, and the width is the point.
      -- 
      -- This first went inside the two-column block above, beside the stage,
      -- which is where MemeControl and WatermarkControl correctly live: they
      -- are controls FOR the picture and belong next to it. This is not a
      -- control, it is the DELIVERABLE -- and in that column it measured
      -- 460px of a 1400px window, so a hundred-character render arrived in a
      -- box narrower than its own first line and scrolled sideways. The art
      -- is the thing the visitor came for; it gets the page.
      --
      -- Out here for the same reason #downloadables is: outside the md:w-1/2
      -- column, in the full-width run below the fold. -->
    <div v-if="offersAscii && uiStore.files.length"
         class="mx-auto max-w-screen-xl px-4 md:px-8 w-full mt-6">
        <AsciiPanel @run="runAscii" />
    </div>

    <div id="downloadables" class="mx-auto max-w-screen-xl px-4 md:px-8 w-full"
         v-show="uiStore.workableImage.ready && uiStore.showDownloadables">

        <h2 class="text-2xl font-black tracking-tight mb-4">{{ t('options.versions', uiStore.imageVersions.length) }}</h2>

        <div class="overflow-x-auto rounded-2xl border border-rule bg-raised">
        <table class="table w-full">
            <thead>
                <tr class="bg-sunk-soft">
                <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label"><span class="sr-only">{{ t('table.preview') }}</span></th>
                <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('table.format') }}</th>
                <th scope="col" class="hidden md:table-cell text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('table.width') }}</th>
                <th scope="col" class="hidden md:table-cell text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('table.height') }}</th>
                <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('table.size') }}</th>
                <th scope="col" class="hidden lg:table-cell text-xs font-bold uppercase tracking-[0.09em] text-ink-label">{{ t('table.bestFor') }}</th>
                <th scope="col" class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label text-right">&nbsp;</th>
                </tr>
            </thead>
            <tbody>
            <!-- why the index? https://stackoverflow.com/questions/37354482/vue-js-even-numbered-rows-formatting -->
            <template v-for="imageData in uiStore.imageVersions" :key="imageData">
             <!-- Every version asked for, on every device.
               --
               -- A phone used to be shown only the first two rows, for room.
               -- Ask for JPEG, WebP and AVIF on an Android and two arrive: not
               -- a format that failed, not a device that cannot read one --
               -- the third was made, uploaded and paid for, and then hidden by
               -- a row counter. A version the visitor chose is a version they
               -- are owed a line for; the thumbnail already falls back to the
               -- format's name when the browser cannot draw it. -->
             <tr>
                <!-- Already a local object URL, so this costs no request. It is
                  -- also the only thing in the row that can show whether a flip
                  -- or a crop actually happened -- numbers cannot. -->
                <td class="px-1 md:px-4 w-14">
                    <!-- The thumbnail is the way to see a version full size, now
                      -- that the expanded preview under the row has gone. The
                      -- reasoning that was attached to that preview still holds
                      -- and moves here with it: the CDN url rather than the
                      -- object url, because a blob: target is refused or
                      -- silently downloaded by some browsers and cannot be
                      -- shared or reloaded; a new tab, safely; and only when the
                      -- browser can render the format, or the tab downloads the
                      -- file instead of showing it. -->
                    <a v-if="imageData.blob && uiStore.isImageFormatSupported(imageData.format)"
                       :href="imageData.url || imageData.blob" target="_blank" rel="noopener"
                       :title="t('batch.openAlone')" class="inline-block cursor-zoom-in">
                        <img v-peek="imageData.blob" :src="imageData.blob" alt="" width="40" height="40"
                             class="h-10 w-10 rounded-lg object-cover bg-stage" />
                    </a>
                    <!-- No thumbnail, and a reason rather than a blank.
                      -- The file is fine and the download works: this browser
                      -- just cannot draw the format. A crossed-out eye over the
                      -- format's name says which of the two it is, and the
                      -- title says it in the visitor's own language -- it used
                      -- to say it in English on all ten. -->
                    <div v-else class="flex h-10 w-10 flex-col items-center justify-center gap-0.5 rounded-lg bg-stage text-ink-faint"
                         :title="t('result.cannotDisplay', {format: imageData.format.toUpperCase()})">
                        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18 18 0 0 1-2.16 3.19M6.6 6.6A18 18 0 0 0 2 12s3 8 10 8a9 9 0 0 0 5.4-1.6"></path>
                            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"></path><path d="M2 2l20 20"></path>
                        </svg>
                        <span class="text-[9px] font-bold uppercase leading-none">{{imageData.format}}</span>
                    </div>
                </td>
                <td class="px-1 md:px-4 text-[15px] font-bold uppercase">{{imageData.format}}</td>
                <td class="hidden md:table-cell text-[15px] tabular-nums">{{imageData.naturalWidth}}</td>
                <td class="hidden md:table-cell text-[15px] tabular-nums">{{imageData.naturalHeight}}</td>
                <td class="px-1 md:px-4 text-[15px] tabular-nums font-bold">{{formatBytes(imageData.size)}}</td>
                <td class="hidden lg:table-cell text-[15px] text-ink-mute">{{ t(imageData.description) }}</td>
                <!-- Tight below md. This project's md is 640px, so that is the
                  -- phone boundary -- and at 390px the row overflowed its
                  -- container by 20px, which is why the Download button looked
                  -- cut in half with the table scrolling sideways to reach it. -->
                <td class="text-right whitespace-nowrap px-1 md:px-4"><a href="#" class="btn btn-primary btn-xs md:btn-sm rounded-lg" role="button"  @click.prevent="saveOneVersion(imageData)">{{ t('table.download') }}</a></td>
             </tr>

             <!-- The expanded preview under each row used to live here: the full
               -- image, a hint about right-clicking, and the original and optimised
               -- dimensions repeated. Two problems. It made the table enormous when
               -- the point of a table is to compare at a glance, and its width
               -- alternated on `index % 2` -- 50% for even rows, 25% for odd -- so
               -- two files of identical dimensions were drawn at different sizes,
               -- which reads as one being smaller than the other. The row thumbnail
               -- shows what the version looks like; Download hands it over. -->
            </template>
            </tbody>
        </table>
        </div>
    </div>
            </div>

            <!-- RIGHT RAIL. The gutter David spotted, put to work. Sticky so it
              -- stays with the reader through the results table. -->
            <aside v-if="(adSlotIdRailRight && railFits) || showAdPlaceholders"
                   class="hidden min-[1652px]:block w-[300px] shrink-0 pt-8">
                <div class="ad-block sticky top-6 flex flex-col items-center gap-1.5">
                    <div class="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-faint">{{ t('ads.label') }}</div>
                    <ins v-if="adSlotIdRailRight && railFits" class="adsbygoogle block w-[300px]"
                         style="display:block;width:300px;height:600px"
                         data-ad-client="ca-pub-5261099672579630"
                         :data-ad-slot="adSlotIdRailRight"
                         data-ad-format="vertical"
                         data-full-width-responsive="false"></ins>
                    <div v-else-if="showAdPlaceholders" class="flex h-[600px] w-[300px] items-center justify-center rounded-lg border border-dashed border-rule-strong bg-sunk text-center text-[13px] text-ink-faint">
                        300 &times; 600<br>{{ t('ads.rightRail') }}
                    </div>
                </div>
            </aside>
        </div>
    </div><!-- /#app -->


    <!-- Second ad slot, below the results. Kept out of the way of the drop
      -- zone; see the top banner for how the ids and the placeholder work. -->
    <div v-if="adSlotIdBottom || showAdPlaceholders" class="ad-block mx-auto max-w-screen-xl flex flex-col items-center gap-2 px-4 md:px-8 py-10">
        <div class="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-faint">{{ t('ads.label') }}</div>
        <!-- The same shape as the top banner; the max-width is this one's own
          -- container. Declared in the style rather than as a Tailwind class so
          -- there is one source of truth for the box adsbygoogle measures. -->
        <ins v-if="adSlotIdBottom" class="adsbygoogle mx-auto"
             style="display:block;min-width:320px;max-width:728px;width:100%;height:90px"
             data-ad-client="ca-pub-5261099672579630"
             :data-ad-slot="adSlotIdBottom"
             data-full-width-responsive="false"></ins>
        <div v-else-if="showAdPlaceholders" class="flex h-[60px] md:h-[90px] w-full max-w-[728px] items-center justify-center rounded-lg border border-dashed border-rule-strong bg-sunk text-[13px] text-ink-faint">
            728 &times; 90 &mdash; ad unit id not set yet
        </div>
    </div>

    <!-- FOOTER -->
    <!-- Above everything, and mounted once however many tables are on the
      -- page. See compositions/thumb-peek.js. -->
    <ThumbPeek />

    <AppFooter v-if="!task"  :wurflData="myWURFLData" />
</template>

<script setup>
import {useUiElements} from "./store/uiElements"
import { ref, computed, watch, onMounted, onUnmounted, nextTick, inject } from "vue"

// Components
// import MyModal from "./components/MyModal.vue"
import ModalRetrieveImageFromUrl from "./components/ModalRetrieveImageFromUrl.vue"
import WorkingNote from "./components/WorkingNote.vue"
import NavBar from './components/NavBar.vue'
import DropZone from './components/DropZone.vue'
import FilePreview from './components/FilePreview.vue'
import OptionsPanel from './components/OptionsPanel.vue'
import OverlayControl from './components/OverlayControl.vue'
import SvgBackground from './components/SvgBackground.vue'
import WatermarkControl from './components/WatermarkControl.vue'
import MemeControl from './components/MemeControl.vue'
import AsciiPanel from './components/AsciiPanel.vue'
import MetadataPanel from './components/MetadataPanel.vue'
import { readExif } from './lib/exifRead'
import LibraryStrip from './components/LibraryStrip.vue'
import { demoStrip, looksLikeVideo } from './lib/videoFrames'
import { logo } from './lib/logo'
import { transformImage } from './lib/imageTransform'
import { MEME_SLOTS, memeSlots, memeStoreKeys, memePayload } from './lib/meme'
import { guessPageCount, renderEstimateMs } from './lib/pdfPages'
import { activeTask, taskWords, samplesAreUseful, taskSteps, inputRules, inputVoice, taskInput, taskOffersOverlay, taskOffersWatermark, taskOffersMeme, taskOffersAscii, taskOffersSheet, taskOffersTiles, taskOffersInspect, taskFamily } from './lib/task'
import { useI18n } from 'vue-i18n'
import CropModal from './components/CropModal.vue'
import CropSurface from './components/CropSurface.vue'
import TileControl from './components/TileControl.vue'
import SheetControl from './components/SheetControl.vue'
import SheetPreview from './components/SheetPreview.vue'
import { sheetName } from './lib/contactSheet'
import { buildPdf } from './lib/pdfBuild'
import PdfProgress from './components/PdfProgress.vue'
import VideoProgress from './components/VideoProgress.vue'
import VideoTrimmer from './components/VideoTrimmer.vue'
import AppFooter from './components/AppFooter.vue'

import useSomeUtils from './compositions/utils'

//File Utils
import useFileList, { batchCapacity, isPdf, enteredBy, originalOf } from './compositions/file-list'
import { track, trackOnce, formatOfAll, totalBytes } from './lib/analytics'
import { take as carriedFile, drop as dropCarried, sweep as sweepCarried } from './lib/handoff'
import ThumbPeek from './components/ThumbPeek.vue'
import { peek, vPeek, configurePeek } from './compositions/thumb-peek'
// Uploader
import createUploader from './compositions/file-uploader'
// import { createObjectExpression } from "@vue/compiler-core"
// import { createSimpleExpression } from "@vue/compiler-core"


// Debugging. Timing of when and in which orders certain events trigger
// https://stackoverflow.com/questions/63100658/add-global-variable-in-vue-js-3
// const start = inject('start')
const start = window.start //new Date().getTime()
//console.log(start, "Start time in APP.vue")
// console.log(new Date().getTime() - start,"reading script section of App.vue")

// WURFL.js IS NO LONGER LOADED HERE. It used to be injected from this file
// with createElement, which meant it reached the pages that mount this
// application and no others -- so every walkthrough and every legal page
// carried gam.js waiting for a WURFL that was never asked for. It is
// `<script defer src="/wurfl-load.js">` on every tier now, and that file is
// idempotent, so putting an injection back here would be harmless and still
// wrong: there would be two answers to where WURFL comes from.
//
// It therefore arrives EARLIER than it used to -- from the head rather than
// after this bundle parses -- which is why the listener below has to cope
// with having already missed the event.

// ScientiaMobile is the Device Detection company. Let's include WURFL.
// formFactor is one of {Desktop, Tablet, Smartphone, Feature Phone, Smart-TV, Robot, Other non-Mobile, Other Mobile}
const myWURFLData = ref({})
// shall we add some quick and dirty form factor detection in the unlikely event WURFL.js
// is unavailable? mmmm.... decisions, decisions. This should capture well north of 95% traffic
myWURFLData.value.formFactor = ""
myWURFLData.value.completeDeviceName = "Undefined"

const userAgent = navigator.userAgent.toLowerCase()
myWURFLData.value.isMobilePhone = /(ipod|phone|(android(.*)mobile))/.test(userAgent)
myWURFLData.value.isTablet = /(ipad|tablet|(android(?!.*mobile))|kindle|playbook|silk)/.test(userAgent)
myWURFLData.value.isSmartTV = /(googletv|sonydtv|appletv|apple tv|smarttv|smart-tv|dlna|inettvbrowser|opera tv|sony bravia|sonycebrowser|hbbtv|large screen|netcast|philipstv|digital-tv|vizio|bravia|roku|vudu|sony bd\/|sony dtv\/|tivo-device|vizio, d|tvos|samsung-agent\/|samsunghas-agent\/|model\/hisense|virginmedia_stb|firetv|android tv; ap|amazon_tv; ap|android tv\/|fire tv\/)/.test(userAgent)
// the trailing empty alternative in /(ipod|ipad|iphone|)/ matched every string,
// so this was true on Android, on Windows, on everything, until WURFL.js
// loaded and overwrote it
myWURFLData.value.isiOS = /(ipod|ipad|iphone)/.test(userAgent)
myWURFLData.value.isAndroid = /android/.test(userAgent)

myWURFLData.value.isMobile = myWURFLData.value.isTablet || myWURFLData.value.isMobilePhone
myWURFLData.value.isDesktop = !myWURFLData.value.isMobile
if (myWURFLData.value.isMobilePhone) myWURFLData.value.formFactor = "Mobile Phone"
if (myWURFLData.value.isTablet) myWURFLData.value.formFactor = "Tablet"
if (myWURFLData.value.isDesktop) myWURFLData.value.formFactor = "Desktop"

/* Who gets to look at a thumbnail properly.
 *
 * A mouse hovers, so a desktop shows the preview on hover. A tablet cannot
 * hover, so it gets press-and-hold instead. A phone gets neither: the screen is
 * barely bigger than the preview, and press-and-hold there collides with
 * scrolling a long table.
 *
 * Called again below once WURFL.js has answered, because the regexes above are
 * a guess and this is exactly the kind of thing they guess wrong -- an iPad
 * reporting a desktop user agent would otherwise be given a gesture it cannot
 * perform.
 */
function applyPeekGestures() {
    configurePeek({
        hover: !!myWURFLData.value.isDesktop,
        hold: !!myWURFLData.value.isTablet,
    })
}
applyPeekGestures()

// we use Pinia for state. Too many interacions among components to have each manage its data
const uiStore = useUiElements()
const { t, te, locale } = useI18n()

// Ad unit ids, from the AdSense console (Ads > By ad unit). Empty means we have
// no unit for that position yet.
const adSlotIdTop = "6876721887"       // "Leaderboard" in the console
const adSlotIdBottom = "9131350135"    // "Ad 3", below the results
const adSlotIdColumn = "2946427182"    // "Ad 2", 300x600 in the second column
const adSlotIdRailRight = ""           // the outer rail gave this unit up; see
                                       // the note on the column slot below
const adSlotIdRailLeft = ""            // no unit yet; adRailLeft is off anyway

// The left rail is off by default. On a left-to-right page it sits between the
// reader and the drop zone, which is the one thing this page exists to do, and
// rails on both sides is the visual signature of an ad farm -- not what you
// want on a tool people hand personal photographs to. Set true to see it.
const adRailLeft = false

// Off now that the units are real. This drew a labelled dashed box wherever an
// id was missing, which was right while the placements were being judged and
// wrong the moment visitors could see it.
const showAdPlaceholders = false

// The side rails live behind a CSS media query, so on most screens their <ins>
// is display:none. adsbygoogle refuses a zero-width container ("No slot size
// for availableWidth=0") and marks the element done anyway, so the element is
// not rendered at all until the viewport is genuinely wide enough. Below
// 1652px, which is most visitors, the rail simply does not exist.
// A media query as a ref that only ever latches ON. adsbygoogle cannot
// re-scan an element it has already claimed, so a unit is never pulled back
// out of the DOM when a window narrows -- that would throw away a live ad.
function widthLatch(query) {
    const fits = ref(false)
    onMounted(() => {
        if (!window.matchMedia) return
        const mq = window.matchMedia(query)
        fits.value = mq.matches
        const widened = () => { if (mq.matches) fits.value = true }
        if (mq.addEventListener) mq.addEventListener("change", widened)
        else if (mq.addListener) mq.addListener(widened)
    })
    return fits
}

const RAIL_QUERY = "(min-width: 1652px)"
const COLUMN_QUERY = "(min-width: 768px)"   // Tailwind md, where two columns exist
const railFits = widthLatch(RAIL_QUERY)
const columnAdFits = widthLatch(COLUMN_QUERY)

// One push per unit that has not been claimed yet. push({}) takes no argument
// naming a slot -- it processes the next unclaimed <ins> in document order --
// so the count has to come from the DOM rather than from the list of ids.
// adsbygoogle stamps data-adsbygoogle-status on an element once it owns it,
// which is what makes "not yet claimed" answerable.
function fillPendingAdUnits() {
    const pending = document.querySelectorAll(
        "ins.adsbygoogle:not([data-adsbygoogle-status])").length
    for (let i = 0; i < pending; i++) {
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({})
        } catch (e) {
            // adsbygoogle.js blocked or not loaded; nothing to do
        }
    }
}

/* A unit that never answers reserves the same space as one that answers
 * "unfilled", and it is the commoner case: an ad blocker, a script that failed
 * to load, a consent decision that stopped it, or simply no network to
 * googlesyndication. AdSense labels its own empty results with
 * data-ad-status="unfilled" and CSS collapses those; nothing labels a unit
 * that was never claimed at all, so 600px stays reserved for something that is
 * never coming. This says it out loud after waiting.
 *
 * Ten seconds, against 3.6s measured for the slowest of the four units on
 * /pdf-to-jpg to answer -- generous on purpose, because being early here costs
 * a real impression. It is also self-correcting: a unit that fills afterwards
 * has its data-ad-status overwritten with "filled" by AdSense itself, and the
 * CSS rule stops matching, so a late ad reappears rather than staying hidden.
 */
const AD_ANSWER_GRACE_MS = 10000

// Two clocks, because there are two ways to end up with nothing and they do not
// deserve the same wait. If adsbygoogle.js itself never arrived -- blocked, or
// no route to googlesyndication -- there is nothing left to wait FOR, and the
// reflow should happen while the reader is still reading the first paragraph
// rather than ten seconds in. The script sets adsbygoogle.loaded when it runs,
// which is how everyone detects this.
const AD_SCRIPT_GRACE_MS = 5000

function collapseUnclaimedAdUnits() {
    for (const el of document.querySelectorAll("ins.adsbygoogle")) {
        if (!el.getAttribute("data-ad-status")) {
            el.setAttribute("data-ad-status", "unfilled")
        }
    }
}

onMounted(() => {
    window.addEventListener("load", fillPendingAdUnits)
    setTimeout(() => {
        if (!(window.adsbygoogle && window.adsbygoogle.loaded)) collapseUnclaimedAdUnits()
    }, AD_SCRIPT_GRACE_MS)
    setTimeout(collapseUnclaimedAdUnits, AD_ANSWER_GRACE_MS)
})

// A unit that appears after the fact needs its own push, once Vue has actually
// put it in the document.
watch([railFits, columnAdFits], async () => {
    await nextTick()
    fillPendingAdUnits()
})

// The sizes quoted in the results header. These used to be imageVersions[3]
// and [0] -- positions that were only correct for one particular format list,
// and that threw (blanking the whole page) the moment the list was shorter.
// Look them up by what they mean instead, and tolerate an empty list.
const phoneVariantSize = computed(() => {
    const vs = uiStore.imageVersions
    if (!vs.length) return 0
    // the smallest thing we produced: prefer a small AVIF, else any small one
    const small = vs.filter((v) => v.suffix === "_small")
    const pick = small.find((v) => v.format === "avif") || small[0] || vs[0]
    return pick ? pick.size : 0
})

const primaryVariantSize = computed(() => {
    const vs = uiStore.imageVersions
    if (!vs.length) return 0
    const pick = vs.find((v) => v.format === "jpeg" && v.suffix === "_large") || vs[0]
    return pick ? pick.size : 0
})

// imageVersions is derived from the enabled formats now, so build it once on load
uiStore.rebuildImageVersions()

// the "after" side of the comparison: prefer the large JPEG, which every browser
// can render, so the slider never shows an empty panel on an unsupported format
const compareOptimizedUrl = computed(() => {
    const v = uiStore.imageVersions.find((x) => x.format === "jpeg" && x.suffix === "_large")
             || uiStore.imageVersions[0]
    if (!v || !uiStore.workableImage.url) return ""
    return `${uiStore.workableImage.url}?imgeng=${uiStore.buildDirectives(v)}`
})

// The <picture> snippet, built from whichever formats are actually enabled.
// It used to be written out with imageVersions[0]..[7] hardcoded in the
// template, which would emit <source> tags for variants that were never
// fetched as soon as the format set became selectable.
//
// Browsers take the first <source> they understand, so these are ordered
// most-efficient first. JPEG is the <img> fallback because everything reads it;
// PNG is never a <source> -- it is larger than the alternatives, so offering it
// to a browser that could take AVIF would be the wrong way round.
const SOURCE_ORDER = ["jxl", "avif", "webp"]

// One <picture> element from one image's variant list.
//
// Pulled out of the single-image computed below so a batch of ten produces the
// same markup, element for element, as optimizing those ten one at a time. Two
// builders would have drifted apart on the first change to either.
function buildPictureElement(vs, altText) {
    if (!vs || !vs.length) return ""

    // Whatever someone typed has to be safe inside a double-quoted attribute.
    // A stray quote in the alt text used to end the attribute early and hand
    // the visitor markup that would not parse.
    const alt = String(altText === undefined || altText === null ? "" : altText)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")

    const pick = (fmt, suffix) => vs.find((v) => v.format === fmt && v.suffix === suffix && !v.failed)

    // The formats actually in hand, not the ones the store has enabled. For a
    // single image those are the same list. For a batch producing one file per
    // image they are not: the store may have three formats enabled while this
    // image only has the one that was asked for, and reading enabledFormats
    // there would pick a fallback that does not exist and emit an empty
    // <picture>. It also means a format whose transforms all failed drops out
    // by itself.
    const present = []
    for (const v of vs) {
        if (!v.failed && present.indexOf(v.format) < 0) {
            present.push(v.format)
        }
    }
    const dir = uiStore.subFolderWithSlash
    const sizes = `${uiStore.pct}vw`
    const zwsp = "​"

    /* The width descriptor, measured where possible and requested otherwise.
     *
     * Measured wins because ImageEngine does not upscale: ask for 1280 from an
     * 800px original and the file is 800px, and a srcset claiming 1280w makes
     * browsers pick the wrong one. But a format THIS browser cannot decode has
     * no measurement -- and the markup is for the visitor's own site, where
     * other browsers can decode it, so dropping the source would be worse than
     * describing it with the width we asked the CDN for.
     *
     * What it must never do is interpolate undefined. It did: the measurement
     * came off a DOM node nothing had assigned since the versions table was
     * rewritten, so every srcset this page produced read "undefinedw". */
    const widthOf = (v) => v.naturalWidth || v.width || 0
    const descriptor = (v) => {
        const w = widthOf(v)
        return w ? ` ${w}w` : ""
    }

    const srcset = (fmt) => {
        const big = pick(fmt, "_large")
        const small = pick(fmt, "_small")
        if (!big || !small) return null
        // A srcset may not mix entries with and without a descriptor, so if
        // either width is unknown both go bare and the browser treats them as
        // 1x candidates -- valid, if less useful, and never a false width.
        if (!widthOf(small) || !widthOf(big)) {
            return `${dir}${small.filename},${zwsp} ${dir}${big.filename}`
        }
        return `${dir}${small.filename}${descriptor(small)},${zwsp} ${dir}${big.filename}${descriptor(big)}`
    }

    // fallback: JPEG if we have one, otherwise whatever is left
    const fallbackFmt = present.includes("jpeg")
        ? "jpeg"
        : present[present.length - 1]

    const lines = ["<picture>"]
    for (const fmt of SOURCE_ORDER) {
        if (!present.includes(fmt)) continue
        // no point offering a source in the same format as the fallback
        if (fmt === fallbackFmt) continue
        const ss = srcset(fmt)
        if (ss) {
            lines.push(` <source type="image/${fmt}" sizes="${sizes}"`)
            lines.push(`    srcset="${ss}">`)
        }
    }

    const fb = pick(fallbackFmt, "_large")
    const fbSmall = pick(fallbackFmt, "_small")
    if (fb) {
        lines.push(` <img alt="${alt}" src="${dir}${fb.filename}"`)
        if (fbSmall) {
            lines.push(`    srcset="${srcset(fallbackFmt) || `${dir}${fb.filename}`}"`)
        }
        lines.push(`    sizes="${sizes}" loading="lazy" decoding="async">`)
    }
    lines.push("</picture>")
    return lines.join("\n")
}


const pictureSnippet = computed(() => buildPictureElement(uiStore.imageVersions, uiStore.altattr))


// Every image in the batch, one <picture> after another, each labelled so a
// developer can tell which is which when they paste the lot into a page. This
// is also what goes into HTML-picture-elements.txt in the zip.
const batchSnippets = computed(() => {
    // Asked for, and not on a phone: the single-image path hides its picture
    // panel on phones for the same reason, and a list of ten would be worse.
    if (!uiStore.pictureElements || myWURFLData.value.isMobilePhone) return ""
    const blocks = []
    for (const item of uiStore.batchItems) {
        if (item.status !== "done" || !item.versions.length) continue
        const el = buildPictureElement(item.versions, item.alt)
        if (el) {
            blocks.push(`<!-- ${item.origName} -->\n${el}`)
        }
    }
    return blocks.join("\n\n")
})

// --- option handlers -------------------------------------------------------
// Each one writes to the store and then re-runs the optimization, because every
// option is part of the ImageEngine URL rather than something applied locally.

function reoptimize() {
    /* A CLIP IS RE-CONVERTED, not re-optimised.
     *
     * The two video controls decide what the browser encodes, and by the time
     * there is anything to re-optimise the video has left the stage -- so
     * without this, changing frames-a-second after a conversion would do
     * nothing at all and look broken. Same fact as rebatchIfDone(): the half
     * that has to run again is the half those controls actually feed. */
    if (offersVideo && reconvertVideo()) return
    /* Same contract: the background is painted INTO the PNG, so it is
     * the one SVG setting a rewritten URL cannot deliver. Everything
     * else on this page -- width, quality, output format -- stays a
     * directive and falls through. */
    if (reconvertSvg()) return

    // NOTHING TO REDO BEFORE ANYTHING HAS BEEN DONE.
    //
    // Every control in the panel calls this, and it used to send the picture
    // the moment the signature changed -- whether or not the visitor had asked
    // for anything yet. On /darken-image the scrim is the first thing anybody
    // touches, so choosing a colour uploaded the photograph, and every release
    // of the slider uploaded it again, all before "Darken it" had been
    // pressed. The optimised result then appeared beside a button that had
    // never been clicked.
    //
    // Before the first run, a control changes the PREVIEW and nothing else.
    // Afterwards it means "and do it again", which is what it always meant for
    // a flip or a crop once there was something to redo.
    // one hook for both paths: a batch re-runs its transforms, a single image
    // rebuilds its six variants
    if (uiStore.batchMode) {
        // startBatch() re-uploads, so a flip or a crop is picked up there
        rebatchIfDone()
        return
    }

    // A single image: apply the change to the LOCAL file and stop there. The
    // stage draws from that file, so a crop or a flip shows immediately and
    // costs a canvas pass rather than an upload and a render.
    if (!uiStore.files.length) return

    // A scrim or watermark change needs no canvas pass -- CSS is already showing
    // it -- but it does make any downloaded result stale, and after a download
    // those marks are burned into the bytes on the stage. Left alone, editing
    // then would show the OLD marks baked in with no live ones over them.
    // Stripping them is what puts CSS back in charge.
    if (bakedDecorations.value) {
        queueTransform().catch((err) => alert(err.message || t('errors.transformFailed')))
        return
    }
    if (uiStore.showDownloadables || handedOver.value) {
        uiStore.showDownloadables = false
        handedOver.value = false
    }

    if (transformSignature() !== appliedTransform.value) {
        queueTransform().catch((err) => {
            // Never silent. This used to swallow the exception on the grounds
            // that applyTransforms reports its own failures -- it does not;
            // its caller does, and startUploadFiles was the only caller that
            // did. A flip that threw left the control switched on, the picture
            // unchanged, and nothing said about it.
            alert(err.message || t('errors.transformFailed'))
        })
    }
}


// Both copy buttons go through the same helper, which prefers the async
// Clipboard API. Selecting this readonly textarea and calling execCommand is
// what fails on iOS Safari.
function copyBatchSnippets() {
    return copyText(batchSnippets.value)
}


const { /* files, */ addFiles, removeFile, removeAllChildNodes,
        reconvertVideo, reconvertSvg, convertTrimmed, cancelTrim,
        bakeVideoCaption } = useFileList()
const { /* secureFileName, */ downloadAsZip, downloadBatchZip, downloadBatchPdf, downloadTiles, downloadSingleImage, shareOrDownload, formatBytes, copyPictureTagsToClipboard, copyText } = useSomeUtils()

// let's generalize 'http://localhost:8080/upload'
const baseUrl = window.location.protocol +"//"+ window.location.host
const { uploadFiles,downloadOptimizedFiles, fetchImageFromWeb } = createUploader(baseUrl)


// A flip or a region crop changes the FILE, not the directives, so it has to
// happen here rather than in buildDirectives(): ImageEngine cannot do either
// (flip_h answers 412; cr_ will not combine with a resize), so the canvas does
// it and the transformed bytes are what get uploaded.
//
// The original File is kept on the wrapper. Untick the flip and the next
// upload is the untouched file again, not a re-encode of a re-encode.
const appliedTransform = ref("")

function transformSignature() {
    // The overlay belongs here or moving the slider changes nothing: this
    // string is what decides whether the browser-side pass runs again.
    // Only what a canvas has to do. The scrim and the watermark are drawn over
    // the stage in CSS and burned in on the way to a download, so changing
    // either costs nothing and shows immediately; a crop or a flip cannot be
    // seen at all until the pixels move.
    return JSON.stringify([uiStore.flipH, uiStore.flipV, uiStore.crop])
}

// Leave the pipeline for the editor, carrying one image with you.
//
// The parameters come along -- quality, formats, width, the crop preset, the
// Putting the images back together as one document.
//
// Offered only where it can be done without re-encoding: a PDF takes JPEG data
// byte for byte, and nothing else the app produces goes in as it stands. See
// downloadBatchPdf() in compositions/utils.js.
//
// NOT offered when a PDF is what was dropped in, and this is the important
// one. A text PDF stores glyph references; a rasterised one stores pixels, so
// the assembled file is far bigger than the document it came from -- a 205 KB
// thirteen-page report measured 1.72 MB coming back, eight and a half times
// the size. Offering that round trip is not a feature, it is bad advice with a
// button on it. Somebody who wants the pages as pictures already has the zip.
/* The document the pages came out of, or null.
 *
 * When it is a document, its zip carries an index.html that shows the pages in
 * order -- see galleryPage() in compositions/utils.js. A folder of unrelated
 * photographs gets nothing extra: there is no order to put them back into, and
 * a page listing twenty holiday pictures is a file nobody asked for.
 */
/* WHAT THE ZIP'S index.html SHOULD BE, or null for no index at all.
 *
 * A document names itself -- the PDF's own filename is what somebody is
 * looking for -- and its pages are a STACK in reading order.
 *
 * A batch of photographs has no such name and no such order, which used to be
 * the argument for giving it no page at all. Too narrow: order is one job and
 * LOOKING is the other, and a folder of twenty optimised photographs is no
 * easier to look through than a folder of twenty pages. It gets a GRID
 * captioned with filenames instead -- see galleryPage() for why those two
 * differences are the whole of the variation.
 *
 * downloadBatchZip() still requires more than one entry, so a single picture
 * gets no index: a page over one image is noise in a folder that holds the
 * image.
 */
const documentGallery = computed(() => {
    const doc = uiStore.files.find((f) => isPdf(f.file))
    if (doc) return {title: (doc.file && doc.file.name) || "Document", kind: "document"}
    /* NOT in <picture> mode. There the zip holds every variant of every
     * picture -- six files for one photograph -- so a grid captioned with
     * filenames would show the same image six times and read as a fault. That
     * zip's companion is HTML-picture-elements.txt, which is the thing those
     * files are for. */
    if (uiStore.pictureElements) return null
    return {title: "Your images", kind: "set"}
})

const canAssemblePdf = computed(() =>
    !uiStore.pictureElements
    && uiStore.batchFormat === "jpeg"
    && !uiStore.files.some((f) => isPdf(f.file))
    && uiStore.batchItems.some((i) => i.status === "done"))

/* TWO FINISHED PICTURES, which is the smallest thing that is a SET.
 *
 * A "contact sheet" of one picture is that picture with a caption under it and
 * a border round it -- a worse copy of a file the visitor already has. The
 * name stops being true at the edge of its range, which is the `--11%` fault
 * in a different costume.
 *
 * `status === "done"` on both sides, like batchTotals(): counting rows that
 * are still uploading would put the button up before there is anything to
 * paint, and then paint a sheet missing half the set.
 */
const sheetPreview = ref(null)

/* One blob, one <a download>, revoked late.
 *
 * The same three lines downloadBatchZip() and downloadBatchPdf() use, and not
 * shared with them deliberately: those build what they hand over and this
 * hands over something built elsewhere, so folding them together would mean a
 * helper whose argument is sometimes a canvas and sometimes a set of rows.
 *
 * Ten seconds before revoking, because Safari has been observed to abandon a
 * download whose URL is revoked in the same tick as the click. */
function handOverBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.style.display = "none"
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10000)
}

const sheetReady = computed(() =>
    uiStore.batchItems.filter((i) => i.status === "done").length >= 2)

const assemblingPdf = ref(false)

/* One row of the versions table, taken on its own.
 *
 * A wrapper rather than an event fired from inside shareOrDownload(), which
 * the primary button also reaches -- instrumenting the shared helper would
 * report two downloads for one press of Download. */
function saveOneVersion(version) {
    track("download_clicked", {
        file_count: 1,
        output_format: version.format || "unknown",
        download_type: "single",
    })
    return shareOrDownload(version.blob || version.downloadUrl || version.url,
                           version.filename)
}

/* One row of a batch, taken on its own.
 *
 * This is the only download on the site that is a plain <a download> with no
 * JavaScript behind it, and it stays that way: the handler is added for the
 * count alone and deliberately returns nothing, so the browser's own
 * navigation is untouched. If the event throws -- it cannot, track() swallows
 * everything -- the file still saves. */
function savedOneRow() {
    track("download_clicked", {
        file_count: 1,
        output_format: uiStore.batchFormat || "unknown",
        download_type: "single",
    })
}

/* The zip of a finished batch. A wrapper only so the event fires once beside
 * the existing call rather than from inside the shared utility, which several
 * pages reach by different routes. */
function downloadBatchSet() {
    const done = uiStore.batchItems.filter((it) => it.status === "done")
    track("download_clicked", {
        file_count: done.length,
        output_format: uiStore.batchFormat || "unknown",
        download_type: "zip",
    })
    return downloadBatchZip(batchSnippets.value, {gallery: documentGallery.value})
}

async function assemblePdf() {
    if (assemblingPdf.value) return
    assemblingPdf.value = true
    try {
        /* ON THE SHEET PAGE, "one PDF" MEANS THE SHEET.
         *
         * Everywhere else it means one page per photograph, which is what a
         * folder of scanned pages wants. Here it produced nine pages of the
         * nine pictures the visitor had just arranged onto ONE -- the sheet
         * they were looking at appeared nowhere in it. Reported as exactly
         * that, and it is the right complaint: on a page whose subject is a
         * single composed picture, every export should be of that picture.
         *
         * buildPdf() does the whole job unchanged -- it makes a page per image
         * and sizes each to its own JPEG, so handing it ONE image is one page
         * holding the sheet at its own proportions. No PDF code for this. */
        if (offersSheet) {
            const made = await sheetPdf()
            track("download_clicked", {
                file_count: made ? 1 : 0,
                output_format: "pdf",
                download_type: "contact_sheet_pdf",
            })
            if (!made) {
                alert(t("sheet.failed"))
            }
            return
        }
        const result = await downloadBatchPdf()
        /* Reported after the assembly rather than on the press, because this
         * one can fail: the pages are re-read and written into a document
         * here, and an event fired on the click would count a download that
         * never happened. The zip and the single save cannot fail that way --
         * the bytes already exist -- so those fire beside the call. */
        track("download_clicked", {
            file_count: (result && result.pages) || 0,
            output_format: "pdf",
            download_type: "pdf",
        })
        // A page that could not be read is worth saying out loud: the document
        // is short by one and nothing else would show that.
        if (result && result.skipped) {
            alert(t('errors.pdfSomeSkipped', {n: result.skipped}))
            track("optimization_error", {
                error_type: "pdf_page_skipped",
                input_format: batchInputFormat,
                file_count: result.skipped,
            })
        }
    } catch (err) {
        track("optimization_error", {
            error_type: "pdf_assemble_failed",
            input_format: batchInputFormat,
        })
        alert(t('errors.pdfAssembleFailed'))
    } finally {
        assemblingPdf.value = false
    }
}

/* Take the reader to the combined picture, IF THEY ARE NOT ALREADY LOOKING
 * AT IT.
 *
 * Not scrolling at all was the previous answer, and it was right for the
 * wrong reason: on a wide screen the preview sits at the top of the second
 * column, so the batch-results scroll went PAST it to a table of the pieces
 * it was made from. Removing the scroll fixed that and broke the phone --
 * reported from Android, where the columns stack and "Your sheet" is below
 * the drop zone, the button and six thumbnails. Nothing moved, and the thing
 * the reader had just waited for was off the bottom of the screen.
 *
 * So it is the same rule the clip trimmer already uses, and for the same
 * reason: scroll only when what appeared does not already START near the top
 * (r.top < 0 || r.top > h * 0.4). A desktop stays put because the preview is
 * at 394px of an 1100px window; a phone moves because it is a screen and a
 * half down. One rule, both shapes, no check for which device it is.
 *
 * Retried like scrollToDownloadables(), because #sheet-preview is v-if'd on
 * two rows having finished and may not exist yet at the moment this is
 * called.
 */
function goToSheet(tries = 40) {
    const el = document.getElementById("sheet-preview")
    if (el && el.offsetParent !== null) {
        const r = el.getBoundingClientRect()
        const h = window.innerHeight || 0
        if (r.top < 0 || r.top > h * 0.4) {
            el.scrollIntoView({behavior: "smooth", block: "start"})
        }
        return
    }
    if (tries > 0) {
        setTimeout(() => goToSheet(tries - 1), 100)
    }
}

// The results table sits below the fold on every screen, and further below it
// after a PDF, because the table is as long as the document. Scrolled rather
// than jumped to: setting location.hash would push a history entry, and going
// back is now how you return to a list.
function goToBatchResults() {
    setTimeout(() => {
        const el = document.getElementById("batch-results")
        if (el) el.scrollIntoView({behavior: "smooth", block: "start"})
    }, 300)
}

/* THE TWO HALVES OF A CAPTION ARE FAR APART ON A PHONE.
 *
 * The rectangle sits on the picture and the field that fills it is below the
 * stage, the button and the whole options panel -- so tapping Area 1 to write
 * in it does nothing visible, because the box you were meant to type into is
 * off the bottom of the screen.
 *
 * A TAP, not a drag. CropSurface only emits this when the pointer went down
 * and up without travelling, so picking a rectangle up to move it does not
 * scroll the page out from under the gesture.
 *
 * focus() as well as scroll: the point of the tap was to write something, and
 * on a phone focusing is what opens the keyboard.
 */
/* WHICH SLOT IS BEING TYPED INTO, ON THE PICTURE.
 *
 * Reported from an iPhone 16 Pro, watched over her shoulder. Tapping a caption
 * area scrolled the page down to a text field below the stage, the keyboard
 * came up under that, and the PICTURE went off the top of the screen -- so she
 * was writing a caption for something she could no longer see. The live
 * preview is the whole point of this page and the interaction was hiding it.
 *
 * Every other app she uses puts the caret on the picture. So now this does.
 *
 * The fields in MemeControl stay, and are not a fallback: on a laptop they are
 * the better control -- a colour, a size and a face sit beside them -- and they
 * are what a keyboard or a screen reader reaches without a pointer at all. Two
 * ways in, one store field behind both.
 */
/* Whether the ready-made pictures are showing while one is already staged.
 *
 * False by default and closed again on every pick, so the shelf is out of the
 * way in the state it spends most of its life in -- a picture chosen and
 * captions being written -- and one click away in the state that was
 * reported: having picked one ready-made picture and wanting another. */
const libraryOpen = ref(false)

const captionEditing = ref(null)

function editCaption(slot) {
    /* NOTHING SCROLLS. That is the whole of it, and it took a second report
     * from a phone to get here.
     *
     * The old interaction scrolled DOWN to a text field below the stage, which
     * took the picture off the top of the screen -- the complaint that made
     * this editor exist. Replacing it, I kept a scroll of my own: the stage to
     * the top, on the reasoning that the keyboard was about to take the lower
     * half. That was the same mistake wearing a different hat, and it was
     * reported the same way -- "the scroll still triggers, very confusing".
     *
     * A tap on a caption is a tap on something the reader is ALREADY LOOKING
     * AT. There is nothing to bring into view, and moving the page under a
     * thumb that has just landed is the confusion, whichever direction it
     * goes. The editor is focused where it stands; see the preventScroll in
     * WatermarkLayer, which stops the browser making the same jump on our
     * behalf.
     *
     * WHAT IS NOT SOLVED HERE: a caption low on the screen can still end up
     * behind the soft keyboard. That wants a measured nudge off
     * window.visualViewport -- move by exactly the overlap, not to the top --
     * and it is a separate change that needs a real device to tune. Jumping
     * is not a substitute for it.
     */
    captionEditing.value = slot
}
/* DONE TYPING, which is a commit and not just a blur.
 *
 * The panel's field has always had two halves: @input writes the store so the
 * preview follows every keystroke, and @change calls reoptimize() when the
 * edit is finished. The second half is not decoration -- reoptimize() is what
 * notices that the bytes on the stage already carry BAKED words and strips
 * them, putting CSS back in charge.
 *
 * Without it, editing after a Save writes the store and changes nothing you
 * can see: bakedDecorations suppresses the live stamps, so the stage goes on
 * showing the caption that was burnt in. That is the state a reader is in
 * every time they save once and then think better of the wording -- and the
 * editor on the picture had only the first half until this was added.
 *
 * On finish rather than on every keystroke, which is the same split the
 * sliders use: typing is free, re-rendering is not.
 */
function finishCaption() {
    captionEditing.value = null
    reoptimize()
}

/* Typed on the picture, straight into the same store field the panel writes.
 *
 * ONE STORE FIELD BEHIND BOTH WAYS IN, which is what makes the panel below
 * mirror what is being typed on the image and -- the other direction, and the
 * one that was asked for -- makes an edit in the panel change the picture.
 * There is no second copy of the text anywhere that could fall out of step.
 *
 * settled:false, the way a slider's @input is: every keystroke re-lays out the
 * caption in CSS and costs nothing else. Asking the CDN for a render per
 * character is what the meme rectangles' `settled` split exists to prevent,
 * and a caption is baked at Save in any case.
 *
 * THIS WAS BOUND IN THE TEMPLATE AND NOT DEFINED, for a while: removing the
 * dead focusCaptionField() walked backwards to the nearest preceding comment
 * and took this with it. Nothing failed loudly -- Vue binds a handler that is
 * undefined without a word -- so the caption drew on the picture, the store
 * never heard about it, and the size therefore never shrank and the panel
 * never filled in. One missing function, three symptoms.
 */
function onCaptionText({slot, text}) {
    /* By slot, for the reason setCaptionBox() carries: two named branches
     * wrote NOWHERE for areas 3 and 4. The editor on the picture keeps its own
     * text while it is open, so the words looked fine until it closed and then
     * were simply gone -- reported as "what I write disappears as soon as the
     * box loses focus", which is exactly what a write to nothing looks like. */
    if (!MEME_SLOTS.includes(slot)) return
    uiStore[memeStoreKeys(slot).text] = text
}


/* WHERE A CONVERTED CLIP PUTS YOU.
 *
 * On a phone the drop zone and its fine print fill the screen, so the GIF
 * that has just been made -- and the button that saves it -- land below the
 * fold. The conversion starts by itself once the trimmer is accepted, so
 * nothing about finishing it is a gesture that would scroll the page.
 *
 * Retried like scrollToDownloadables(), and for the same reason: the stage is
 * v-show'd, and scrollIntoView on a display:none element does nothing at all.
 */
function scrollToId(id, tries = 30) {
    const el = document.getElementById(id)
    if (el && el.offsetParent !== null) {
        /* Only if it does not already START near the top of the screen.
         *
         * On a laptop the trimmer and the stage are in the right-hand column
         * with their tops well up the page, and moving it under somebody who
         * can see the thing perfectly well is the surprise rather than the
         * help.
         *
         * Measured on where it BEGINS, not on how much of it shows. The first
         * rule asked for a quarter of the element to be visible, and on a
         * 740px handset the trimmer began 450px down with 290px peeking in --
         * which satisfied "a quarter" while the whole top half of the screen
         * was still the page intro, and is exactly the state that was
         * reported. Anything starting below two fifths of the viewport gets
         * scrolled to.
         */
        const r = el.getBoundingClientRect()
        const h = window.innerHeight || document.documentElement.clientHeight
        if (r.top < 0 || r.top > h * 0.4) {
            el.scrollIntoView({behavior: "smooth", block: "start"})
        }
        return
    }
    if (tries > 0) setTimeout(() => scrollToId(id, tries - 1), 100)
}

// Scrolled, not navigated.
//
// This was `window.location.href = "#downloadables"`. A fragment navigation is
// not just a scroll: it appends a session history entry and TRAVERSES to it,
// and traversing fires popstate -- the same event the back button fires. The
// handler below could not tell the two apart, so it saw the app's own scroll
// as "the reader pressed back" and restored the stashed batch.
//
// That is why "open on its own" worked on a desktop and snapped straight back
// on a phone: the jump only ran under isMobile. It also left a junk history
// entry behind on every mobile run, so back did nothing but drop a hash.
/* Take the reader to the table of versions.
 *
 * It sits below the settings panel, which is a screen or more further down --
 * so handing over a zip of four files put three of them somewhere the visitor
 * had no reason to look, and no reason to believe existed.
 *
 * It waits for the table rather than scrolling to it: #downloadables is
 * v-show'd on the versions being ready, and scrollIntoView on a display: none
 * element scrolls nowhere at all.
 */
function scrollToDownloadables(tries = 40) {
    const el = document.getElementById("downloadables")
    if (el && el.offsetParent !== null) {
        el.scrollIntoView({behavior: "smooth", block: "start"})
        return
    }
    if (tries > 0) setTimeout(() => scrollToDownloadables(tries - 1), 100)
}

// Take the reader to the stage. On a phone the columns stack and the stage
// begins below the fold, so a picture can be staged with nothing on screen to
// show for it.
function goToSingleStage() {
    setTimeout(() => {
        const el = document.getElementById("single-stage")
        if (el) el.scrollIntoView({behavior: "smooth", block: "start"})
    }, 120)
}

// The optimized result once it exists, the original in the meantime, so a row
// has a picture in it from the moment it appears rather than popping one in
// when the transform lands. Both are local object URLs; neither costs a
// request.
function thumbFor(item) {
    if (item.blob) return item.blob
    const f = uiStore.files.find((x) => x.id === item.id)
    return f ? f.url : ""
}

/* "What can you use it for?" -- the card in the second column.
 *
 * content-pages.json still carries a translation for every language in
 * SUPPORTED, so the path CAN be derived -- but a translation existing in the
 * registry is no longer the same as a page existing on disk. Only the languages
 * in window.OIOLang.published get one built, so that is what decides, and
 * everything else falls back to the hand-written English page rather than
 * linking somewhere that 404s.
 *
 * The same two lines live in AppFooter.vue; a shared helper for one expression
 * would be indirection, and the same test covers both.
 */
const whatForHref = computed(() => {
    const shipped = (typeof window !== "undefined" && window.OIOLang
                     && window.OIOLang.published) || ["en"]
    return shipped.includes(locale.value) && locale.value !== "en"
        ? `/${locale.value}/what-is-it-for.html`
        : "/what-is-it-for.html"
})

// The task this page was served as, or null on the homepage. The page already
// carries its own masthead, heading, tutorial and footer as real HTML, so the
// application leaves its own chrome out rather than printing a second set.
const task = activeTask()

// What the application calls things while it is standing in for a task. The
// homepage wording is the fallback, so a task that leaves a string out still
// reads properly rather than showing a gap.
// The registry's words win where a task supplies them; the catalogue is the
// fallback. That fallback has to be the LOCALISED string, or a Spanish visitor
// on the homepage gets an English dropzone.
// A PDF page says "choose a PDF" on a phone. The desktop headline already came
// from the registry (words.dropzone); the mobile one never did, so every task
// page told people on a phone to choose images.

// A stock photograph demonstrates "compress this". On a page whose subject is
// a PNG, a WebP or a PDF it demonstrates nothing, so the gallery goes. The
// judgement now lives with the declaration rather than here.
const samplesTeachSomething = samplesAreUseful()

// The heading stays "How it works" -- the static tutorial below already says
// "How to turn a PDF into images", and repeating that here would put the same
// sentence on the page twice for no gain. Only the steps change.
/* THE SAME THREE STEPS WERE ON THE PAGE TWICE.
 *
 * A task page is generated with its steps in the static prose -- that is what
 * a crawler reads and what an AdSense reviewer reads, and it is the whole
 * reason make_task_pages.py emits them. The application then rendered the
 * SAME three, read out of window.OIO_TASK, a screenful higher up. So
 * /png-to-jpg told a reader "Drop in the PNGs / They come back as JPEGs /
 * Take one, or the whole set" twice, word for word, with nothing between the
 * copies but the drop zone.
 *
 * Reported as part of "the pages are a bit overwhelming -- a wall of text",
 * and this was the most redundant thing in it.
 *
 * THE APPLICATION'S COPY IS THE ONE THAT GOES, and the asymmetry decides it:
 * the static prose is in the HTML whether or not JavaScript runs, and the
 * app's version is not. Dropping the static one would have been the cheaper
 * edit and would have cost the page its indexable description of itself.
 *
 * The home page keeps its steps, because there taskSteps() is null and the
 * catalogue's generic three are the only ones anywhere. */
const hasOwnSteps = computed(() => !!taskSteps(locale.value))

const howSteps = computed(() => taskSteps(locale.value) || [
    {title: t('how.s1title'), body: t('how.s1body', {max: maxBatchFiles})},
    {title: t('how.s2title'), body: t('how.s2body')},
    {title: t('how.s3title'), body: t('how.s3body')},
])

/* Whether the page is still set up the way it arrived.
 *
 * "Already set up for this" is an orientation note: it tells somebody landing
 * on /pdf-to-jpg what the page will do without being asked. It stops being
 * true the moment they change something -- choosing PNG left the card still
 * announcing "Output: one JPEG per page", which is the same fault as the rows
 * that kept saying .jpg after the format changed: the interface asserting
 * something the settings have since contradicted.
 *
 * Compared against the REGISTRY rather than a snapshot taken at mount, because
 * addFiles() calls $reset() and $reset() rebuilds from state() -- which
 * reapplies the task config. A snapshot would have to survive that; the
 * registry simply is the answer.
 *
 * `formats` is the one key that is not a store field of the same name: the
 * registry names formats, the store keeps enabledFormats. Anything else the
 * store does not have is skipped rather than guessed at, so a new ALLOWED key
 * cannot make this quietly always-false.
 */
const presetIntact = computed(() => {
    const task = activeTask()
    const cfg = (task && task.config) || null
    if (!cfg) return true
    return Object.keys(cfg).every((key) => {
        if (key === "formats") {
            const want = Array.isArray(cfg.formats) ? cfg.formats : []
            const have = uiStore.enabledFormats || []
            return want.length === have.length && want.every((f) => have.includes(f))
        }
        if (!(key in uiStore.$state)) {
            return true
        }
        return uiStore[key] === cfg[key]
    })
})

const words = computed(() => taskWords({
    // dropzone.headline is not voiced -- it is one string, and a page that
    // takes a clip must not tell somebody to drop an image on it.
    dropzone: t(offersVideo ? "dropzone.headlineVideo" : "dropzone.headline"),
    cta: "",
    ctaFormat: "",
    configured: "",
}, locale.value))

// The request in flight, so the overlay's cancel has something to abort.
let pdfRequest = null
let pdfTicker = null

/* Switch to the phase that cannot be measured.
 *
 * The estimate from here on is the page count times the measured per-page
 * cost, counted down by a ticker rather than by anything the server says --
 * because the server says nothing until it is finished. When the estimate runs
 * out it is dropped, not stretched: the overlay then shows "still working",
 * which is true, instead of a countdown frozen at one second, which reads as
 * broken. */
/* Watch the server's own count of the work.
 *
 * The upload has always had a real bar -- XHR reports bytes sent. Rendering had
 * nothing to report, so a forty-page scan sat under a spinner and a sentence
 * promising a second or two. The work IS countable, twice over: the server
 * rasterises page by page and then stores page by page, and now says so.
 *
 * Polled rather than streamed down the POST, which leaves /upload-pdf's status
 * codes and JSON alone and does not depend on the reverse proxy declining to
 * buffer a response.
 *
 * Every failure here is silent on purpose. This is the thing that describes the
 * work, not the thing that does it: if the poll 404s, or the network drops it,
 * or the answer is nonsense, the document still arrives and the modal falls
 * back to the estimate it had before. */
let pdfWatcher = null

function stopWatching() {
    if (pdfWatcher) clearInterval(pdfWatcher)
    pdfWatcher = null
}

function watchPdfJob(job) {
    stopWatching()
    // Twice a second: fast enough that a page finishing is visible, slow enough
    // that a fifty-page document is a hundred requests rather than thousands.
    pdfWatcher = setInterval(async () => {
        try {
            const res = await fetch(`${baseUrl}/pdf-progress/${encodeURIComponent(job)}`,
                                    {cache: "no-store"})
            if (!res.ok) return
            const p = await res.json()
            if (!p || !p.total) return
            uiStore.pdfDone = p.done || 0
            uiStore.pdfTotal = p.total
            uiStore.pdfStage = p.phase || ""
            // The count is better than the guess: it came from the document
            // rather than from its byte size.
            if (p.total !== uiStore.pdfPages) uiStore.pdfPages = p.total
        } catch (err) { /* the document does not depend on being described */ }
    }, 500)
}

function beginRendering() {
    if (uiStore.pdfPhase === "rendering") return
    uiStore.pdfPhase = "rendering"
    uiStore.pdfPercent = 100

    const budget = renderEstimateMs(uiStore.pdfPages)
    uiStore.pdfRemainingMs = budget
    if (pdfTicker) clearInterval(pdfTicker)
    if (!budget) return

    const endsAt = Date.now() + budget
    pdfTicker = setInterval(() => {
        const left = endsAt - Date.now()
        uiStore.pdfRemainingMs = left > 0 ? left : 0
        if (left <= 0) { clearInterval(pdfTicker); pdfTicker = null }
    }, 200)
}

function stopPdf() {
    if (pdfTicker) { clearInterval(pdfTicker); pdfTicker = null }
    stopWatching()
    pdfRequest = null
    uiStore.pdfPhase = null
    uiStore.pdfPercent = 0
    uiStore.pdfRemainingMs = 0
    uiStore.pdfPages = 0
    uiStore.pdfDone = 0
    uiStore.pdfTotal = 0
    uiStore.pdfStage = ""
}

/* Cancelling during the upload really stops the work. Cancelling during
 * rendering stops the waiting -- the server finishes either way and the sweep
 * collects the pages. Both are better than a dialog with no way out. */
function cancelPdf() {
    if (pdfRequest) { try { pdfRequest.abort() } catch (err) { /* already done */ } }
    stopPdf()
    uiStore.batchRunning = false
}

const cropperOpen = ref(false)
const cropSurface = ref(null)

// Which half of the stage is showing. Edit until there is something to
/* Edit, and it stays Edit.
 *
 * There used to be a watcher here that switched to the comparison the moment
 * the versions arrived, on the reasoning that you had asked for them by
 * pressing a button and here they were. A single image now goes through on
 * arrival, so the versions always arrive without anyone asking -- and the
 * switch fired every time, taking the visitor off the editing surface before
 * they had done anything. The comparison is a tab you choose, not a place you
 * get sent.
 */
const stageTab = ref("edit")

function pickTab(tab) {
    if (tab === "compare" && !uiStore.showDownloadables) return
    stageTab.value = tab
}

function toggleFlip(key) {
    uiStore[key] = !uiStore[key]
    reoptimize()
}

/* A quarter turn from where the picture is now.
 *
 * STEPPED, not set. The panel's four buttons are absolute -- 0, 90, 180, 270 --
 * which is the right shape for a list of settings and the wrong one beside the
 * picture: somebody straightening a photograph is pressing "again", not
 * working out which multiple of ninety they are on.
 *
 * Kept inside 0..359 so it never becomes r_450, which the CDN has no reason to
 * accept, and so the panel's buttons still agree with it: after four presses
 * the 0 button is lit again rather than nothing being lit at all.
 */
function turnBy(deg) {
    uiStore.rotate = (((uiStore.rotate || 0) + deg) % 360 + 360) % 360
    reoptimize()
}

// One button for "put it back", rather than making somebody remember which
// three things they changed.
function resetTransforms() {
    uiStore.flipH = false
    uiStore.flipV = false
    uiStore.crop = null
    uiStore.rotate = 0
    uiStore.overlayOpacity = 0
    uiStore.watermarkText = ""
    // The captions' own rectangles go with everything else. The WORDS stay:
    // Reset is beside the crop and flip tools and undoes what was done to the
    // picture, and throwing away a sentence somebody typed is not that.
    for (const slot of MEME_SLOTS) uiStore[memeStoreKeys(slot).box] = null
    reoptimize()
}

// Always the file the visitor picked. Cropping a crop compounds the
// re-encode and, worse, makes the second region mean something different
// from what the rulers on screen said.
/* The "before" half of the comparison.
 *
 * originalUrl() is the file as UPLOADED, and on a page with a browser-side
 * transform that file has already been darkened, flipped or cropped -- so the
 * slider showed a darkened picture against a darkened-and-compressed one,
 * which is a comparison of the compression alone and not what anybody came to
 * see. When the browser changed the picture before sending it, the honest
 * "before" is the file the visitor actually chose, which is still on the
 * wrapper as originalFile with its own object URL beside it.
 */
const localOriginal = computed(() => {
    const f = uiStore.files[0]
    return (f && f.originalFile && f.originalFile !== f.file) ? f : null
})
const beforeUrl = computed(() =>
    localOriginal.value ? localOriginal.value.url : uiStore.originalUrl())
const beforeSize = computed(() =>
    localOriginal.value ? localOriginal.value.originalFile.size : uiStore.workableImage.size)

/* What the stage shows when nothing is being dragged: the current file, which
 * carries whatever crop, flip and rotation have been applied. */
const stageDisplay = computed(() => {
    const f = uiStore.files[0]
    return f ? f.file : null
})

const cropSource = computed(() => {
    const f = uiStore.files[0]
    return f ? (f.originalFile || f.file) : null
})

function openCropper() {
    if (!uiStore.files.length) {
        alert(t('crop.needImage'))
        return
    }
    // A phone column is too narrow to drag a rectangle in, so there the same
    // surface takes the whole screen instead.
    if (myWURFLData.value.isMobilePhone) {
        cropperOpen.value = true
        return
    }
    pickTab("edit")
    if (cropSurface.value) cropSurface.value.openCrop()
}

function applyCrop(region) {
    cropperOpen.value = false
    uiStore.crop = region
    reoptimize()
    // Choosing a region is a decision about the OUTPUT, so take them to it.
    // On a phone the cropper is a full-screen modal: closing it leaves you
    // wherever you were before it opened, which is the settings panel, with
    // the thing you just changed off the bottom of the screen.
    nextTick(() => scrollToDownloadables())
}

/* @param decorate  Whether to burn the scrim and the watermark into the bytes.
 *
 * The stage says no. Both are things CSS draws for nothing and a canvas draws
 * by re-encoding the whole photograph -- so baking them made every touch of the
 * slider, and every KEYSTROKE of the watermark, a full pass over a 15 megapixel
 * image, with the stage showing the picture as it was until the pass landed a
 * second or two later. The drop zone had it right all along: preview it, do not
 * compute it. WatermarkLayer.vue is what both surfaces draw with.
 *
 * Download says yes, because the file has to actually be darker and actually be
 * signed. What is never optional is crop and flip: nothing can fake those.
 */
async function applyTransforms(wrappers, {decorate = true} = {}) {
    /* A CANVAS MAY NOT TOUCH AN ANIMATION. NOT ONE OF THESE, EVER.
     *
     * A canvas holds ONE FRAME, so anything baked here -- a crop, a flip, a
     * scrim, a watermark, a caption -- turns an animated GIF into a still.
     * The crop was withheld first, for that reason and because it costs a
     * whole re-conversion; then a caption arrived on /mp4-to-gif and only the
     * crop was guarded.
     *
     * WHAT THAT COST. bakeVideoCaption() runs the clip through again with the
     * words in it, and returns false when the bytes ALREADY carry them -- so
     * pressing Save a second time fell straight through to here with
     * decorate=true and a caption set, put a canvas over a 12 MB animation,
     * and failed with "That image could not be flipped or cropped". Reported
     * from a phone; on a desktop it merely flattened it.
     *
     * So the rule is the whole class, not the one case that bit. Null rather
     * than a flag, so planTransform() reports changed=false and
     * transformImage() returns the ORIGINAL File -- the invariant that keeps a
     * no-op from costing a re-encode.
     *
     * The words still reach an animation, by the only route that can carry
     * them: the converter draws them onto every frame. See bakeVideoCaption().
     */
    /* `|| offersVideo`, because animatedGif ARRIVES LATE.
     *
     * It is set after intake, once lib/gifFrames.js has walked the bytes --
     * so a Save that lands in that window saw false, let the canvas have the
     * file, and flattened it. Measured as a delivered GIF with the crop
     * applied and ONE frame in it: `[{"cr":true,"n":1}]`, intermittently.
     *
     * On /mp4-to-gif there is nothing to wait for: everything staged there is
     * an animation this page made. A static fact beats a flag in flight. */
    const animation = !!uiStore.animatedGif || offersVideo
    const decorating = decorate && !animation
    const opts = {crop: animation ? null : uiStore.crop,
                  flipH: animation ? false : uiStore.flipH,
                  flipV: animation ? false : uiStore.flipV,
                  overlay: decorating
                      ? {color: uiStore.overlayColor, opacity: uiStore.overlayOpacity}
                      : null,
                  // One or the other. Both at once would need two sets of
                  // placement controls to solve a problem nobody has reported.
                  watermark: (decorating && logo.mode !== "logo")
                      ? {text: uiStore.watermarkText, position: uiStore.watermarkPosition,
                         size: uiStore.watermarkSize, opacity: uiStore.watermarkOpacity,
                         color: uiStore.watermarkColor, font: uiStore.watermarkFont}
                      : null,
                  logo: (decorating && logo.mode === "logo")
                      ? {image: logo.image, position: uiStore.watermarkPosition,
                         size: logo.size, opacity: uiStore.watermarkOpacity}
                      : null,
                  // Captions ride on the same bargain as the scrim and the
                  // watermark: previewed in CSS for nothing, burned in only on
                  // the way to a download. Two empty fields are not a caption,
                  // so a visitor who types nothing still gets their own bytes.
                  // A CLOSED AREA IS NOT A CAPTION. The words are kept -- a
                  // second area added again brings them back -- but nothing
                  // that is not on the picture may be in the bytes.
                  meme: decorating
                      ? memePayload(uiStore, uiStore.memeZones)
                      : null}
    for (const w of wrappers) {
        if (!w.originalFile) {
            w.originalFile = w.file
        }
        // Mutated in place on purpose: uploadFile() writes progress back to
        // these same wrappers, so handing it copies would lose the status.
        w.file = await transformImage(w.originalFile, opts)
    }
    // The stage draws whatever the bytes do not, so it has to be told what they
    // now do.
    bakedFlip.value = {h: uiStore.flipH, v: uiStore.flipV}
    bakedDecorations.value = decorate
    appliedTransform.value = transformSignature()
    return wrappers
}

// must match SHARE_CACHE in service-worker.js
// How many CDN transforms to have in flight during a batch. Browsers cap
// requests per host at around six anyway; matching that keeps the queue honest
// instead of letting twenty requests sit in it.
const BATCH_CONCURRENCY = 6

// Up to this many previews are shown as cards; beyond it they stack. Six is
// where a wrapping row stops fitting beside the drop zone's own text on a
// phone, measured against the narrowest layout rather than chosen for looks.
const STACK_AFTER = 6

// The faces of the stack. Five, because the sixth would be almost entirely
// hidden by the count that follows it and would only cost a decode.
/* HOW MANY OF THE SET THE DROP ZONE SHOWS.
 *
 * Was 5, from when these were overlapping 64px thumbnails and a sixth would
 * not have fitted the row. They are a wrapping grid now, so the question is
 * how many ROWS are worth spending rather than how many files -- and twelve
 * is three rows of four in a narrow column and two of six in a wide one.
 *
 * Not all of them: twenty squares turn the drop zone into a gallery, and
 * what it is for is confirming you picked the right folder. The "+N" tile
 * says the rest are there. */
const PREVIEW_TILES = 12
const stackedPreviews = computed(() => uiStore.files.slice(0, PREVIEW_TILES))

// so the copy quotes the cap rather than repeating it
// THIS page's cap, not the application's. Four strings interpolate it -- the
// browse line, the mobile body, the accepts caption and the first "How it
// works" step -- and on a page that takes one document all four were quoting
// twenty.
const maxBatchFiles = batchCapacity()

// What the picker offers, from the same table the intake filter uses.
const accepted = inputRules()

/* The camera button, and when it is worth its height.
 *
 * Not on a page that wants a document -- a phone camera cannot produce a PDF,
 * so the button was an offer that ended in a refusal. Not on a phone that
 * already has a picture either, for the room it takes. A tablet keeps it in
 * both states.
 */
/* Which crop affordance the settings panel should carry.
 *
 * The stage panel -- Edit tab, crop surface, flip tools -- is rendered only
 * when the image is ready AND we are not on a phone. So a phone has no stage
 * and the panel's button is the only route to the cropper; a desktop gets the
 * cropper on the stage and does not need a second trigger sitting in a list of
 * settings, opening a surface in the other panel.
 */
/* The single image has come back and there is something to hand over. Not for
 * a batch, which has its own table and its own zip, and not for a task that
 * edits in the browser, whose button already produces and delivers. */
/* The shape of the comparison, from the picture rather than from a layer.
 * Falls back to 3:2 while the dimensions are still unknown, which is closer to
 * a photograph than the zero height it had before. */
const compareShape = computed(() => {
    const w = uiStore.workableImage.width
    const h = uiStore.workableImage.height
    return {aspectRatio: (w && h) ? `${w} / ${h}` : "3 / 2"}
})

/* Something is happening and the visitor did not have to ask for it, so the
 * button that asks for it has nothing to offer. It was reading "OPTIMIZE YOUR
 * IMAGE" over an image already being optimised. */
const workInFlight = computed(() =>
    uiStore.isUploading || uiStore.optimizationInProgress)

/* PRESSING SAVE AND GETTING NOTHING FOR SEVERAL SECONDS.
 *
 * Reported as "the system may need several seconds before the download is
 * signaled by the device". makeAndDeliver() is the whole of that wait --
 * upload, then waitForVersions(), then zipping and handing over -- and
 * NONE of it sets isUploading or optimizationInProgress. Measured by driving
 * /resize-image: the delivered table appeared, and across 600 polls
 * workInFlight was never once true, so the page said nothing at all.
 *
 * A separate flag rather than widening workInFlight, deliberately. The
 * primary button is v-show'n on `!workInFlight` and several browser suites
 * read that absence as "work is in flight" -- video_to_gif_test.sh waits for
 * the button to COME BACK before pressing Save twice. Widening it would
 * change the button's visibility on every page that delivers, which is four
 * suites broken to fix one gap. The button stays put and is DISABLED while
 * this is true, which stops a second press without moving anything.
 */
/* Set by server.py's /_share-target fallback, which is reached only when the
 * service worker did not answer the share. Read once from the query string:
 * an installed PWA has no address bar, so this is the only way the visitor
 * can be told why the drop zone is empty. */
const shareMissed = ref(false)
/* What the share actually carried, when it carried something unusable.
 * Not translated: it is the file's own name and MIME type, which is data. It
 * is here because a share that vanishes with no trace is undebuggable from a
 * phone -- there is no console, and an installed PWA has no address bar. */
const shareMissedDetail = ref("")

const delivering = ref(false)

/* What the spinner watches: either kind of waiting. */
const saving = computed(() => workInFlight.value || delivering.value)

/* Where the button that does the thing should sit.
 *
 * Under the stage, next to the picture it acts on, whenever there is a stage.
 * There is not always one: it is for a single image on something wider than a
 * phone, so a batch, a PDF and every phone still need the button in the left
 * column, under the drop zone, which is where it has always been. Both places
 * render the same words from the same computeds -- only the position differs.
 */
/* The clip's own numbers, for the card that appears after converting.
 *
 * Formatted here rather than in the template because the trimmer formats the
 * same two values the same way, and a second spelling of "1.3x" is how two
 * parts of one page come to disagree about what happened. */
const spedClock = computed(() => {
    const total = Math.max(0, Math.round(uiStore.videoSeconds || 0))
    return Math.floor(total / 60) + ":" + String(total % 60).padStart(2, "0")
})
const spedLabel = computed(() => {
    const s = uiStore.videoSpeed || 1
    return (Math.abs(s - Math.round(s)) < 0.05 ? Math.round(s) : s.toFixed(1)) + "\u00d7"
})

const stageShown = computed(() =>
    uiStore.files.length > 0 && !uiStore.batchMode)

const primaryTitle = computed(() =>
    offersDownload.value ? t('result.downloadTitle')
        : (uiStore.batchMode ? t('batch.optimizeAllTitle') : t('batch.optimizeOne')))

/* On a phone the two columns stack, so the stage begins below the fold: a
 * picture is staged and the page still shows the drop zone it was dropped
 * into, with the working surface out of sight underneath. Take the reader to
 * it, the same way opening a PDF page on its own does.
 *
 * Phones only. On anything wider the stage is already beside the drop zone,
 * and scrolling would move the page under somebody who can see both. */
watch(stageShown, (now, before) => {
    if (now && !before && myWURFLData.value.isMobilePhone) goToSingleStage()
})

/* An edit is a canvas pass over the whole picture, and on a big one it is not
 * instant. Measured here: one megapixel flips about as fast as you can see it,
 * eleven does not -- same work either way, one drawImage and one encode, just
 * more of it. The control switched on and the picture sat there, so the
 * reasonable conclusion was that it had not worked, and the reasonable
 * response was to press it again. The second press is a toggle. That is what
 * "the flip bounces back" was: nothing wrong with the transform, nothing on
 * screen saying it was running.
 *
 * So the picture says it is being worked on, and the presses are serialised.
 * Serialised rather than blocked: applyTransforms always starts from the
 * untouched original with whatever the controls currently say, so when several
 * arrive at once only the last one is worth doing -- the earlier ones would
 * produce a picture that is already out of date, on top of being the slow part.
 */
const editing = ref(false)
let editRun = 0
let editQueue = Promise.resolve()

function queueTransform({force = false} = {}) {
    const run = ++editRun
    editing.value = true
    // .catch on the tail first: one failed edit must not stop every edit after
    // it from ever running.
    editQueue = editQueue.catch(() => {}).then(async () => {
        // Superseded while it waited its turn: the newer signature already
        // describes everything this one was going to do, and on a big picture
        // the work skipped here is the expensive part. Download does not get
        // to skip -- it has to hand over the picture as it now stands.
        if (!force && run !== editRun) return

        /* AN ANIMATION IS RE-CONVERTED, NOT DRAWN ON.
         *
         * applyTransforms() is a canvas and a canvas holds one frame, so
         * baking words onto an animated GIF here would hand over a still.
         * bakeVideoCaption() runs the clip through again with the caption in
         * it instead, and returns false when there is nothing to do -- no
         * words, or the bytes already carry exactly these ones, so pressing
         * Save twice does not re-encode for the same answer.
         *
         * Only on the force pass. Every other pass is a preview, and a preview
         * deliberately leaves the words to CSS so their rectangle can be
         * dragged with them re-fitting on every pointer move. */
        /* An animation NEVER reaches applyTransforms() on a download pass.
         *
         * bakeVideoCaption() is a no-op when the bytes already carry these
         * words, and the early return happens either way -- because the thing
         * being avoided is not the wasted work, it is the canvas below. */
        if (force && uiStore.animatedGif) {
            const baked = await bakeVideoCaption()
            if (baked) bakedDecorations.value = true
            uiStore.showDownloadables = false
            handedOver.value = false
            return
        }

        // force means this is the download: the scrim has to be in the file
        // rather than drawn over it. Every other pass leaves it to CSS.
        await applyTransforms([...uiStore.files].slice(0, 1), {decorate: force})
        // Whatever came back from the CDN was made from the older picture.
        uiStore.showDownloadables = false
        handedOver.value = false
    }).finally(() => {
        if (run === editRun) editing.value = false
    })
    return editQueue
}

/* Set once a file has actually been handed over, so the saving can be
 * reported afterwards rather than announced before anyone asked. */
const handedOver = ref(false)

// Which way round the bytes on the stage currently are. A fresh file has been
// through nothing.
const bakedFlip = ref({h: false, v: false})

// ...and whether the scrim and the watermark are in them. True only between a
// download and the next edit: the stage's own passes leave them out.
const bakedDecorations = ref(false)

/* The chosen shape, as a ratio.
 *
 * "Square 1:1 -- 1080x1080" is a w_/h_/m_cropbox directive: the CDN crops the
 * picture to that frame on the way out. Nothing in the browser moves a pixel
 * for it, so the stage showed the original shape however square the answer was
 * going to be. Zero means keep the original.
 */
const presetRatio = computed(() => {
    const p = uiStore.presetList.find((x) => x.id === uiStore.preset)
    return p && p.h ? p.w / p.h : 0
})

/* The picture on the stage, as a file rather than as a CDN response.
 *
 * Its name and size are known the moment it is staged. Its pixel dimensions
 * are not -- UploadableFile carries width and height, but nothing has ever
 * written to them, so they are zero everywhere they are read. They are
 * measured here instead, from the bytes we hold, and re-measured after a crop
 * or a rotate, because that is when the answer changes. */
const stagedFile = computed(() => uiStore.files[0] || null)

/* WHEN THE DROP ZONE'S THUMBNAIL IS SAYING NOTHING NEW.
 *
 * Reported looking at a captioned GIF: the words appeared in the drop zone
 * picture as well as on the stage, "which seems silly". They are not drawn
 * there -- FilePreview refuses to draw marks, and says so -- they are in the
 * file's own pixels once a Save has baked them. So the thumbnail was telling
 * the truth, at a size where the words are illegible and sitting under the
 * "Uploaded" badge.
 *
 * The thumbnail's own job, in its words, is to say WHICH FILE IS LOADED. With
 * one file staged, the stage beside it is showing that same file larger, with
 * its name and size printed underneath -- so the question has already been
 * answered better a few hundred pixels to the right.
 *
 * ONE FILE ONLY. A batch is the opposite case: the stage shows a single
 * picture and the row of thumbnails is the only sight of the other nineteen.
 * A document has no thumbnail to lose, and a clip in the trimmer is not
 * staged at all.
 */
/* The picture takes the room the drop zone stops needing.
 *
 * Only on a real screen -- below md the columns stack and each is full width,
 * so there is nothing to rebalance -- and only once something is staged, which
 * is exactly when the drop zone has stopped being the subject of the page.
 *
 * A batch counts too: the stage shows one page of a document or one photo of
 * twenty, and it is still the thing being looked at.
 */
const stageWide = computed(() => uiStore.files.length > 0 && !uiStore.videoTrimming)

const previewIsRedundant = computed(() =>
    uiStore.files.length === 1 && !uiStore.videoTrimming && !stagedIsPdf.value)

/* A document is not a picture, and the stage is a picture-editing surface.
 *
 * Staging a PDF put an empty crop frame on screen with crop, flip and reset
 * sitting under it -- four controls with nothing to act on, over a blank
 * rectangle, on a page whose whole job is to turn pages into images. The
 * progress of the run itself was never the gap: PdfProgress has covered that
 * from the moment the button is pressed. The gap was before it. */
const stagedIsPdf = computed(() => {
    const f = stagedFile.value
    return !!(f && f.file && isPdf(f.file))
})

/* On a page that exists to convert PDFs, a PDF starts on its own.
 *
 * There is nothing to ask first, and that is a fact about the architecture
 * rather than a judgement: /upload-pdf rasterises and STORES the pages, and
 * every setting the options panel offers -- format, width, quality, rotation,
 * EXIF -- is an ImageEngine directive applied to those stored pages afterwards
 * by batchDirectives(). Changing one rewrites a URL; it does not re-send the
 * document. So the button was asking permission for a decision the visitor had
 * already made by being on this page.
 *
 * ONLY where the page said so. taskInput().kind === "pdf" is /pdf-to-jpg
 * declaring that a PDF is the entire point. On the home page a PDF is one of
 * several things a drop might mean, and 40 MB leaving a phone on metered data
 * because somebody tapped the wrong row in a picker is not something to guess
 * about -- there the button stays.
 *
 * The confirmation is not removed, it MOVES. A PDF is the one input with no
 * thumbnail ("no thumbnail for a document" is what the card says), so the
 * filename is the only way to see it is the wrong file; PdfProgress now names
 * it beside the page count, while the upload is still cancellable. Same check,
 * no dead click.
 *
 * Keyed on the file's identity, not on stagedIsPdf: replacing one PDF with
 * another leaves the boolean true, and the second document must start too.
 * Cancelling does NOT re-arm it -- the id has not changed, so the watcher stays
 * quiet and the button under the stage is there to try again with. That is the
 * whole reason the button and its card are kept rather than deleted.
 */
/* A document the visitor did not just choose. See collectCarriedImage(): a
 * picture carried from the previous tool is STAGED, never uploaded, because
 * arriving on a page is not a request -- "nothing reaches the network until
 * somebody asks for a file" is the rule the share target is deliberately the
 * only exception to. Without this the carry-over would start a 40 MB upload
 * for somebody who did nothing but open a menu item. */
let carriedId = null

watch(
    () => (stagedIsPdf.value && stagedFile.value) ? stagedFile.value.id : null,
    (id) => {
        if (!id) return
        if (id === carriedId) return
        if (taskInput().kind !== "pdf") return
        if (workInFlight.value || uiStore.batchRunning) return
        /* Not while a self-test is driving the page -- the same reason
         * collectCarriedImage() stands down. #selftest-pdf puts the progress
         * modal into a synthetic state on mount; a real upload firing into it
         * is a race, and a race is worse than either outcome. */
        if (/^#selftest-/.test(window.location.hash || "")) return
        startUploadFiles()
    }
)

/* A document whose pages are already back.
 *
 * There is nothing left for the button to ask for: the pages exist, and every
 * control in the panel re-runs them by itself now. Pressing it again re-sent
 * the whole document to be rasterised a second time for the same answer, which
 * is why it read as "I have to press it again" rather than as a no-op.
 */
const documentDone = computed(() =>
    stagedIsPdf.value && uiStore.batchItems.length > 0 && !uiStore.batchRunning)

const stagedDims = ref({w: 0, h: 0})

watch(
    () => {
        const f = stagedFile.value
        return f ? `${f.id}|${f.file && f.file.size}` : ""
    },
    async () => {
        const f = stagedFile.value
        if (!f || !f.file) { stagedDims.value = {w: 0, h: 0}; return }
        const url = URL.createObjectURL(f.file)
        try {
            stagedDims.value = await measure(url)
        } finally {
            // measure() resolves on error too, so this always runs and the blob
            // does not outlive the question it answered.
            URL.revokeObjectURL(url)
        }
    },
    {immediate: true},
)

// A different picture is a different question, and the saving reported for the
// last one is not an answer to it.
watch(() => uiStore.files.map((f) => f.id).join("|"), () => {
    handedOver.value = false
    bakedFlip.value = {h: false, v: false}
    bakedDecorations.value = false
})

/* One image: the button downloads, and downloading is what uploads. A batch
 * still starts its own run, and a PDF is a different job again. */
const offersDownload = computed(() =>
    !uiStore.batchMode && taskInput().kind !== "pdf" && uiStore.files.length > 0)

/* The one format on offer, when there is exactly one.
 *
 * The LABEL rather than the id, because it is going in a sentence: "PNG",
 * "JPEG XL", "WebP" -- the catalogue already spells them the way a person
 * writes them, and a second spelling somewhere else is a second thing to keep
 * in step. */
const soleFormatLabel = computed(() => {
    const ids = uiStore.enabledFormats
    if (!Array.isArray(ids) || ids.length !== 1) return ""
    const found = (uiStore.formatCatalog || []).find((f) => f.id === ids[0])
    return found ? found.label : ""
})

/* The task's own words, with the chosen format written into them.
 *
 * Empty unless the page declared a `ctaFormat` template AND exactly one
 * format is on offer -- so it is one test at the top rather than the same
 * condition repeated down each branch. Past one format the answer is a zip of
 * several and naming any of them is the same lie the template exists to fix. */
const formatAwareLabel = computed(() => {
    const only = soleFormatLabel.value
    const template = words.value.ctaFormat
    return (only && template) ? template.replace("{format}", only) : ""
})

const primaryLabel = computed(() => {
    if (offersDownload.value) {
        // The task's own words describe the job it was set up for, and that job
        // is one file: "Download the image". Tick a second format and they are
        // simply wrong -- there are two, and they arrive as a zip. The generic
        // string is the one with a plural in it, so past one version it wins.
        if (uiStore.imageVersions.length > 1) {
            return t("result.downloadAll", uiStore.imageVersions.length)
        }
        /* A CONVERSION PAGE FOLLOWS ITS OWN FORMAT PICKER.
         *
         * Reported from /heic-to-jpg: the results table said PNG while the
         * button still promised JPEGs. Every conversion page has that shape --
         * a format named in the button and a picker for it underneath -- so
         * this belongs here rather than on one page.
         *
         * This is the branch that runs: offersDownload is true as soon as
         * anything is staged, so a fix placed only in the other one never
         * executed. Measured, which is the only reason it was noticed. */
        return formatAwareLabel.value
            || words.value.cta
            || t("result.downloadAll", uiStore.imageVersions.length)
    }
    return formatAwareLabel.value || words.value.cta || (uiStore.batchMode
        ? t("batch.optimizeAll", {n: uiStore.files.length})
        : t("batch.optimizeOne"))
})

/* Cut the staged picture up and hand back a zip.
 *
 * NOTHING IS UPLOADED, which is the whole shape of this page: the picture is
 * decoded into an <img>, drawn tile by tile onto a canvas and zipped, so
 * /upload, the CDN and the versions table are never involved. /svg-to-png and
 * /mp4-to-gif make the same bargain and this one is not even a trade --
 * cutting moves pixels and there is nothing a CDN can do that the browser
 * cannot.
 *
 * THE SOURCE IS files[0].file, NOT originalFile. That is the file as it is
 * NOW, carrying whatever crop, flip or rotation has been applied -- which is
 * what the stage is showing and therefore what the grid lines were drawn over.
 * Reading originalFile would cut a picture the visitor is not looking at.
 */
async function runTiles() {
    const staged = uiStore.files[0]
    if (!staged || !staged.file || uiStore.tileBusy) {
        return
    }
    uiStore.tileError = ""
    uiStore.tileBusy = true
    const url = URL.createObjectURL(staged.file)
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image()
            el.onload = () => resolve(el)
            el.onerror = () => reject(new Error("decode"))
            el.src = url
        })
        const result = await downloadTiles({
            file: staged.file,
            img,
            cols: uiStore.tileCols,
            rows: uiStore.tileRows
        })
        if (!result) {
            // cutTiles() returns nothing only when the grid does not fit the
            // picture or the browser could not encode a tile. Both are worth
            // saying out loud: the button appearing to do nothing is the one
            // outcome that sends somebody away.
            uiStore.tileError = t("tiles.failed")
            return
        }
        track("download_clicked", {
            file_count: result.tiles,
            output_format: "zip",
            download_type: "tiles",
        })
    } catch (err) {
        uiStore.tileError = t("tiles.failed")
    } finally {
        URL.revokeObjectURL(url)
        uiStore.tileBusy = false
    }
}

/* SAVE HANDS OVER THE CANVAS THAT IS ON SCREEN.
 *
 * Not a re-render of it with the same settings -- the very canvas the preview
 * is showing, read straight off the component. So the file cannot disagree
 * with the picture it was downloaded from, by construction rather than by two
 * code paths happening to agree. That is the rule lib/meme.js exists for, and
 * it is available here in a way it is not there: a caption has to be drawn
 * twice because baking on every keystroke is too expensive, while a sheet is
 * drawn once and simply kept.
 *
 * It also retires downloadContactSheet()'s own render. Leaving that in place
 * would be a second renderer that nothing calls -- the shape of the
 * --edge-threshold flag that was built, verified and never emitted.
 */
async function makeContactSheet() {
    if (uiStore.sheetBusy) {
        return
    }
    uiStore.sheetBusy = true
    try {
        const preview = sheetPreview.value
        const blob = preview ? await preview.toBlob("image/jpeg", 0.9) : null
        if (!blob) {
            // toBlob returns nothing when the canvas could not be encoded, and
            // the preview returns nothing when there is no plan -- which is
            // what a sheet too large for a canvas looks like from here. Both
            // are worth saying: a button that appears to do nothing is the one
            // outcome that sends somebody away.
            alert(t("sheet.failed"))
            return
        }
        const first = uiStore.batchItems.find((i) => i.status === "done")
        handOverBlob(blob, sheetName(first ? uiStore.batchOutputName(first) : "",
                                     uiStore.batchTotals().done))
        track("download_clicked", {
            file_count: uiStore.batchTotals().done,
            output_format: "jpeg",
            download_type: "contact_sheet",
        })
    } catch (err) {
        alert(t("sheet.failed"))
    } finally {
        uiStore.sheetBusy = false
    }
}

/* The sheet as a one-page PDF.
 *
 * Reads the SAME canvas Save reads, so the PDF and the JPEG are the same
 * picture -- there is no second render to disagree, which is the property the
 * preview was built to give.
 */
async function sheetPdf() {
    const preview = sheetPreview.value
    // JPEG at 0.92 rather than the 0.9 Save uses: a PDF is the version that
    // gets printed, and the few per cent this costs is worth not carrying a
    // second generation of compression into a document.
    const blob = preview ? await preview.toBlob("image/jpeg", 0.92) : null
    if (!blob) {
        return false
    }
    const bytes = new Uint8Array(await blob.arrayBuffer())
    const {bytes: pdf, pages} = buildPdf([{bytes, name: "sheet.jpg"}])
    if (!pdf || !pages) {
        return false
    }
    const first = uiStore.batchItems.find((i) => i.status === "done")
    handOverBlob(new Blob([pdf], {type: "application/pdf"}),
                 sheetName(first ? uiStore.batchOutputName(first) : "",
                           uiStore.batchTotals().done, "pdf"))
    return true
}

function primaryAction() {
    if (offersAscii) return runAscii()
    /* BEFORE offersDownload, because a tiles page has a file staged and would
     * otherwise take the ordinary upload-and-deliver path -- which would hand
     * back one optimised picture on a page whose entire promise is many. */
    if (offersTiles) return runTiles()
    if (offersDownload.value) return makeAndDeliver()
    return startUploadFiles()
}

/* Upload once, then convert as often as the settings change.
 *
 * The key is cached in asciiKey and keyed on the staged file's id, which is
 * what makes moving the width slider cheap: the first run uploads, every run
 * after it is one small POST. Exactly the fact rebatchIfDone() turns on for a
 * PDF -- the bytes are already in the bucket, so only the half after the
 * upload has to happen again.
 *
 * The id guard matters because the cache would otherwise outlive its picture:
 * drop a second image and the old key would convert the old photograph while
 * the new one sat on the stage.
 */
let asciiKey = null
let asciiKeyFor = null
// Whether the reader has been taken to the art yet for THIS picture. Reset
// alongside the key, so a second picture scrolls again.
let firstAscii = true

/* Wait for the box rather than scrolling at it.
 *
 * #ascii-panel is v-if'd on a staged file and the <pre> inside it on the art
 * existing, so scrollIntoView can run before there is anything at that
 * position -- and scrollIntoView on an element with no layout box scrolls
 * nowhere at all. Same trap, and same fix, as #downloadables.
 */
function scrollToAscii(tries = 40) {
    const arrived = document.getElementById("ascii-out")
    const el = document.getElementById("ascii-panel")
    if (arrived && el && el.offsetParent !== null) {
        el.scrollIntoView({behavior: "smooth", block: "start"})
        return
    }
    if (tries > 0) setTimeout(() => scrollToAscii(tries - 1), 100)
}

async function runAscii() {

    const staged = uiStore.files[0]
    if (!staged) return
    if (uiStore.asciiRunning) return

    uiStore.asciiRunning = true
    uiStore.asciiError = ""

    try {
        /* THE CACHED UPLOAD HAS TO FOLLOW THE EDITS, and that -- not the
         * transform itself -- was the bug.
         *
         * Reported as "the crop function is not being honored": the stage
         * showed a cropped cat and the art was still the whole photograph,
         * sign and all.
         *
         * The tempting diagnosis is that a region crop is not a CDN directive
         * -- applyTransforms() moves the pixels on a canvas, which is why
         * /pdf-to-jpg cannot offer flip at all -- so the upload must be missing
         * it. MEASURED, AND WRONG: the store's own watchers already call
         * queueTransform() when flipH, flipV or crop change, so by the time
         * anything is pressed the staged file has been rewritten. Removing the
         * queueTransform() call below changes nothing about whether a flip
         * reaches the art.
         *
         * What was actually broken is one line further down: the key was
         * cached against staged.id ALONE. So the sequence in the report --
         * convert, then crop, then convert again -- reused the upload made
         * from the uncropped picture, and the second run asked the CDN for the
         * same object with the same directives and got the same answer.
         * Proved by reverting only the key: the crop stops reaching the art
         * and nothing else changes.
         *
         * The queueTransform() call stays anyway. It costs nothing when the
         * watcher has already run -- queueTransform coalesces -- and it means
         * this path does not depend on a watcher somewhere else having fired
         * first, which is exactly the sort of assumption that breaks when the
         * next control is added. Through the QUEUE rather than at
         * applyTransforms() directly, so two canvas passes cannot race over
         * one wrapper; startUploadFiles() does the same for the same reason.
         */
        const signature = transformSignature()
        if (!asciiKey || asciiKeyFor !== staged.id + signature) {
            try {
                await queueTransform({force: true})
            } catch (err) {
                throw new Error(err.message || t("errors.transformFailed"))
            }
            const [answer] = await uploadFiles([staged])
            if (!answer || !answer.key) {
                throw new Error(answer && answer.Error
                    ? answer.Error
                    : t("errors.uploadFailed", {message: ""}))
            }
            asciiKey = answer.key
            // KEYED ON THE TRANSFORM TOO, not just the file. Cropping after a
            // first conversion would otherwise re-use the upload made from the
            // uncropped picture -- the same bug the report was about, only
            // one interaction later and harder to see.
            asciiKeyFor = staged.id + signature
            firstAscii = true
        }

        const body = new FormData()
        body.append("key", asciiKey)
        body.append("cols", uiStore.asciiCols)
        body.append("contrast", uiStore.asciiContrast)
        body.append("edges", uiStore.asciiEdges)
        body.append("nobg", uiStore.asciiNoBg ? "1" : "0")
        // The source's own proportions, because m_productletterbox honours
        // w AND h exactly and would otherwise square up a landscape. These
        // are read off the loaded <img> by FilePreview; if they are somehow
        // absent the server declines the removal rather than guessing.
        body.append("srcw", staged.width || 0)
        body.append("srch", staged.height || 0)
        body.append("ramp", uiStore.asciiRamp)
        body.append("colours", uiStore.asciiColours ? "1" : "0")
        body.append("dark", uiStore.asciiDark ? "1" : "0")

        const res = await fetch(`${baseUrl}/asciiArt`, {method: "POST", body})
        const data = await res.json()
        if (!res.ok || data.Error) {
            throw new Error(data.Error || `HTTP ${res.status}`)
        }

        uiStore.asciiArt = data.art
        uiStore.asciiRows = data.rows
        uiStore.asciiBytes = data.bytes

        /* Take the reader to the art.
         *
         * The panel is below the two-column block, which on a laptop puts it
         * off the bottom of the screen -- so pressing the button appeared to
         * do nothing while the thing it produced sat a screen further down.
         * The same fault scrollToDownloadables() exists for, and the same
         * answer: SCROLLED, never a fragment navigation. `location.hash = ...`
         * appends a history entry and TRAVERSES to it, which fires popstate --
         * the event the back button fires -- and the handler here cannot tell
         * the app's own jump from a reader pressing back.
         *
         * Only when the art is NEW. Re-running from a slider drag must not
         * yank the page about while somebody is adjusting it, and by then
         * they are already looking at the right place.
         */
        if (firstAscii) {
            firstAscii = false
            scrollToAscii()
        }
    } catch (err) {
        // The picture stays on the stage: the settings are still there to
        // adjust and pressing again is the whole of the retry.
        uiStore.asciiArt = ""
        uiStore.asciiError = String((err && err.message) || err)
    } finally {
        uiStore.asciiRunning = false
    }
}

const cropTrigger = computed(() => {
    // Whether the settings panel needs its own way into the cropper depends on
    // one thing: whether the stage is there with handles already on the
    // picture. It used to ask whether the upload had come back, which stopped
    // being a question about the stage the moment the upload was deferred to
    // Download -- so the panel offered "choose the part to keep" beside a stage
    // that was showing the crop handles at the time.
    if (stageShown.value) return "none"
    // With nothing staged, the button could only ever answer "pick an image
    // first" -- so say what will happen instead. Same while work is in flight.
    if (!uiStore.files.length) return "hint"
    if (uiStore.isUploading || uiStore.optimizationInProgress) return "hint"
    // Staged, nothing in flight, and still no stage: a batch or a PDF, which
    // have no handles to offer, so the button that has always worked.
    return "button"
})

const cameraOffered = computed(() => myWURFLData.value.isMobile && accepted.images
    && !(myWURFLData.value.isMobilePhone && uiStore.files.length))

/* A blank starter, entering exactly where a dropped file does.
 *
 * addFiles() is the only intake, and that is worth keeping: it resets the
 * store, reapplies the task's preset, orders what arrives, refuses what this
 * page does not accept and says so in the right noun. A picture we generated
 * ourselves is still a picture somebody is about to work on, so it gets no
 * shortcut past any of that.
 */
/* A picture off the shelf, or a blank card.
 *
 * `areas` is where THIS picture's own caption spaces are -- three of the twelve
 * have a white bar painted in for the words. Setting the rectangles before the
 * file is staged is what makes the first thing the visitor sees correct: on
 * yes-no the two boxes land on the two white bars, and on artwork that plainly
 * says where the words go, anywhere else looks broken.
 *
 * Set BEFORE addFiles(), because addFiles() opens with uiStore.$reset() and
 * would throw them away. $reset() rebuilds from state(), so the assignment has
 * to come after it -- hence the order here, which is the opposite of what reads
 * naturally.
 */
function useLibraryPicture({file, areas}) {
    if (!file) return
    enteredBy("library")
    addFiles([file])
    const chosen = Array.isArray(areas) ? areas : []
    /* Positionally, and EVERY slot -- the ones the picture does not declare
     * are cleared rather than left holding the previous picture's rectangle,
     * which is what naming two of them here used to do for areas 3 and 4. */
    MEME_SLOTS.forEach((slot, i) => {
        uiStore[memeStoreKeys(slot).box] = chosen[i] || null
    })
    /* THE PICTURE DECIDES HOW MANY ZONES IT WANTS.
     *
     * A card with two white bars painted into it -- yes-no has them side by
     * side -- is asking for two captions, and opening one and making the
     * reader find the button would be withholding what the picture already
     * says. Everything else opens one. Written AFTER addFiles(), like the
     * boxes above, because addFiles() opens with $reset(). */
    uiStore.memeZones = memeSlots(chosen.length).length
    /* Folded again, for the same reason it is folded by default: the next
     * thing anybody does is write a caption, and the shelf they have just
     * finished with should not be sitting between them and the picture. A
     * plain ref, so addFiles()'s $reset() does not touch it either way. */
    libraryOpen.value = false
}


/* A caption's rectangle, drawn on the stage.
 *
 * Costs nothing but a repaint, like every other caption change: the preview is
 * CSS and the rectangle is burned in only on the way to a download. So this
 * goes through reoptimize(), which does nothing at all unless an earlier
 * download already baked the old placement into the bytes.
 */
function setCaptionBox({slot, box, settled}) {
    // By slot, not by name. Two named branches silently DROPPED the drag for
    // areas 3 and 4 -- the rectangle followed the pointer, the store never
    // heard, and it snapped back on release. Reported as "I can't drag one of
    // the boxes".
    if (!MEME_SLOTS.includes(slot)) return
    uiStore[memeStoreKeys(slot).box] = box
    // Only when the pointer comes up. The store is written on every move so the
    // words follow the rectangle -- which is free, because the stage draws them
    // in CSS -- but re-encoding the photograph is not, so that waits. Same
    // split as the sliders: @input writes, @change asks for the work.
    if (settled) reoptimize()
}

// The scrim gets its own place under the preview on the page that is about it.
const offersOverlay = taskOffersOverlay()

// Same again for the watermark. Either one means the picture on screen is
// already the finished article, so the button delivers instead of uploading.
const offersWatermark = taskOffersWatermark()
// And captions. Same bargain: the picture on screen is already the finished
// article, so the button delivers rather than merely optimising.
const offersMeme = taskOffersMeme()
const offersAscii = taskOffersAscii()
const offersInspect = taskOffersInspect()

/* Clear the stage instead of opening the picker, once there is something on
 * it. preventDefault() is what stops the <label> reaching #file-input;
 * removeFile() already does the rest properly -- $reset(), drop() so the
 * picture does not follow to the next tool, and back to the opening state. */
function onDropzoneClick(event) {
    if (!uiStore.files.length) return          // empty: let the label do its job
    event.preventDefault()
    for (const f of [...uiStore.files]) removeFile(f)
}

/* READING THE STAGED FILE, which is the whole of this page.
 *
 * No upload, no options, no primary button: the artefact already exists and
 * belongs to the visitor. The tool is this pair of refs, the watcher at the
 * foot of this script, and the panel they fill. */
const metadataInfo = ref(null)
const metadataReading = ref(false)
/* Many pictures onto one sheet, and one picture into many tiles.
 *
 * Each other's inverse, and both read off the registry the way offersMeme and
 * offersAscii do rather than testing a slug -- a slug test is how
 * /meme-generator would have ended up named in five files.
 *
 * They sit at opposite ends of the pipeline. The sheet is a BATCH page: the
 * pictures upload, optimise and list exactly as /bulk-image-compressor's do,
 * and the sheet is one more thing to do with the finished set beside the zip
 * and the PDF. The tiles are a SINGLE-image page that never uploads at all --
 * the cut is a canvas, the way /svg-to-png works -- so it takes over the
 * primary button the way the ASCII page does. */
const offersSheet = taskOffersSheet()
const offersTiles = taskOffersTiles()

/* Does this page take a clip? Asked off the declared INPUT rather than a
 * config key, because the two video controls are about the conversion that
 * happens before intake -- there is nothing in the config to switch on. */
const offersVideo = taskInput().kind === "video"
/* KEPT, though nothing gates the background control on it any more.
 *
 * The control follows `svgConverted`, which is set by the conversion itself,
 * because a drawing is now rasterised on every page that takes pictures and
 * the colour behind it is worth choosing wherever that happened. Gating on
 * the page instead left the home page with a converted drawing and no way to
 * put anything behind it -- which is how the reported "0 Bytes" screenshot
 * came to be taken on a page with no swatches on it. */
const offersSvg = taskInput().kind === "svg"

/* Where the draggable caption rectangles are offered.
 *
 * The meme page always, because that is what it is for. And /mp4-to-gif once
 * there are words to position -- the request was "wherever they want, like in
 * the meme task", and the machinery is the same machinery: the rectangle is
 * fractions of the picture, lib/meme.js lays the words out from it, and
 * CropSurface drags it. Offering an empty rectangle before anybody has typed
 * anything would be furniture over a picture for no reason. */
/* Whether an address can be typed here.
 *
 * A page that takes a clip takes one by URL too -- /mp4-to-gif is the whole
 * reason this widened past accepted.images, which is false there because
 * KIND_RULES.video does not admit pictures.
 *
 * WORTH KNOWING, and the page says it: a clip fetched this way DOES pass
 * through our server. That is the point of /fetchImage -- the browser cannot
 * read most other hosts because of CORS -- and it is the one exception to
 * "the clip stays on your device". Nothing is stored: the bytes are handed
 * straight back rather than put in the bucket, the same as a fetched picture.
 */
const fetchOffered = computed(() => accepted.images || offersVideo)

/* Whether the TURNS are on the picture, which decides whether the settings
 * panel still needs its own row of them. Exactly the bargain cropTrigger
 * already strikes for the cropper: one control, wherever it can be reached.
 *
 * Reported as the rotate controls being missing -- they were in the panel,
 * behind "Show advanced options", which on /rotate-image hides the one thing
 * the page is for. Moving them onto the picture fixes that; leaving the
 * panel's row there as well would be two answers to one question, which this
 * repository has paid for more than once.
 *
 * The panel keeps them wherever the stage's row is not rendered. That is not
 * a rare case: a DOCUMENT has no crop surface and still turns (a PDF built
 * wrong is the one thing rotation can rescue), a BATCH has no stage at all,
 * and a meme page suppresses the whole tool row. A clip is the one place
 * neither shows them, which is deliberate and older than this.
 *
 * It does NOT ask which stage tab is open, so on "Before / after" the turns
 * are out of reach until you come back to Edit. That is exactly what the crop
 * already does -- cropTrigger answers "none" whenever the stage is shown --
 * and the compare tab is for looking rather than editing, so a row that
 * appeared and disappeared with the tab would be the worse trade. */
const stageTurns = computed(() =>
    stageShown.value && !stagedIsPdf.value && !offersVideo && !offersMeme)

/* BELOW `const offersVideo`, AND THAT IS NOT TIDINESS.
 *
 * watch() runs its getter immediately to collect dependencies. These sat 1000
 * lines above the const they read, so that first run hit the temporal dead
 * zone -- and Vue wraps a watcher getter in its own error handling, so the
 * ReferenceError was swallowed, the watcher tracked NOTHING, and it never
 * fired again. Everything else on the page worked, which is what made it look
 * like a device-detection problem: the scroll simply never happened, silently,
 * on every device.
 *
 * TWO MOMENTS WORTH SCROLLING TO: the trimmer opening, and the conversion
 * finishing. Choosing a clip does not stage anything -- it opens the TRIMMER,
 * which is further down the page than the drop zone that was just tapped, so
 * the file appears to have gone nowhere.
 *
 * ASKED AS GEOMETRY, NOT AS DEVICE DETECTION. The first version gated both on
 * myWURFLData.isMobilePhone and nothing scrolled on a real phone -- and
 * whatever the reason for that (detection not yet answered, a proxy in the
 * way, a device it does not know), the question was never really "is this a
 * phone". It is "can they see the thing that just appeared", which the
 * element can answer itself. That also gets it right on a narrow desktop
 * window and wrong nowhere.
 */
watch(
    () => offersVideo && uiStore.videoTrimming,
    (open, was) => { if (open && !was) scrollToId("video-trimmer") }
)

/* "Choose another clip" has to put the chooser back in front of you.
 *
 * cancelTrim() empties everything and the drop zone reappears at the top of
 * the page -- but the page is still scrolled down to where the trimmer was, so
 * the button seems to do nothing but blank the screen. The same complaint as
 * the conversion not scrolling, from the other direction.
 *
 * After the reset, not before: the drop zone is v-show'd off while
 * videoTrimming is true, and scrollToId() waits for a laid-out element anyway.
 */
function cancelClip() {
    cancelTrim()
    scrollToId("drop-zone")
}

/* A REFUSAL HAS TO BE WHERE THEY ARE LOOKING.
 *
 * Reported as "I clicked Make the GIF, the UI disappeared and nothing
 * happened". Something did happen: convertTrimmed() dismisses the trimmer and
 * the conversion then failed, and #video-error is up beside the drop zone --
 * which is back at the top of the page, well above where the button they had
 * just pressed used to be. So the page went blank-looking and said nothing,
 * exactly as described.
 *
 * Same geometry rule as the other two: it only moves the page when the message
 * is not already on screen.
 */
watch(
    () => offersVideo && !!uiStore.videoError,
    (bad, was) => { if (bad && !was) scrollToId("video-error") }
)
watch(
    () => offersVideo && !uiStore.videoTrimming && !uiStore.videoBusy
          && uiStore.files.length > 0,
    (ready, was) => { if (ready && !was) scrollToId("single-stage") }
)

/* THE RECTANGLE IS HOW ANYBODY LEARNS THE WORDS ARE POSSIBLE.
 *
 * This used to require words to already exist on a video page, on the
 * reasoning that "before anybody types there is nothing to position, and an
 * empty rectangle over a picture is furniture for no reason". That is
 * circular, and it was reported as exactly what it produces: a converted GIF
 * on the stage with nothing on it to suggest a caption is available, so the
 * one gesture that would reveal the rectangles is the one gesture nobody
 * knows to make.
 *
 * The meme page never had the problem -- it shows both rectangles from the
 * moment a picture is staged -- so this is now the same rule on both. Being
 * inside the stage is enough of a guard: #single-stage is v-show'd on
 * something being staged, and on a clip that is the finished GIF. */
const captionBoxesShown = computed(() => offersMeme || offersVideo)

/* THE RECTANGLES AND THEIR EMPTINESS, PER SLOT.
 *
 * Both were object literals naming top and bottom, so areas 3 and 4 were
 * handed `undefined` -- CropSurface then found no stored box for them and drew
 * the default band every time, which is the other half of why a dragged
 * rectangle appeared to snap back. Built from MEME_SLOTS so adding an area is
 * still a change to the list and nothing else. */
const captionBoxes = computed(() => {
    const out = {}
    for (const slot of MEME_SLOTS) out[slot] = uiStore[memeStoreKeys(slot).box]
    return out
})
const captionEmpty = computed(() => {
    const out = {}
    for (const slot of MEME_SLOTS) {
        out[slot] = !(uiStore[memeStoreKeys(slot).text] || "").trim()
    }
    return out
})
const editsInBrowser = offersOverlay || offersWatermark || offersMeme

/* The button says Download, so it downloads.
 *
 * On this page the picture on screen is already the darkened one -- that is
 * the whole point of the preview -- so a button reading "Darken it" asks for
 * something the visitor can see has happened. It says Download, and a button
 * that says Download and then merely reveals a table of versions would be the
 * same lie in the other direction. So it does both: makes the file, then hands
 * it over.
 *
 * The delivery is best-effort on purpose. On iOS the share sheet is what puts
 * the file in Photos, and it needs transient activation -- after a round trip
 * to the CDN the tap that started this may no longer count. shareOrDownload()
 * falls back to an ordinary download in that case, which lands in Files: worse
 * than Photos, and still the file they asked for. The per-version buttons in
 * the table below are a direct tap with no wait in front of them, so those
 * reach the share sheet reliably.
 */
/* The flag, and nothing else. Wrapped rather than wrapping the body in a
 * try/finally in place: that would reindent ninety lines and bury the
 * change in the diff. */
async function makeAndDeliver() {
    delivering.value = true
    try { return await makeAndDeliverInner() }
    finally { delivering.value = false }
}

async function makeAndDeliverInner() {
    const startedAt = Date.now()
    await startUploadFiles()
    // startUploadFiles() resolves when the upload is done, not when the
    // versions have come back from the CDN -- so asking for a blob straight
    // afterwards finds none and hands over nothing at all, silently. Wait for
    // what is being handed over to actually exist.
    const ready = await waitForVersions()
    if (!ready.length) {
        track("optimization_error", {
            error_type: "no_versions_returned",
            input_format: formatOfAll(uiStore.files.map((f) => f.file)),
            file_count: uiStore.files.length,
        })
        alert(t('errors.transformFailed'))
        return
    }

    /* ONCE PER STAGED FILE, not once per render.
     *
     * Every control in the options panel calls reoptimize(), which re-runs
     * the whole thing once results exist -- so a visitor who nudges quality
     * three times gets three renders of the same picture. Counting those as
     * three completions would make the funnel ratio meaningless: the question
     * this event answers is "did a result arrive for the file they staged",
     * and that has one answer. The key is the staged file's own id, and
     * resetFunnel() in addFiles() is what lets the next picture report again.
     *
     * The DURATION is therefore the first one, which is the honest number: it
     * is what the visitor waited before seeing anything. */
    trackOnce(
        `optimized:${(uiStore.files[0] && uiStore.files[0].id) || "none"}`,
        "optimization_completed", {
            file_count: uiStore.files.length,
            input_format: formatOfAll(uiStore.files.map((f) => f.file)),
            output_format: ready.length > 1 ? "multiple" : ready[0].format,
            total_input_bytes: totalBytes(uiStore.files.map((f) => f.file)),
            total_output_bytes: ready.reduce((n, v) => n + (Number(v.size) || 0), 0),
            duration_ms: Date.now() - startedAt,
            picture_element: !!uiStore.pictureElements,
        })

    // One file is a file. Several are an archive -- handing them over one at a
    // time means one save prompt per version, and on a phone each one replaces
    // the last in the share sheet.
    if (ready.length > 1) {
        /* One archive, one event. Six formats of one picture is a single act
         * of taking the result away, and counting it six times would put the
         * download rate above the completion rate. */
        track("download_clicked", {
            file_count: ready.length,
            output_format: "multiple",
            download_type: "zip",
        })
        await downloadAsZip(uiStore.pictureElements ? pictureSnippet.value : null)
    } else {
        track("download_clicked", {
            file_count: 1,
            output_format: ready[0].format,
            download_type: "single",
        })
        await shareOrDownload(ready[0].blob, ready[0].filename)
    }
    handedOver.value = true
    // The job is done, so the carried copy has served its purpose. Keeping
    // somebody's photograph in browser storage past the point of use only
    // widens the window in which it could reappear somewhere they did not
    // expect it.
    dropCarried()

    // Only when there is a table to go to. One version does not make one --
    // the stage is already showing it -- and scrolling to a heading with
    // nothing under it is worse than not moving.
    if (ready.length > 1) scrollToDownloadables()
}

/* Every version that came back, once they all have.
 *
 * Waiting for the FIRST one was right while the button handed over a single
 * file. It is not right for an archive: the zip would be built from whichever
 * variants happened to have landed, and the visitor would get two of the three
 * they asked for with nothing said about the third. So this waits for the
 * whole set, and settles for what exists if the deadline passes -- a missing
 * variant should cost its own entry, not the download.
 */
function waitForVersions(timeoutMs = 30000) {
    const done = () => uiStore.imageVersions.filter((v) => v.blob && !v.failed)
    return new Promise((resolve) => {
        const started = Date.now()
        const tick = () => {
            const have = done()
            if (have.length >= uiStore.imageVersions.length) return resolve(have)
            if (Date.now() - started > timeoutMs) return resolve(have)
            setTimeout(tick, 250)
        }
        tick()
    })
}

/* The copy this page speaks in.
 *
 * `says("dropzone.browse")` reads dropzone.browse.<voice>, falling back to
 * .images for a voice nobody has written strings for yet. That fallback is
 * what stops a new task shape rendering a raw key path at somebody, and it is
 * why adding /darken-image later is a catalogue edit rather than a code one.
 *
 * This replaced a row of `acceptsPdf ? t('x.somethingPdf') : t('x')`
 * ternaries. Those worked, and they were a dead end: a third input kind would
 * have wanted browseSvg, mobileBodySvg and a ternary growing a third arm. */
const voice = inputVoice(maxBatchFiles)
function says(base, args) {
    const key = `${base}.${voice}`
    return te(key) ? t(key, args || {}) : t(`${base}.images`, args || {})
}

const SHARE_CACHE = 'imgopt-shared-v1'
const SHARE_MAX_AGE_MS = 5 * 60 * 1000


// useful for testing: no need to paste URL to be fetched each time
// uiStore.fetchedImageUrl = 'https://www.passani.it/fame/luca_james.jpg'

onMounted(() => {

    // console.log(new Date().getTime() - start, "APP.Vue has mounted")

    // let's allow copying and pasting an image.
    // https://www.techiedelight.com/paste-image-from-clipboard-using-javascript/
    // https://developer.mozilla.org/en-US/docs/Web/API/DataTransferItem/getAsFile
    const fileInput = document.getElementById("file-input")

    // fill the myWURFLData object with data from WURFL.js
    const takeWurfl = () => {
        // console.log(new Date().getTime() - start, "WurflJSDetectionComplete TRIGGERED (from App.vue)")
        myWURFLData.value.formFactor = window.WURFL.form_factor
        myWURFLData.value.isMobile = window.WURFL.is_mobile
        myWURFLData.value.completeDeviceName = window.WURFL.complete_device_name
        myWURFLData.value.isiOS = window.WURFL.is_ios
        myWURFLData.value.isAndroid = window.WURFL.is_android

        myWURFLData.value.isDesktop = myWURFLData.value.formFactor === "Desktop"
        myWURFLData.value.isMobilePhone = ["Smartphone", "Feature Phone","Other Mobile"].indexOf(myWURFLData.value.formFactor) >= 0
        myWURFLData.value.isTablet = myWURFLData.value.formFactor === "Tablet"
        myWURFLData.value.isSmartTV = myWURFLData.value.formFactor === "Smart-TV"
        // The form factor is only now known for certain, and the thumbnail
        // preview asks a different question of it than anything else does:
        // hover and press-and-hold are not the same gesture, and the regex
        // guess above cannot tell an iPad from a laptop.
        applyPeekGestures()
        // console.dir(myWURFLData)
    }

    /* ASK WHETHER IT HAS ALREADY ANSWERED, don't only listen.
     *
     * While this file injected the script itself, the listener was attached
     * in the same tick as the request and a 744 ms round trip meant it could
     * not be missed. Loading from the head makes that race real: on a repeat
     * view with wurfl.js in cache, WurflJSDetectionComplete can fire before
     * this component mounts, and a listener attached afterwards hears
     * nothing at all -- leaving the UA-sniffing fallback above in place with
     * nothing to say so. gam.js has guarded for exactly this from the start;
     * this is the same guard, for the same reason. */
    if (window.WURFL && window.WURFL.complete_device_name) {
        takeWurfl()
    } else {
        window.addEventListener('WurflJSDetectionComplete', takeWurfl)
    }


    window.addEventListener('paste', pasteEvent => {

        // there was a gotcha. This guy really helped me:
        // https://stackoverflow.com/questions/74452863/chrome-images-reading-clipboard-frm-javascript-something-must-have-changed-re/74453230#74453230
        let item;
        for (const x of pasteEvent.clipboardData.items) {
            if(x.type.indexOf("image") === 0){
                item = x;
                break;
            }
        }
        if (item) {

            let clipboadFile = item.getAsFile()
            // Setting the filename of the blob in the clipboard wasn't straightforward
            // https://stackoverflow.com/questions/21720390/how-to-change-name-of-file-in-javascript-from-input-file
            let  blob = clipboadFile.slice(0, clipboadFile.size, item.type)
            let newFile = new File([blob], 'clipboardPaste_'+Date.now(), {type: item.type})
            enteredBy("paste")
            addFiles([newFile])

            pasteEvent.preventDefault();
        }
        // } else {
        //     alert("You tried to Copy&Paste something, but... it doesn't seem to be an image!")
        // }

    })

    // Anything shared into the app from another app is waiting in a cache by
    // the time this page loads -- see the share-target handler in
    // service-worker.js for why it is a cache and not a postMessage.
    //
    // And if nothing was shared, whatever the visitor was working on before
    // they switched tools. In that order: a share is something they just did,
    // a carry-over is something they did a moment ago, and the fresh intent
    // wins. collectSharedImage() stages AND uploads; this only stages.
    if (/[?&]share-missed=1\b/.test(window.location.search || "")) {
        shareMissed.value = true
        /* Whoever redirected here says what they were handed. `from=server`
         * means the worker never ran; its absence means the worker ran and
         * found no file in the field the manifest names. The two need
         * opposite fixes and look identical on screen without this. */
        try {
            const q = new URLSearchParams(window.location.search)
            const shape = q.get("shape")
            if (shape) {
                shareMissedDetail.value =
                    (q.get("from") === "server" ? "server: " : "worker: ") + shape
            }
        } catch (err) { /* the message stands without it */ }
        // Take it out of the address so a reload, or a page kept open for a
        // week, does not go on reporting a share that failed once.
        try {
            const u = new URL(window.location.href)
            u.searchParams.delete("share-missed")
            window.history.replaceState({}, "", u.pathname + u.search + u.hash)
        } catch (err) { /* history is a nicety here */ }
    }

    collectSharedImage().then((shared) => {
        if (!shared) collectCarriedImage()
        // Whatever a closed tab left behind. sessionStorage dies with its tab
        // and the cache does not, so without this a picture could sit in
        // storage indefinitely with nothing left that would ever come back
        // for it -- which is also what lets privacy.html say it is cleared.
        sweepCarried()
    })

    // see selfTestBatch()
    /* #selftest-crop exposes applyCrop() so a suite can commit a rectangle
     * without driving the cropper's pointer gestures.
     *
     * Only behind the hash, and only the commit -- the region still goes
     * through uiStore.crop and reoptimize() exactly as the cropper's own
     * "accept" does, so what a test proves is the real path. It exists because
     * tests/video_to_gif_test.sh has to show that a crop on an animation costs
     * NO re-conversion, and the thing being measured is what happens after the
     * rectangle is chosen rather than the choosing. */
    if (/^#selftest-crop\b/.test(window.location.hash || "")) {
        window.__oioCrop = (w, h, x, y) => applyCrop({
            x: x || 0, y: y || 0, w: w || 160, h: h || 90,
        })
    }

    const selftest = /^#selftest-batch=(\d+)$/.exec(window.location.hash || "")
    if (selftest) {
        selfTestBatch(parseInt(selftest[1], 10))
    }

    /* /mp4-to-gif#selftest-video=13 opens the trimmer on a 13-second clip.
     * A filename may follow: #selftest-video=13/holiday.mp4 */
    const vidSelftest = /^#selftest-video=(\d+(?:\.\d+)?)(?:\/(.+))?$/
        .exec(window.location.hash || "")
    if (vidSelftest) {
        selfTestVideoTrim(parseFloat(vidSelftest[1]),
                          vidSelftest[2] ? decodeURIComponent(vidSelftest[2]) : "")
    }

    // see selfTestPdfProgress()
    /* The filename is optional and it is why this hook grew a fourth field.
     *
     * tools/shots.js could not photograph the progress panel at all. It aims
     * at the panel NAMING the document, and under --virtual-time-budget that
     * panel does not exist long enough: a probe sampling every 100 ms from the
     * moment a 24-page PDF was handed over found the whole run already
     * finished on its FIRST sample. The window is not short, it is skipped --
     * the same fact as the note above about /pdf-progress never being polled
     * in there. A bigger fixture does not help.
     *
     * So the shot needs the panel put on screen synchronously, which is what
     * this hook has always been for -- except docName reads
     * uiStore.files[0].file.name, and a hook that stages nothing produces a
     * panel with no name on it, and the name is the whole subject of the shot.
     *
     * Anything after the third slash is taken as the filename, so the existing
     * three-field form keeps working exactly as it did.
     */
    const pdfSelftest = /^#selftest-pdf=(\d+)\/(\d+)\/(rendering|storing)(?:\/(.+))?$/
        .exec(window.location.hash || "")
    if (pdfSelftest) {
        selfTestPdfProgress(parseInt(pdfSelftest[1], 10), parseInt(pdfSelftest[2], 10),
                            pdfSelftest[3],
                            pdfSelftest[4] ? decodeURIComponent(pdfSelftest[4]) : "")
    }


    // console.log("window.innerWidth: " + window.innerWidth)
    // console.log("window.innerHeight: " + window.innerHeight)
})


const SHARED_EXT_TYPES = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
    webp: 'image/webp', avif: 'image/avif', heic: 'image/heic',
    heif: 'image/heif', bmp: 'image/bmp', svg: 'image/svg+xml',
    // A clip shared from a gallery usually arrives with its type; these are
    // for the pickers that send none, which is the same gap looksLikeVideo()
    // covers on the drop path.
    mp4: 'video/mp4', m4v: 'video/x-m4v', mov: 'video/quicktime',
    webm: 'video/webm', ogv: 'video/ogg', '3gp': 'video/3gpp',
    mkv: 'video/x-matroska', avi: 'video/x-msvideo',
}

function sharedTypeFromName(name) {
    const dot = name.lastIndexOf('.')
    if (dot < 0) {
        return ''
    }
    return SHARED_EXT_TYPES[name.slice(dot + 1).toLowerCase()] || ''
}

// Picked up from the cache the service worker wrote before it redirected here.
// A share is an explicit act, so this does not just stage the image the way the
// file picker does -- it starts the whole thing immediately.
async function collectSharedImage() {

    if (!('caches' in window)) {
        return false
    }

    let cache
    try {
        cache = await caches.open(SHARE_CACHE)
    } catch (err) {
        return false                 // storage unavailable; nothing to collect
    }

    const keys = await cache.keys()
    if (!keys.length) {
        return false
    }

    const files = []
    for (const request of keys) {
        const hit = await cache.match(request)
        await cache.delete(request)  // collected once, whatever happens next
        if (!hit) {
            continue
        }
        // Ignore a stale hand-off. The worker clears the cache before writing,
        // so this only catches an image shared into a page that was then closed
        // before it could be read.
        const stamp = Number(hit.headers.get('x-share-stamp') || 0)
        if (stamp && Date.now() - stamp > SHARE_MAX_AGE_MS) {
            continue
        }
        const name = decodeURIComponent(hit.headers.get('x-share-filename') || 'shared')
        const blob = await hit.blob()
        // addFiles refuses anything it cannot place, with an alert. The
        // manifest offers this app for image/* AND video/* shares, so the type
        // is normally there, but a sharing app that sends none would otherwise
        // be met with "doesn't look like a valid IMAGE file". Fall back to the
        // extension, and drop it quietly if even that says nothing.
        const sent = (blob.type || '').toLowerCase()
        const type = sent.startsWith('image/') || sent.startsWith('video/')
            ? blob.type
            : sharedTypeFromName(name)
        if (!type) {
            /* IT USED TO `continue` HERE, AND THAT WAS THE BUG BEHIND A REPORT
             * that read "OIO won't receive the image": a share arrived, the
             * worker stashed it, and this dropped it without a word because
             * neither the MIME type nor the extension said what it was. The
             * visitor got an untouched drop zone and no way to tell that from
             * a share that never happened.
             *
             * Record what turned up instead. The name and the type are what
             * decide this branch, so they are exactly what is worth seeing. */
            shareMissedDetail.value = (name || "?") + " \u2014 " +
                (blob.type ? blob.type : "no type")
            continue
        }
        files.push(new File([blob], name, { type }))
    }

    if (!files.length) {
        // Something was shared and none of it could be placed. Silence here is
        // indistinguishable from a broken app, which is how this was reported.
        if (keys.length) {
            shareMissed.value = true
        }
        return false
    }

    /* Same two steps as picking a file and pressing the button, so the shared
     * image goes through exactly the path that is already exercised -- and
     * keeps its real filename, which the old code threw away along with the
     * extension.
     *
     * EXCEPT FOR A CLIP, WHICH IS NOT UPLOADED. That is not a policy decision
     * taken here, it is what the page does: on /mp4-to-gif addFiles() hands a
     * video to the TRIMMER and returns, so there is nothing staged to upload
     * and startUploadFiles() would be asking the CDN to optimise a file that
     * never left the device. The comment in file-list.js beside startTrim()
     * used to say the share hand-off "never carries a video"; it does now, and
     * this is the branch that keeps that true in the only way that matters.
     *
     * The worker sends a shared clip to /mp4-to-gif, so the trimmer is what it
     * meets. If an older worker is still in control and it lands somewhere
     * else, addFiles() refuses it out loud, which is the same answer any other
     * unusable file gets rather than a silence. */
    const clip = files.length === 1 && looksLikeVideo(files[0])
    enteredBy("share")
    addFiles(files)
    if (!clip) {
        startUploadFiles()
    }
    return true
}

/* The picture the visitor had on the previous tool.
 *
 * lib/handoff.js explains where it was kept and why the tab decides. Two
 * things this deliberately does NOT do:
 *
 * It does not upload. A share is an explicit act, so collectSharedImage()
 * starts the whole thing; arriving on a different tool is not, and the rule
 * everywhere else here is that nothing reaches the network until somebody asks
 * for a file.
 *
 * And it does not complain. The file is offered to inputRules() first -- the
 * same table the file picker's accept attribute is built from -- so a PDF
 * carried to a page that takes only images is quietly left where it is for the
 * next tool along. addFiles() would have refused it with an alert, and an
 * apology about a file nobody just chose is worse than the emptiness it
 * replaced.
 */
async function collectCarriedImage() {
    if (uiStore.files.length) return false
    /* Not while a self-test is driving the page.
     *
     * #selftest-batch and #selftest-pdf exist to put the application into a
     * synthetic state that no real journey produces, and they run synchronously
     * on mount. This lands afterwards, asynchronously, and addFiles() opens
     * with $reset() -- so a carried picture would quietly wipe the state the
     * self-test had just set up. The two e2e PDF-progress cases passed or
     * failed depending on which won, which is worse than either.
     */
    if (/^#selftest-/.test(window.location.hash || "")) return false
    const rules = inputRules()
    const file = await carriedFile((f) => isPdf(f)
        ? rules.pdf
        : rules.images && (f.type || '').includes('image'))
    if (!file) return false
    enteredBy("carry_over")
    addFiles([file])
    /* Recorded synchronously, which is what makes it work: addFiles() is
     * synchronous, so the store already holds the carried file here, while the
     * auto-start watcher above is queued and does not run until this tick
     * ends. It reads this id and stands down. */
    carriedId = (uiStore.files[0] && uiStore.files[0].id) || null
    return true
}

/* Nothing reaches the network until somebody asks for a file.
 *
 * A single image used to be uploaded and rendered the moment it was staged.
 * That spent a round trip on an image the visitor was about to change, and
 * every edit afterwards spent another: measured at two uploads and two renders
 * for one flip, plus an S3 object each time. ImageEngine is a bill now rather
 * than a demonstration, and the stage does not need it -- it draws the canvas
 * result locally.
 *
 * So edits are local, and Download is the only thing that uploads.
 */
/* The drop zone was bound straight to addFiles. It goes through here now only
 * so a drop can be told apart from the eight other ways a file arrives -- a
 * dropped file and a picked one are the same intake and must stay so, which is
 * why this adds a label and nothing else. */
function onFilesDropped(dropped) {
    enteredBy("drag_drop")
    addFiles(dropped)
}

function onInputChange(e) {
	// console.log("1. New Files")
    // console.dir(e.target.files)
    enteredBy("file_picker")
    addFiles(e.target.files)
    e.target.value = null // reset so that selecting the same file again will still cause it to fire this change
}


// when there's still no image, people will click on the upload button. Rather than activating, we
// trigger the file selection mechanism automatically
function clickOnFileInput() {

    let fileInput = document.getElementById("file-input")
    fileInput.click()

}


// Run `work` over `items`, `limit` at a time.
//
// Needed because every item is a cache-miss transform on the CDN. Firing twenty
// at once gets them queued by the browser's per-host cap anyway, but with no
// ordering and no way to show sensible progress; a small pool finishes the
// early ones early, so the table fills top to bottom.
async function inBatches(items, limit, work) {
    let next = 0
    const runners = []
    for (let i = 0; i < Math.min(limit, items.length); i++) {
        runners.push((async () => {
            while (next < items.length) {
                const mine = next++
                await work(items[mine], mine)
            }
        })())
    }
    await Promise.all(runners)
}


// Batch mode. One output per image, in one format, in exchange for none of the
// single-image apparatus: no six variants, no before/after, no picture element.
// See the batch notes in the store for why that is the right trade.
/* A PDF goes to its own endpoint, and comes back as a set of pages.
 *
 * Nothing is transformed in the browser first: applyTransforms() exists for the
 * flip and the region crop, and neither applies to a document you have not
 * looked at yet. The server rasterises, the pages land in the bucket, and from
 * there this is an ordinary batch.
 */
async function startPdf(wrapper) {

    /* A genuinely new set, so the completion below may report again. */
    batchRunId++
    batchStartedAt = Date.now()
    batchInputFormat = formatOfAll(uiStore.files.map((f) => f.file))

    uiStore.batchRunning = true
    uiStore.batchDone = 0
    uiStore.showDownloadables = false

    const form = new FormData()
    form.append('file', wrapper.file)
    // A token to watch the work by. The browser makes it, because the POST does
    // not answer until every page is done -- there is no earlier moment for the
    // server to hand one back. crypto.randomUUID where it exists; a random
    // string where it does not, because this only has to be unguessable enough
    // that two visitors cannot collide.
    const job = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID().replace(/-/g, "")
        : Math.random().toString(36).slice(2) + Date.now().toString(36)
    form.append('job', job)

    // XHR rather than fetch, for one reason: fetch cannot report upload
    // progress. A 20 MB scan on a slow connection spends most of its time
    // going up, and that is the part we can put a number on.
    //
    // The page count is guessed from the bytes first, so the rendering phase --
    // which reports nothing at all until it is done -- still has an estimate to
    // offer. A guess of zero simply withholds it.
    uiStore.pdfPhase = "uploading"
    uiStore.pdfPercent = 0
    uiStore.pdfRemainingMs = 0
    uiStore.pdfDone = 0
    uiStore.pdfTotal = 0
    uiStore.pdfStage = ""
    uiStore.pdfPages = await guessPageCount(wrapper.file)

    /* The server admits only so many documents at once and says so.
     *
     * It answers 503 with Retry-After when its slots are full -- see
     * PDF_MAX_CONCURRENT in server.py, and the measurements it cites. That is
     * not a failure and must not read as one: nothing is wrong with the
     * document, the queue is just full for a few seconds. So it becomes a wait
     * with a countdown, and the upload is retried without the visitor having
     * to do anything.
     *
     * Twice, then it gives up and says so. A page that retries for ever is a
     * page that never tells you it is not going to work.
     */
    const PDF_BUSY_MAX_WAITS = 2

    const sendOnce = () => new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest()
            pdfRequest = xhr
            const started = Date.now()

            xhr.open("POST", `${baseUrl}/upload-pdf`)

            xhr.upload.onprogress = (ev) => {
                if (!ev.lengthComputable) return
                uiStore.pdfPercent = Math.round((ev.loaded / ev.total) * 100)
                // Rate measured over the whole transfer so far, which is
                // steadier than the last chunk and good enough to say "about".
                const elapsed = Date.now() - started
                if (elapsed > 400 && ev.loaded > 0) {
                    const perMs = ev.loaded / elapsed
                    uiStore.pdfRemainingMs = Math.round((ev.total - ev.loaded) / perMs)
                }
                if (ev.loaded >= ev.total) beginRendering()
            }
            xhr.upload.onload = beginRendering

            xhr.onload = () => {
                let body = null
                try { body = JSON.parse(xhr.responseText) } catch (err) { /* not json */ }
                if (xhr.status >= 200 && xhr.status < 300) return resolve(body || {})
                // Busy is not broken. Retry-After is the server's own estimate;
                // the header is authoritative and the body carries it too so a
                // proxy that strips headers cannot turn a wait into an error.
                if (xhr.status === 503) {
                    const header = parseInt(xhr.getResponseHeader("Retry-After") || "", 10)
                    const seconds = header || (body && body.RetryAfter) || 15
                    return reject(Object.assign(
                        new Error((body && body.Error) || "Busy"),
                        {busy: true, retryAfter: Math.max(1, Math.min(120, seconds))}))
                }
                // The STATUS travels with it. A 400 is the server saying
                // something about THIS document -- too many pages, too many
                // megapixels, not a PDF -- and that will be just as true next
                // time; anything else is the queue, the network or us. The
                // caller needs to tell those apart to know whether the file on
                // the stage is still worth keeping.
                reject(Object.assign(
                    new Error((body && body.Error) || `HTTP ${xhr.status}`),
                    {status: xhr.status}))
            }
            xhr.onerror = () => reject(new Error("the upload did not complete"))
            xhr.onabort = () => reject(Object.assign(new Error("cancelled"), {cancelled: true}))
            xhr.onloadend = stopWatching
            watchPdfJob(job)
            xhr.send(form)
    })

    // A visible wait rather than a hidden one: the ring keeps turning, the
    // panel says why, and the number counts down so it is obvious something
    // is going to happen.
    const waitForSlot = async (seconds) => {
        uiStore.pdfPhase = "waiting"
        uiStore.pdfStage = ""
        uiStore.pdfPercent = 0
        uiStore.pdfRemainingMs = 0
        uiStore.pdfDone = 0
        uiStore.pdfTotal = 0
        for (let left = seconds; left > 0; left--) {
            uiStore.pdfRetryIn = left
            await new Promise((r) => setTimeout(r, 1000))
            if (uiStore.pdfPhase !== "waiting") return false   // cancelled
        }
        uiStore.pdfRetryIn = 0
        return true
    }

    let payload
    let busyWaits = 0
    while (payload === undefined) {
        try {
            payload = await sendOnce()
        } catch (err) {
            if (err.busy && busyWaits < PDF_BUSY_MAX_WAITS) {
                busyWaits += 1
                uiStore.pdfPhase = "uploading"
                if (await waitForSlot(err.retryAfter)) {
                    uiStore.pdfPhase = "uploading"
                    uiStore.pdfPercent = 0
                    continue
                }
            }
            stopPdf()
            uiStore.batchRunning = false
            // A cancel is not a failure and does not deserve an alert.
            if (!err.cancelled) {
                track("optimization_error", {
                    error_type: "pdf_upload_refused",
                    input_format: "pdf",
                })
                // The server writes its refusals to be shown: each one names which
                // limit was met, because "that did not work" leaves somebody with
                // nothing to try next.
                alert(err.message || t('errors.uploadFailed', {message: ''}))

                /* AND THE DOCUMENT COMES OFF THE STAGE, on a 400 only.
                 *
                 * The refusal is permanent for this file, and leaving it
                 * staged leaves the primary button armed over it -- so the one
                 * thing the page then invites is sending the same document up
                 * again to be refused in the same words. Measured on the report
                 * this was written against: a 124-page, 9.4 MB magazine, which
                 * is 9.4 MB of somebody's uplink per press. The auto-start
                 * watcher does not re-fire (the file's id has not changed), so
                 * that button is the only route back and it is a route to the
                 * same wall.
                 *
                 * It also un-carries it. keep() ran at intake, so without this
                 * a document that got nowhere here follows the visitor to the
                 * next tool and sits in Cache Storage until the TTL collects
                 * it. removeFile() does both halves, and the task preset
                 * survives because applyTaskToState() runs inside state() --
                 * the page comes back set up for the job rather than blank.
                 *
                 * NOT on the others, and that is the whole reason the status
                 * is carried this far. A 503 after two waits is a queue that
                 * was full, a network error is a network error, and a cancel
                 * is deliberate -- for all three the file on the stage is HOW
                 * you try again, which is the rule the cancel path already
                 * turns on ("The button and its card stay").
                 */
                if (err.status === 400) {
                    for (const f of [...uiStore.files]) removeFile(f)
                }
            }
            return
        }
    }

    stopPdf()

    const pages = payload.pages || []
    if (!pages.length) {
        uiStore.batchRunning = false
        alert(t('errors.pdfNoPages'))
        return
    }

    // A PDF always presents as a batch, even a one-page one.
    //
    // This was `pages.length > 1`, which left a single-page document with
    // batchMode off while runBatch() -- the only thing that runs next -- never
    // touches workableImage. So neither table was shown: the overlay came
    // down, the page went back to looking like the drop zone, and the one image
    // that had just been made was nowhere. A receipt or a certificate is one
    // page, so this was not a rare case.
    //
    // One row is a truthful result, and "open on its own" is right there in it
    // for anyone who wants the editor.
    uiStore.batchMode = true

    return runBatch(
        pages.map((p) => ({name: p.name, key: p.key})),
        pages.map((p) => ({id: p.key, name: p.name, size: p.size})))
}


async function startBatch() {

    /* A genuinely new set, so the completion below may report again. */
    batchRunId++
    batchStartedAt = Date.now()
    batchInputFormat = formatOfAll(uiStore.files.map((f) => f.file))

    uiStore.batchRunning = true
    uiStore.batchDone = 0
    uiStore.showDownloadables = false

    const chosen = [...uiStore.files]

    // Upload first, a few at a time.
    //
    // This used to be uploadFiles(), an unbounded Promise.all over the whole
    // list. That was survivable while the cap was ten and browsers held
    // themselves to six connections per host -- but production speaks HTTP/2,
    // which multiplexes instead of queueing, so twenty files would have meant
    // twenty simultaneous multipart uploads of up to 50 MB each. The CDN phase
    // below was already bounded by inBatches(); the upload phase now is too,
    // through the same limit.
    let uploaded
    try {
        await applyTransforms(chosen)
        uploaded = new Array(chosen.length)
        await inBatches(chosen, BATCH_CONCURRENCY, async (file, i) => {
            const one = await uploadFiles([file])
            uploaded[i] = one[0]
        })
        // A file that came back with nothing has no key to fetch, and would
        // otherwise become a row that can never finish.
        if (uploaded.some((u) => !u)) {
            throw new Error("one of the uploads came back empty")
        }
    } catch (err) {
        uiStore.batchRunning = false
        alert(t('errors.uploadFailed', { message: err }))
        return
    }

    return runBatch(uploaded, chosen.map((f) => ({id: f.id, name: f.name, size: f.size})))
}


/* Everything after the upload, for whatever put the files on S3.
 *
 * A batch of photographs gets here by uploading them one POST at a time. A PDF
 * gets here having been rasterised on the server, so its pages are already
 * objects in the bucket and there is nothing left to upload. Past this line the
 * two are the same thing: one row per image, the same directives, the same
 * table, the same zip.
 *
 * `meta` is what the row shows before its own result arrives -- the name and
 * the original size. For photographs that is the file the visitor chose; for a
 * PDF it is the page.
 */
/* Which run finished, so a re-render cannot report a second completion.
 *
 * rebatchIfDone() rebuilds every row from the objects already in the bucket
 * whenever a setting changes, and that is the same twenty pictures arriving
 * again -- one job, however many times its URLs are rewritten. Incremented in
 * startBatch()/startPdf() where a genuinely new set enters. */
let batchRunId = 0
let batchStartedAt = 0
let batchInputFormat = "unknown"

async function runBatch(uploaded, meta) {
    uiStore.batchItems = uploaded.map((res, i) => ({
        id: meta[i].id,
        origName: meta[i].name,
        origSize: meta[i].size,
        name: res.name,
        key: res.key,
        url: uiStore.s3urlRoot + res.key,
        outSize: 0,
        status: "waiting",
        blob: null,
        versions: [],
        // Editable per image, the way the single-image panel has always let the
        // alt text be edited. One shared value would be useless across ten
        // pictures. The filename is a starting point, not a good answer.
        alt: meta[i].name.replace(/\.[^.]+$/, "")
    }))

    await inBatches(uiStore.batchItems, BATCH_CONCURRENCY, async (item) => {
        item.status = "working"
        try {
            if (uiStore.pictureElements) {
                // the whole responsive set, so the snippets below refer to
                // files that will actually be in the zip
                if (!await fetchVariantSet(item)) {
                    throw new Error("no variant came back")
                }
                const primary = item.versions.find((v) => !v.failed && v.suffix === "_large")
                    || item.versions.find((v) => !v.failed)
                item.outSize = primary ? primary.size : 0
            } else {
                const res = await fetch(uiStore.batchUrl(item))
                if (!res.ok) {
                    throw new Error("HTTP " + res.status)
                }
                const data = await res.blob()
                // Held as an object URL so the row can offer a direct download
                // and the zip can be built without asking the CDN twice.
                // Revoked in releaseBatch() -- these are not garbage collected.
                item.blob = window.URL.createObjectURL(data)
                item.outSize = Number(res.headers.get("content-length")) || data.size
            }
            item.status = "done"
        } catch (err) {
            item.status = "failed"
        }
        uiStore.batchDone++
    })

    uiStore.batchRunning = false

    /* THE BATCH'S ONE COMPLETION, counted once whatever the run held.
     *
     * Twenty photographs is one job: the visitor asked once and waited once,
     * so this is one event with file_count=20 rather than twenty events. The
     * key is the run, so rebatchIfDone() -- which re-renders every row when a
     * setting changes -- does not report a second completion for the same set.
     *
     * Rows that failed are counted separately and not included in the output
     * total, which is the same rule batchTotals() applies on screen: summing
     * every original against only the outputs that arrived is how a figure
     * comes to claim a saving it did not make. */
    {
        const items = uiStore.batchItems
        const done = items.filter((it) => it.status === "done")
        const failed = items.filter((it) => it.status === "failed")
        trackOnce(`batch:${batchRunId}`, "optimization_completed", {
            file_count: done.length,
            input_format: batchInputFormat,
            output_format: uiStore.batchFormat || "unknown",
            total_input_bytes: done.reduce((n, it) => n + (Number(it.origSize) || 0), 0),
            total_output_bytes: done.reduce((n, it) => n + (Number(it.outSize) || 0), 0),
            duration_ms: Date.now() - batchStartedAt,
            picture_element: !!uiStore.pictureElements,
        })
        if (failed.length) {
            track("optimization_error", {
                error_type: "render_failed",
                input_format: batchInputFormat,
                file_count: failed.length,
            })
        }
    }

    /* The wait ends exactly here. After a PDF the overlay has just come down
     * and the reader is still looking at the drop zone, with the pages they
     * waited for somewhere off the bottom of the screen -- the same complaint
     * as the spinner below the fold, one step later. Every device, not just
     * phones: a desktop window is wider, not taller.
     *
     * ON THE SHEET PAGE the thing the reader waited for is the combined
     * picture rather than the table of pieces it was made from, so it goes
     * to that instead -- and only when it is not already in view, which is
     * what keeps a desktop still and moves a phone. */
    if (offersSheet) {
        goToSheet()
    } else {
        goToBatchResults()
    }
}


// Measure what actually came back. A variant's srcset descriptor has to state
// the real width: ImageEngine will not upscale, so asking for 1280 from an
// 800px original gives an 800px file, and claiming 1280w would make browsers
// pick the wrong one.
function measure(objectUrl) {
    return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve({w: img.naturalWidth, h: img.naturalHeight})
        img.onerror = () => resolve({w: 0, h: 0})
        img.src = objectUrl
    })
}


// One image's worth of variants, for a batch that is building <picture>
// elements. Same directives and the same naming as the single-image path.
async function fetchVariantSet(item) {

    item.versions = uiStore.newVariantSet()

    for (const v of item.versions) {
        try {
            const res = await fetch(`${item.url}?imgeng=${uiStore.buildDirectives(v)}`)
            if (!res.ok) {
                throw new Error("HTTP " + res.status)
            }
            const data = await res.blob()
            v.blob = window.URL.createObjectURL(data)
            v.size = Number(res.headers.get("content-length")) || data.size
            v.filename = uiStore.batchVariantName(item, v)
            const dims = await measure(v.blob)
            v.naturalWidth = dims.w
            v.naturalHeight = dims.h
        } catch (err) {
            v.failed = true
        }
    }
    return item.versions.some((v) => !v.failed)
}


// Object URLs live until they are revoked or the document goes away. One image
// never mattered; twenty held across a re-run does.
function releaseBatch() {
    for (const item of uiStore.batchItems) {
        if (item.blob) {
            window.URL.revokeObjectURL(item.blob)
        }
        for (const v of item.versions || []) {
            if (v.blob) {
                window.URL.revokeObjectURL(v.blob)
            }
        }
    }
}


// The options panel calls this after any change, the same way the single-image
// path re-renders when a directive changes.
function rebatchIfDone() {
    if (!uiStore.batchMode || !uiStore.batchItems.length || uiStore.batchRunning) return

    /* A DOCUMENT'S PAGES ARE ALREADY IN THE BUCKET.
     *
     * startBatch() re-uploads uiStore.files, which after a PDF run still holds
     * the PDF -- so every change to the panel sent the DOCUMENT back as though
     * it were a photograph, and the CDN cannot render a PDF as a PNG. The three
     * finished pages were replaced by one row named "f1040.pdf" reading FAILED,
     * which is what a visitor saw for changing the output format.
     *
     * Reported against the output format; it was never about the format. Any
     * control -- width, quality, rotation, EXIF -- took the same path. The
     * report happened to arrive on PNG.
     *
     * Nothing needs re-uploading. Rasterising put one object per page on S3 and
     * every setting the panel offers is an ImageEngine directive over those
     * objects, so re-running the half AFTER the upload is the whole job:
     * runBatch() re-fetches each page at the new directives and the pages
     * themselves are untouched. It is the same reason the document could start
     * without being asked -- there is nothing to decide before the upload.
     */
    if (uiStore.files.some((f) => isPdf(f.file))) {
        const uploaded = uiStore.batchItems.map((i) => ({name: i.name, key: i.key}))
        const meta = uiStore.batchItems.map((i) => ({id: i.id, name: i.origName,
                                                     size: i.origSize}))
        releaseBatch()
        // runBatch() clears neither of these -- startBatch() and startPdf() set
        // them on the way in, and this is the third way in.
        uiStore.batchRunning = true
        uiStore.batchDone = 0
        uiStore.showDownloadables = false
        runBatch(uploaded, meta)
        return
    }

    releaseBatch()
    startBatch()
}


// Drive the batch path from a headless browser.
//
// A file input cannot be filled from outside the page, so without this there is
// no way to see batch mode actually render -- and shipping a path that has only
// ever been reasoned about is how the blank-page bug got out. Inert unless the
// hash asks for it, and it only ever uses this site's own demo images, so it
// does nothing a visitor could not do by hand.
//
//     /#selftest-batch=4
/* The progress modal, at a given point, without a document.
 *
 * Its arithmetic cannot be checked any other way here. The counts arrive over a
 * poll, and under Chrome's --virtual-time-budget -- which is what lets the
 * end-to-end suite run at all -- virtual time PAUSES while a fetch is
 * outstanding. The upload POST is outstanding for the whole conversion, so a
 * setInterval measured in virtual milliseconds never fires, and the poll cannot
 * run in the one environment that can drive the interface. Measured: zero
 * requests to /pdf-progress across a full run, with the poll demonstrably in
 * the bundle and the endpoint demonstrably answering curl.
 *
 * So this sets the numbers the poll would have set. Inert unless the hash asks
 * for it, and it touches nothing but what is on screen -- no upload, no file,
 * no request.
 *
 *     /pdf-to-jpg#selftest-pdf=12/40/rendering
 */
function selfTestPdfProgress(done, total, stage, name) {
    uiStore.pdfPhase = "rendering"
    uiStore.pdfPages = total
    uiStore.pdfDone = done
    uiStore.pdfTotal = total
    uiStore.pdfStage = stage

    /* A FILE-SHAPED OBJECT, not a File, and deliberately so.
     *
     * PdfProgress reads uiStore.files[0].file.name and nothing else about it,
     * so that is all this provides. Building a real File and going through
     * addFiles() would be worse in three ways: addFiles() opens with $reset(),
     * which would wipe the phase and counts just set above; it would stage a
     * document nobody chose, which is exactly what collectCarriedImage()
     * stands down from doing; and on a page whose declared input is a PDF the
     * auto-start watcher would fire and send it to be rasterised -- the watcher
     * already refuses anything under a #selftest- hash for this reason.
     *
     * Nothing else reads these fields while pdfPhase is set: the stage is
     * behind the modal and the drop zone is not what the shot is aimed at.
     */
    if (name) {
        const size = 2_411_724
        uiStore.files = [{
            file: {name, size, type: "application/pdf"},
            name,
            size,
            width: 0,
            height: 0,
            id: `#selftest-${name}`,
            url: "",
            status: null
        }]
    }
}

/* THE TRIMMER, PUT ON SCREEN WITHOUT A VIDEO.
 *
 * Same argument as #selftest-pdf, and the note there is worth re-reading: a
 * shot has to be taken under --virtual-time-budget, and the media pipeline
 * does not survive it at all. Measured in tests/video_to_gif_test.sh, which is
 * the only real-time suite here: one frame in 4,024 ms with realTick, one in
 * 27,561 ms via requestVideoFrameCallback. So a screenshot that waits for a
 * clip to decode waits for ever, and tools/shots.js cannot photograph the one
 * control this page is built around.
 *
 * Everything below is what decodeFrames() and sampleStrip() would have
 * written, set synchronously: no <video>, no seeking, no canvas. The
 * thumbnails are drawn here rather than fetched, so the hook needs nothing on
 * disk and cannot leave anything behind.
 *
 * It is a hash, so it can only ever be reached deliberately -- and
 * collectCarriedImage() and the PDF auto-start already stand down for any
 * #selftest- hash, which is what keeps it from colliding with them.
 */
function selfTestVideoTrim(seconds, name) {
    uiStore.videoName = name || "holiday-clip.mp4"
    uiStore.videoDuration = seconds
    uiStore.videoStart = 0
    uiStore.videoEnd = seconds
    /* Drawn rather than sampled, and drawn in lib/videoFrames.js rather than
     * here -- see demoStrip(). A raw hex in a .vue file is exactly what
     * tests/test_palette.py exists to refuse, and it caught this one. */
    uiStore.videoStrip = demoStrip()
    uiStore.videoTrimming = true
}

async function selfTestBatch(n) {

    const files = []
    for (let i = 1; i <= Math.min(n, 6); i++) {
        const res = await fetch(`${uiStore.s3urlRoot}image${i}.jpg?imgeng=/w_1200/f_jpeg`)
        files.push(new File([await res.blob()], `demo${i}.jpg`, { type: 'image/jpeg' }))
    }
    enteredBy("sample")
    addFiles(files)
    await startUploadFiles()
}


async function startUploadFiles() {

    //console.log("#files: " + files.length)
    if (uiStore.files.length == 0) {
        alert(t('errors.pickFirst'))
        return
    }

    // A PDF is a different job again: it is one file that becomes many, and
    // the pages are made on the server rather than uploaded from here.
    const pdf = uiStore.files.find((f) => isPdf(f.file))
    if (pdf) {
        releaseBatch()
        return startPdf(pdf)
    }

    // More than one image is a different job, not this one N times.
    if (uiStore.batchMode) {
        releaseBatch()
        return startBatch()
    }

    uiStore.showDownloadables = false  // user uploaded new file. remove UI that refers to old downloads
    uiStore.optimizedImagesCount = 0

    // original library supported multiple uploads, but we only want one file really
    let justOnefileReally = [...uiStore.files].slice(0,1);
    try {
        // Through the queue, not straight to applyTransforms: pressing Download
        // while a flip is still being applied would otherwise put two canvas
        // passes over the same wrapper at once, and whichever finished last
        // would win.
        await queueTransform({force: true})
    } catch (err) {
        // Never silent. A canvas that quietly returns blank is what put empty
        // thumbnails on an iPad; a flip that quietly does nothing is the same
        // bug wearing a different hat.
        alert(err.message || t('errors.transformFailed'))
        return
    }
    /* RE-READ THE STAGE, because the pass above may have replaced it.
     *
     * The list was captured before queueTransform(), which is fine when that
     * only rewrites a wrapper's `file` in place -- and wrong now that it can
     * re-convert a clip, because bakeVideoCaption() goes back through
     * addFiles() and stages a NEW wrapper. Holding the old one uploaded the
     * GIF without the words in it, and the delivered file came back
     * wordless with nothing to say why.
     */
    justOnefileReally = [...uiStore.files].slice(0, 1)
    if (!justOnefileReally.length) return

    /* RETURNED, so `await startUploadFiles()` means what it says.
     *
     * This chain used to be fired and abandoned, so the async function
     * resolved almost immediately -- before the upload finished and long
     * before downloadOptimizedFiles() had been called at all. makeAndDeliver()
     * then ran waitForVersions(), which decides readiness by `v.blob`, and on
     * a RE-RENDER every version still held the PREVIOUS render's blob. So the
     * wait returned instantly and Save handed over the file built for the
     * setting before last.
     *
     * Reported as three saves at 100 %, 66 % and 50 % arriving as 1280, 1280
     * and 844 -- each one the previous answer. Confirmed by intercepting the
     * anchor: the first two downloads were the SAME blob URL. Two missing
     * returns on one path, this being the outer of them. */
    return uploadFiles(justOnefileReally).then((thingy) => {

        uiStore.workableImage.ready = true;
        // rolling my own version of secureFileName as found on Python was giving me all
        // kinds of headache. Ended up letting the server tell me what file name it picked.
        // 'name' is the plain filename (shown to the user, used to name the
        // downloads), 'key' also carries the random folder the object went into.
        uiStore.workableImage.name = thingy[0].name
        uiStore.workableImage.size = justOnefileReally[0].size
        uiStore.workableImage.width = justOnefileReally[0].width
        uiStore.workableImage.height = justOnefileReally[0].height
        uiStore.workableImage.url = uiStore.s3urlRoot + thingy[0].key

        uiStore.optimizationInProgress = true
        // console.log("uiStore.optimizationInProgress", uiStore.optimizationInProgress)

        return downloadOptimizedFiles()
            .then(() => {
                // console.log("All images loaded")
                uiStore.showDownloadables = true
                // Whether to go to the table, and when, is makeAndDeliver's
                // to decide: it is the only thing that reaches this path for a
                // single image, and it knows whether there is a table at all.
                // Scrolling here as well was a phone-only half of that, from
                // before the versions were something you asked for.
            })

    }).catch(
        (err) => alert(err)
        )


    // let's calculate the directives for each image and load images client-side

}


// The samples sit in the RIGHT column; the image they stage appears in the drop
// zone, which is in the LEFT one. Side by side on a wide screen, so the preview
// lands next to the thumbnail that was clicked and the link plainly did
// something. Once the columns stack -- below md, or any narrowed window -- the
// drop zone is far above the samples: measured at 474px wide, the gap is 1987px
// against a 900px viewport. The click works, the image is staged, and the
// reader sees nothing whatever happen.
//
// That is the same failure the results table had, with the same remedy: take
// the reader to the work. See goToSingleStage().
async function setImageOnStage(imgIdx) {

    const imageUrl = `${uiStore.s3urlRoot}image${imgIdx}.jpg?imgeng=/pass_true`

    try {
        const res = await fetch(imageUrl)
        // fetch only rejects on a network error; a 404 or a 412 from the CDN
        // arrives as a perfectly resolved response and used to become an
        // unreadable blob that failed later, somewhere less obvious.
        if (!res.ok) throw new Error(`HTTP ${res.status}`)

        const data = await res.blob()
        const myFile = new File([data], 'image' + imgIdx + ".jpg",
            { type: "image/jpeg", lastModified: new Date().getTime() })

        const container = new DataTransfer()
        container.items.add(myFile)
        enteredBy("starter")
        addFiles(container.files)
        goToDropZone()
    } catch (error) {
        // This was `.catch(error => { return error })`, which is a discard with
        // extra steps: returning from a catch resolves the chain. A sample that
        // could not be fetched did nothing and said nothing, which is
        // indistinguishable from a dead link.
        alert(t('errors.sampleFailed', {message: error.message || String(error)}))
    }
}

// Only when the drop zone is actually out of sight. On a wide screen it is
// already beside the samples, and scrolling then would move the page under
// somebody who could see everything they needed.
function goToDropZone() {
    setTimeout(() => {
        const el = document.getElementById("drop-zone")
        if (!el) return
        const box = el.getBoundingClientRect()
        const onScreen = box.bottom > 0 && box.top < window.innerHeight
        if (!onScreen) el.scrollIntoView({behavior: "smooth", block: "center"})
    }, 120)
}


/* The field under the drop zone hands over to the existing path.
 *
 * Trimmed and guarded here rather than inside prepareFetchImageFromWeb(),
 * which is also reached from the modal -- one caller's empty string is not a
 * reason to change what the others may pass. It reads the address off the
 * store rather than an argument, so this sets it before handing over.
 */
function fetchFromField() {
    const url = (uiStore.fetchedImageUrl || "").trim()
    if (!url) return
    uiStore.fetchedImageUrl = url
    prepareFetchImageFromWeb()
}

// handle case Image Retrieved from URL
async function prepareFetchImageFromWeb() {


	uiStore.awaitingFetchImage = true
	uiStore.imageFromInternetModal = true

    // console.log(uiStore.fetchedImageUrl)
    // let's do some cleaning first
    const previewer = document.getElementById("fetchedUrlPreview")
    removeAllChildNodes(previewer)

    /* The endpoint hands back the PICTURE, not a key.
     *
     * It used to upload to S3 and answer with a key, and this then downloaded
     * the same image again from the CDN to stage it -- the same bytes across
     * the network three times. A 23 MB photograph spent 50.8 seconds being
     * stored and another 6 coming back, which is long enough for a browser to
     * abandon the request: reported as "Failed to fetch".
     *
     * So: two legs, which is the floor. An error is still JSON, so the two are
     * told apart by the content type rather than by trying to parse a JPEG.
     */
    const res = await fetchImageFromWeb()
    const asJson = (res.headers.get("Content-Type") || "").includes("json")

    if (!res.ok || asJson) {
        let message = "HTTP " + res.status
        try {
            const body = await res.json()
            if (body && body.Error) {
                message = body.Error
            }
        } catch (err) { /* not json after all; the status will do */ }
        uiStore.awaitingFetchImage = false
        uiStore.fetchedImageError = message
        return
    }

    /* ONE DOOR: the fetched picture enters exactly where a dropped one does.
     *
     * It used to write uiStore.workableImage by hand, append its own small
     * preview and call downloadOptimizedFiles() -- a second intake that skipped
     * everything addFiles() does, so it never staged anything. #downloadables
     * is deliberately hidden for a single version ("the button hands it over
     * and the stage already shows it") and this path made no stage, so with one
     * format enabled -- every page as it ships -- a successful fetch ended on a
     * blank screen.
     *
     * addFiles() opens with $reset(), and every flag this modal runs on is in
     * state(), so the panel closes and the field empties without being told to.
     */
    try {
        const blob = await res.blob()
        const name = res.headers.get("X-Image-Name") || "image"
        /* The type comes off the RESPONSE, and for a clip it has to: the
         * browser will not hand a blob to a <video> element without a video/*
         * type, and looksLikeVideo() is what routes it to the trimmer instead
         * of the picture intake. The jpeg fallback is for a typeless response
         * and must not swallow a video, so it only applies when nothing said
         * otherwise. */
        enteredBy("url_fetch")
        addFiles([new File([blob], name, {type: blob.type || "image/jpeg"})])
    } catch (err) {
        uiStore.awaitingFetchImage = false
        uiStore.fetchedImageError = String((err && err.message) || err)
    }
}


/* AT THE FOOT, BELOW EVERY CONST IT READS. A watcher placed above the values
 * in its getter runs once into the temporal dead zone; Vue wraps a getter in
 * its own error handling, so the ReferenceError is SWALLOWED and the watcher
 * then tracks nothing and never fires again. That is the silent failure that
 * took out the phone scroll, and it is why this sits here.
 *
 * Guarded by the file's id for the reason the animated-GIF notice is: reading
 * bytes takes a tick, and a second photograph dropped inside that tick would
 * otherwise be labelled with the first one's findings -- which on THIS page
 * means showing somebody a position that is not in the file they are looking
 * at. */
if (offersInspect) {
    watch(() => (uiStore.files[0] && uiStore.files[0].id) || null, async (id) => {
        if (!id) { metadataInfo.value = null; metadataReading.value = false; return }
        const staged = uiStore.files[0] && uiStore.files[0].file
        if (!staged) return
        /* THE FILE THE VISITOR CHOSE, not the one the application made of it.
         *
         * A HEIC is decoded on the device and re-enters as a JPEG that
         * libheif writes with NO EXIF -- so reading the staged file reported
         * "Nothing found in this one" about an iPhone photograph carrying
         * GPS. Measured on a real iPhone 12 Pro frame: handed over as .heic,
         * staged as .jpg, and the page said it was clean.
         *
         * That is the worst failure this page can have. Everything else it
         * gets wrong is visible; a false negative is believed, and the
         * visitor shares the photograph BECAUSE they checked. */
        const file = originalOf(staged) || staged
        metadataReading.value = true
        metadataInfo.value = null
        try {
            const bytes = new Uint8Array(await file.arrayBuffer())
            if (!uiStore.files[0] || uiStore.files[0].id !== id) return
            metadataInfo.value = readExif(bytes)
        } catch (err) {
            metadataInfo.value = {found: false, device: {}, taken: {}, location: null, otherTags: 0}
        } finally {
            if (uiStore.files[0] && uiStore.files[0].id === id) metadataReading.value = false
        }
    }, {immediate: true})
}

</script>

<style scoped>
/* The component's own root. The element it mounts INTO is #app, provided by
 * index.html and by every generated task page; this is the thing inside it. */
.oio-app {

    font-family: var(--font-ui);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: var(--ink);
    background: var(--paper);
    margin: 0 auto;
    height: 100%;
}


/* THE GRADIENT IS THREE TOKENS, and every one of them lives in palette.css.
 *
 * It used to be two Tailwind arbitrary values in the template -- the only two
 * raw hexes tests/test_palette.py allows in this file -- and painting it per
 * errand would have meant seven more pairs of them. So the stops are read from
 * custom properties, and palette.css sets them off [data-family].
 *
 * THE FALLBACKS ARE THE BRAND PAIR, and they are not decoration. palette.css
 * is a separate request, and this repo has already had a phone render a page
 * against a service worker still holding the PREVIOUS build's copy of it. A
 * var() with no fallback would paint no gradient at all there -- white text on
 * white -- so the stylesheet failing to arrive gives the gradient every page
 * had before this change rather than an unreadable drop zone.
 *
 * The glow follows the dark stop rather than being a fourth colour, so a
 * family cannot end up lifted in a hue it does not use. */
.drop-area {
    background-image: linear-gradient(to right,
        var(--drop-from, #FF66FF), var(--drop-to, #4636B3));
    box-shadow: 0 6px 20px var(--drop-glow, rgba(70, 54, 179, 0.18));
}

.drop-area[data-active=true] {
    box-shadow: 0 8px 28px var(--drop-glow-strong, rgba(70, 54, 179, 0.38));
}

/* label.dropzonelabel {
    font-size: 24px;
    cursor: pointer;
    display: block;
} */

/* label.dropzonelabel span {
        display: block;
    } */

/* I would like to replace this with tailwind CSS, but it's a CSS hack to make the
   input "file-upload" thingy invisible. Leaving it here for now */
label.dropzonelabel input[type=file]:not(:focus-visible) {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }


.picture-element {
    max-height: 200px;
    overflow:auto;
    text-align: left;
    font-size: 80%;
    font-family:'Courier New', Courier, monospace;
    white-space: pre-wrap;
    word-break: keep-all;
}

.wf {
  outline: 1px solid black;
}
</style>
